import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useContext,
} from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  BackgroundVariant,
  Panel,
  useReactFlow,
  useStore,
  useViewport,
  MiniMap,
  FinalConnectionState,
  getViewportForBounds,
} from '@xyflow/react';
import * as Sentry from '@sentry/react';
import { DnDProvider } from '@/context/dnd-context';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import GenericNode from '@/pages/workflow/component/custom-node/generic-node/generic-node';
import StickyNoteNode from '@/pages/workflow/component/custom-node/sticky-note/sticky-note';
import GroupNode from '@/pages/workflow/component/custom-node/group-node/group-node';
import {
  useNodeGrouping,
  selectGroupChildren,
} from '@/pages/workflow/hooks/use-node-grouping';
import {
  StickyNotePlacementPreview,
  getStickyNotePlacementScreenOffset,
} from '@/pages/workflow/component/custom-node/sticky-note/sticky-note-placement-preview';
import SelectNode from '@/pages/workflow/component/custom-node/select-node/select-node';
import { UploadProgressWidget } from '@/pages/workflow/component/upload/upload-progress-widget';
import CustomEdgeComponent from '@/pages/workflow/component/custom-edge/custom-edge';
import OrthogonalEdgeComponent from '@/pages/workflow/component/custom-edge/orthogonal-edge';
import { pauseReviewContent } from '@/components/agent-chat/paused-node';
import { ChatRailShortcut } from '@/components/agent-chat/chat-rail-shortcut';
import { useCanvasPauseBridgeStore } from '@/stores/canvas-pause-bridge-store';
import { useCanvasRunBridgeStore } from '@/stores/canvas-run-bridge-store';
import { useCanvasApplyMutationsBridgeStore } from '@/stores/canvas-apply-mutations-bridge-store';
import {
  type ChatApplySaveReport,
  useChatApplyStore,
} from '@/stores/chat-apply-store';
import { workflowContentKey } from '@/utils/workflow-content-key';
import {
  useWorkflowMutationApplier,
  getEditingLockedWarning,
} from '@/hooks/use-workflow-mutation-applier';
import { useEdgeLayoutStore } from '@/stores/edge-layout-store';
import { getAutoLayoutPositions } from '@/pages/workflow/helper/auto-layout';
import { useDnD } from '@/context/dnd-context';
import {
  applyAutoConnection,
  calculateDropPosition,
  findDropTargetId,
  repositionDraggedNodesAbsolute,
  resolveAutoConnection,
} from '@/stores/helpers/node-drop-helpers';
import {
  useNodeStore,
  selectSelectedNodeIds,
  remapNodesAndEdges,
  withSuppressedSnapshots,
} from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import {
  useWorkflowStore,
  WorkflowNotFoundError,
} from '@/stores/workflow-store';
import { Button } from '@/components/ui/button';
import {
  RocketIcon,
  MoreHorizontal,
  Edit,
  FileText,
  CopyPlus,
  Trash2,
  Plus,
  Import,
  Share,
  Share2,
  Save,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
  Lock,
  Unlock,
  Shapes,
  Settings,
} from 'lucide-react';
import {
  NodeExplorerSidebar,
  type PendingClarification,
} from '@/pages/workflow/component/controls/node-explorer-sidebar';
import { CanvasContextMenu } from '@/pages/workflow/component/controls/canvas-context-menu';
import {
  MouseControlBar,
  InteractionMode,
} from './component/controls/mouse-control-bar';
import { UtilityPanel } from './component/controls/utility-panel';
import { AddNodesButton } from './component/controls/add-nodes-button';
import { ChatShortcutBar } from './component/controls/chat-shortcut-bar';
import { lockWorkflow } from '@/services/workflow/lock-workflow';
import { ConsoleSidebar } from './component/controls/console-sidebar';
import { ConsoleSidebarLegacy } from './component/controls/console-sidebar-legacy';
import {
  useWorkflowStatusToasts,
  markUserStoppedRun,
  markRunCompletionOverride,
} from '@/hooks/use-workflow-status-toasts';
import { useWebSocket } from '@/hooks/use-websocket';
import { useChatWebSocket } from '@/hooks/use-chat-websocket';
import {
  chatWebsocketService,
  ChatConnectionState,
} from '@/services/websocket/chat-websocket-service';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useKeepSelectedNodeVisible } from '@/hooks/use-keep-selected-node-visible';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { ResultPreviewPanel } from './component/controls/preview-panel';
import { FilesPreviewPanel } from './component/controls/files-preview-panel';
import { websocketService } from '@/services/websocket/websocket-service';
import {
  useWorkflowValidationStore,
  validateImportErrors,
  validateGraphStructure,
} from '@/stores/workflow-validation-store';
import {
  buildCatalogIdentitySet,
  getCatalogNodeIdentityKey,
} from '@/pages/workflow/helper/catalog-node-identity';
import { hasExecutableWorkflowNodes } from '@/pages/workflow/helper/graph-analysis';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import {
  NodeStatusEvent,
  LiveTranscriptEvent,
  StatusUpdateEvent,
  ExecutionResultEvent,
  NodeResultDataEvent,
  NodeEvent,
  WorkflowEventTypes,
  NodeDataType,
  NodeEventType,
  LifecycleEvent,
  LifecyclePhase,
  PauseStateSnapshot,
  TerminationEvent,
  TerminationStatusCode,
} from '@/types/workflow-types';
import {
  classifyTerminalRun,
  isUserCancellation,
} from '@/pages/workflow/component/controls/helpers/status-helpers';
import { WorkflowPanelTab } from '@/stores/workflow-panel-store';
import { v4 as uuidv4 } from 'uuid';
import {
  CustomNode,
  NodeStruct,
  NodeType,
  NodeResult,
  ArtifactNodeData,
  InputValue,
} from '@/types/node-types';
import { runWorkflow } from '@/services/workflow/run-workflow';
import { linkRunToOperatorChat } from '@/stores/operator-conversation-store';
import { getRunSession } from '@/services/workflow/get-run-session';
import { publishWorkflow } from '@/services/workflow/publish-workflow';
import { chatTimerManager } from '@/services/workflow/chat-timer-manager';
import { postChatResumeWorkflow } from '@/services/workflow/post-chat-resume-workflow';
import { createChatSession } from '@/services/workflow/create-chat-session';
import { nodeResumeWorkflow } from '@/services/workflow/node-resume-workflow';
import { nodeStopWorkflow } from '@/services/workflow/node-stop-workflow';
import { stopWorkflow } from '@/services/workflow/stop-workflow';
import { pauseWorkflow } from '@/services/workflow/pause-workflow';
import { resumeWorkflow } from '@/services/workflow/resume-workflow';
import { rerunFromNode } from '@/services/workflow/rerun-from-node';
import { deliverClarification } from '@/services/workflow/deliver-clarification';
import { pauseClarificationCountdown } from '@/services/workflow/pause-clarification-countdown';
import { resumeClarificationCountdown } from '@/services/workflow/resume-clarification-countdown';
import { getPendingClarifications } from '@/services/workflow/get-pending-clarifications';
import {
  useClarificationPrefsStore,
  modeAllowsClarifications,
  clarificationTimeoutForMode,
  effectiveClarificationPrefs,
  isClarificationMode,
} from '@/stores/clarification-prefs-store';
import {
  takePauseSnapshot,
  computeEditedNodeIds,
  type PauseSnapshot,
} from '@/pages/workflow/helper/pause-snapshot';
import { buildDownstreamSet } from '@/pages/workflow/helper/downstream';
import { nodeSupportsChat } from '@/pages/workflow/helper/node-supports-chat';
import { deriveNodeStatusFromEvents } from '@/pages/workflow/helper/derive-node-status';
import { trimLiveTranscriptEvents } from '@/pages/workflow/helper/live-transcription-state';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { NodeActionsProvider } from '@/pages/workflow/contexts/node-actions-context';
import { LoadingSpinner } from '@/components/loading-spinner';
import { RunTimerPill } from './component/controls/run-timer-pill';
import { RunStatusPill } from './component/controls/run-status-pill';
import { useParams, useLocation, useSearchParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { orgPath } from '@/utils/org-path';
import { SaveWorkflowModal } from './component/save-workflow-modal';
import {
  PublishWorkflowModal,
  PublishOptions,
} from './component/publish-workflow-modal';
import { PublishingLoaderModal } from './component/publishing-loader-modal';
import { useNotification } from '@/components/notifications';
import {
  type DeepLinkParams,
  useWorkflowDeepLink,
} from '@/hooks/use-workflow-deep-link';
import {
  loadRunData,
  applyRunToCanvas,
} from '@/pages/workflow/helper/load-run-data';
import { getRunEvents } from '@/services/workflow/get-run-events';
import { mapThinkingEvents } from '@/utils/map-thinking-events';
// TODO: Re-enable when backend supports screenshot
// import { captureWorkflowScreenshot } from '@/pages/workflow/helper/screenshot';
import { useOrgStore } from '@/stores/org-store';
import { useEvalCatalogStore } from '@/stores/eval-catalog-store';
import { useChatUIStore } from '@/stores/chat-ui-store';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { ShareModal } from '@/components/share-modal';
import { DefaultRoleOption } from '@/types/share-types';
import type { ArtifactShareRole } from '@/types/share-types';
import { ROUTES } from '@/constants/routes';
import {
  INTERACTION_MODE_KEY,
  CONSOLE_TAB_PARAM,
  workflowRunPath,
  NODE_EXPLORER_PANEL_MIN_WIDTH,
  NODE_EXPLORER_PANEL_MAX_WIDTH,
} from '@/constants/workflow-editor';
import { CustomEdge } from '@/types/edge-types';
import { AuthContext } from '@/context/auth-context';
import { useAutoSave } from '@/hooks/use-auto-save';
import { useUnsavedChangesWarning } from '@/hooks/use-unsaved-changes-warning';
import { UnsavedChangesModal } from '@/components/unsaved-changes-modal';
import { AddNodesPanel } from './component/controls/add-nodes-panel';
import { PastRunsButton } from './component/controls/past-runs-button';
import { PastRunsSidebar } from './component/controls/past-runs-sidebar';
import { HumanInterventionBanner } from './component/controls/human-intervention-banner';
import { DisabledNodesBanner } from './component/controls/disabled-nodes-banner';
import { EmptyScopeBanner } from './component/controls/empty-scope-banner';
import { CarbonArcConnectBanner } from './component/controls/carbon-arc-connect-banner';
import { type WorkflowRunWithResults } from '@/services/workflow/get-workflow-run';
import { setWorkflowDefaultRole } from '@/services/workflow/workflow-default-role';
import { clearNodeResults } from '@/pages/workflow/helper/clear-node-results';
import { officeDocumentResult } from '@/pages/workflow/helper/office-document-result';
import {
  isAccessDeniedError,
  ACCESS_REVOKED_MESSAGE,
} from '@/pages/workflow/helper/is-access-denied-error';
import { scrubNodesForExport } from '@/pages/workflow/helper/scrub-export-nodes';
import { sanitizeFilename } from '@/utils/filename-utils';
import ArtifactNode from '@/pages/workflow/component/custom-node/artifact-node/artifact-node';
import { ArtifactViewer } from '@/components/artifacts/artifact-viewer';
import type { ChatRunEndedSignal } from '@/hooks/use-general-chat';
import type { FallbackChatFile } from '@/components/chat';
import type { ArtifactListItem } from '@/types/artifacts';
import {
  spawnArtifactNodesForResult,
  removeAllArtifactNodes,
  isArtifactNode,
  isArtifactEdge,
  getTerminalNodeIds,
} from '@/pages/workflow/helper/artifact-node-manager';
import {
  expandGroupNodes,
  getAbsolutePosition,
  isGroupNode,
  getGroupChildIds,
  getGroupEligibleSelection,
  canGroupSelection,
  getSharedGroupParentId,
} from '@/pages/workflow/helper/group-nodes';
import { GroupActionToolbar } from '@/pages/workflow/component/controls/group-action-toolbar';
// TEMPORARILY DISABLED: SaveStatusIndicator
// TODO: Re-enable save status indicator later
// import { SaveStatusIndicator } from './component/controls/save-status-indicator';
import { cn } from '@/lib/utils';

// ── MultiSelectGroupToolbar ──────────────────────────────────────────────────
// Floats above the eligible selection bounds when 2+ groupable nodes are
// selected. Must be rendered inside <ReactFlow> so it can use ReactFlow hooks.

interface MultiSelectGroupToolbarProps {
  onGroup: () => void;
  onCopy: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isLocked: boolean;
}

const TOOLBAR_HEIGHT = 36;
const TOOLBAR_GAP = 8; // px between toolbar bottom and selection top

function MultiSelectGroupToolbar({
  onGroup,
  onCopy,
  onDuplicate,
  onDelete,
  isLocked,
}: MultiSelectGroupToolbarProps) {
  // Subscribe to viewport so we re-render on pan/zoom.
  useViewport();

  const transform = useStore((s) => s.transform);
  const [tx, ty, zoom] = transform;

  const eligibleSelected = useNodeStore(
    useShallow((state) => getGroupEligibleSelection(state.nodes)),
  );
  const canGroup = useNodeStore(
    useShallow(
      (state) =>
        canGroupSelection(state.nodes) && !getSharedGroupParentId(state.nodes),
    ),
  );
  const groupContainerFocusedId = useNodeStore(
    (state) => state.groupContainerFocusedId,
  );

  const { getNodesBounds } = useReactFlow();

  // Don't show when: not enough nodes, can't group, locked, or a container is focused.
  if (
    !canGroup ||
    eligibleSelected.length < 2 ||
    groupContainerFocusedId ||
    isLocked
  ) {
    return null;
  }

  const bounds = getNodesBounds(eligibleSelected);
  // Convert flow coords to container-relative screen coords.
  const screenX = bounds.x * zoom + tx + (bounds.width * zoom) / 2;
  const screenY = bounds.y * zoom + ty - TOOLBAR_GAP - TOOLBAR_HEIGHT;

  return (
    <div
      style={{
        position: 'absolute',
        left: screenX,
        top: screenY,
        transform: 'translateX(-50%)',
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      <div style={{ pointerEvents: 'auto' }}>
        <GroupActionToolbar
          variant='group'
          onAction={onGroup}
          onCopy={onCopy}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          isLocked={isLocked}
        />
      </div>
    </div>
  );
}

// ── UngroupToolbarOverlay ────────────────────────────────────────────────────
// Floats above the focused group container. Rendered as a plain ReactFlow
// child (not a node) rather than inside GroupNode itself, because
// `.react-flow__node-GroupNode` is forced to `z-index: 0` in
// generic-node/style.css — a toolbar rendered inside that node can only
// stack against its own siblings there, so any overlapping node elsewhere
// on the canvas with a higher z-index could cover it and swallow clicks.

interface UngroupToolbarOverlayProps {
  isLocked: boolean;
}

function UngroupToolbarOverlay({ isLocked }: UngroupToolbarOverlayProps) {
  // Subscribe to viewport so we re-render on pan/zoom.
  useViewport();

  const transform = useStore((s) => s.transform);
  const [tx, ty, zoom] = transform;

  const groupContainerFocusedId = useNodeStore(
    (state) => state.groupContainerFocusedId,
  );
  const focusedNode = useNodeStore((state) =>
    state.nodes.find((n) => n.id === groupContainerFocusedId),
  );
  const onCopyNode = useNodeStore((state) => state.onCopyNode);
  const onDuplicateNode = useNodeStore((state) => state.onDuplicateNode);
  const onNodesEdgesDelete = useNodeStore((state) => state.onNodesEdgesDelete);

  const { getNodesBounds } = useReactFlow();
  const { ungroupFromContext } = useNodeGrouping();

  if (!focusedNode || isLocked) {
    return null;
  }

  const focusedId = focusedNode.id;

  // Select this group's children before an action runs, so the toolbar's
  // actions (and keyboard Delete/Backspace, since focusing the container
  // also selects its children) target the same nodes. Marks the change as
  // focus-triggered so onSelectionChange doesn't clear groupContainerFocusedId
  // out from under this toolbar mid-action.
  const selectChildren = () => selectGroupChildren(focusedId);

  const bounds = getNodesBounds([focusedNode]);
  const screenX = bounds.x * zoom + tx + (bounds.width * zoom) / 2;
  const screenY = bounds.y * zoom + ty - TOOLBAR_GAP - TOOLBAR_HEIGHT;

  return (
    <div
      style={{
        position: 'absolute',
        left: screenX,
        top: screenY,
        transform: 'translateX(-50%)',
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      <div style={{ pointerEvents: 'auto' }}>
        <GroupActionToolbar
          variant='ungroup'
          onAction={() => ungroupFromContext(focusedId)}
          onCopy={() => {
            selectChildren();
            onCopyNode();
          }}
          onDuplicate={() => {
            selectChildren();
            onDuplicateNode();
          }}
          onDelete={() => {
            selectChildren();
            onNodesEdgesDelete();
          }}
          isLocked={isLocked}
        />
      </div>
    </div>
  );
}

const nodeTypes = {
  GenericNode,
  StickyNoteNode,
  SelectNode,
  ArtifactNode,
  GroupNode,
};

const WORKFLOW_LOCK_ALLOWED_SUBTYPES = new Set(['file_input', 'text_input']);

const edgeTypes = {
  default: CustomEdgeComponent,
  orthogonal: OrthogonalEdgeComponent,
};

// Helper function to check if an event is a NodeEvent
const isNodeEvent = (event: NodeStatusEvent): event is NodeEvent => {
  return (
    'node_id' in event &&
    'node_display_name' in event &&
    'display_type' in event
  );
};

interface NodeCanvasProps {
  className?: string;
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  isExecutingWorkflow: boolean;
  onSelectionChange: (params: { nodes: Node[]; edges: Edge[] }) => void;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  isLoadingWorkflow: boolean;
  isLoadingTemplate: boolean;
  onGetNodesBoundsReady?: (
    getNodesBounds: (
      nodes: Node[],
    ) => Parameters<typeof getViewportForBounds>[0],
  ) => void;
  onFitViewReady?: (
    fitView: (options?: { padding?: number; duration?: number }) => void,
  ) => void;
  isOwner: boolean;
  onCloneWorkflow: () => void;
  shouldIgnoreKeyboardEvent: () => boolean;
  panelGroupWidth: number;
  isHistoricalData: boolean;
  isLocked: boolean;
  onToggleLock: () => void;
  onDismissStatusBarCompleted?: () => void;
  isWorkflowLocked?: boolean;
  // ── Pause / resume props (Temporal-only) ─────────────────────
  isPaused?: boolean;
  /**
   * react_flow_generated_ids of nodes whose results were seeded
   * from a prior run via rerun-from-node.  Drives the "Cached"
   * badge on each node.
   */
  cachedNodeIds?: Set<string>;
  /**
   * Click handler for the "Re-run from this node" item on the
   * canvas context menu.  Only meaningful when viewing a past run
   * (the editor passes ``undefined`` otherwise so the menu item
   * doesn't render).
   */
  onRerunFromNode?: (nodeId: string) => Promise<void> | void;
  /**
   * Click handler for the per-node "play" button (rendered by
   * GenericNode when ``data.isRerunnable`` is true).  The editor
   * dispatches to either a paused-resume (with this node's id
   * forced into editedNodeIds) or a fresh rerun-from-node start
   * depending on the current context.  Threaded through the
   * NodeActionsProvider rather than via ``node.data`` so the
   * callback identity doesn't trigger ReactFlow re-renders.
   */
  onRerunFromHere?: (nodeId: string) => void;
  /**
   * Canvas is hosted in a pane rather than the editor route, so the node
   * explorer sidebar is absent. Suppresses the affordances that only make
   * sense alongside it.
   */
  embedded?: boolean;
  isConnected: boolean;
  /** Whether the Add Nodes panel and its button are available (fluid-chat layout only). */
  addNodesEnabled?: boolean;
  /**
   * Whether this workflow has never been persisted with a real id. The
   * unified chat design's operator rail only replaces the node explorer's
   * chat tab for that truly-new canvas — a standalone existing workflow
   * (flag on, no primary agent) keeps the legacy explorer and its chat tab.
   */
  isNewWorkflow: boolean;
}

const NodeCanvas = ({
  className,
  nodeStatuses,
  isExecutingWorkflow,
  onSelectionChange,
  onNodeClick,
  isLoadingWorkflow,
  isLoadingTemplate,
  embedded = false,
  onGetNodesBoundsReady,
  onFitViewReady,
  isOwner,
  onCloneWorkflow,
  shouldIgnoreKeyboardEvent,
  panelGroupWidth,
  isLocked,
  onToggleLock,
  isHistoricalData,
  onDismissStatusBarCompleted,
  isWorkflowLocked,
  isPaused: isPausedProp = false,
  cachedNodeIds,
  onRerunFromNode,
  onRerunFromHere,
  isConnected,
  addNodesEnabled = false,
  isNewWorkflow,
}: NodeCanvasProps) => {
  const {
    screenToFlowPosition,
    fitView,
    getNodesBounds,
    getViewport,
    getZoom,
    setNodes: setReactFlowNodes,
    getIntersectingNodes,
    setEdges: setReactFlowEdges,
  } = useReactFlow();
  const notification = useNotification();
  const { isCanvasA2aChatEnabled } = useLaunchDarkly();
  const edgeLayout = useEdgeLayoutStore((s) => s.layout);

  useEffect(() => {
    const targetType = edgeLayout === 'orthogonal' ? 'orthogonal' : 'default';
    setReactFlowEdges((eds) =>
      eds.map((e) => (e.type !== targetType ? { ...e, type: targetType } : e)),
    );
  }, [edgeLayout, setReactFlowEdges]);

  const layoutDirectionRef = useRef<'TB' | 'LR'>('LR');

  const handleAutoLayout = useCallback(() => {
    const {
      nodes: currentNodes,
      edges: currentEdges,
      saveSnapshot,
    } = useNodeStore.getState();
    if (currentNodes.length === 0) return;

    saveSnapshot();

    const direction = layoutDirectionRef.current;
    const positions = getAutoLayoutPositions(
      currentNodes,
      currentEdges,
      direction,
    );

    setReactFlowNodes((nds) =>
      nds.map((node) => {
        const pos = positions.get(node.id);
        if (!pos) return node;
        return { ...node, position: pos };
      }),
    );

    layoutDirectionRef.current = direction === 'TB' ? 'LR' : 'TB';

    setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50);
  }, [setReactFlowNodes, fitView]);

  // Get sidebar states to calculate preview panel margins
  const {
    isMaximized: isNodeExplorerMaximized,
    isHoveringWhileMinimized: isNodeExplorerHovering,
    panelSize: nodeExplorerPanelSize,
  } = useNodeExplorerStore();

  // Preview panel state and tab control
  const {
    isMaximized: isConsoleSidebarMaximized,
    isHoveringWhileMinimized: isConsoleSidebarHovering,
    panelSize: consoleSidebarPanelSize,
    isConsolePanelOpen,
    isPreviewVisible,
    previewData,
    hidePreview,
    setActiveTab: setActiveTab,
    setConsolePanelOpen,
    isFilesPreviewVisible,
    filesPreviewData,
    hideFilesPreview,
  } = useWorkflowPanelStore();

  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const {
    nodes,
    edges,
    isTextareaHovered,
    onDrop,
    onDragOver,
    onCopyNode,
    onPasteNode,
    onDuplicateNode,
    onNodesEdgesDelete,
    onEdgesChange,
    onNodesChange,
    onConnect,
    onReconnect,
    onReconnectStart,
    onReconnectEnd,
    addNode,
    addStickyNote,
    setActiveConnect,
    deselectAllNodes,
    clearActiveConnect,
    setNodes,
    setGroupContainerFocusedId,
    getCompatibleNodes,
    getCompatibleSourceNodes,
    createSelectNodeForConnection,
    setDragOverNodeId,
    isValidEdgeConnection,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useNodeStore();
  const pendingStickyNoteColor = useNodeStore(
    (state) => state.pendingStickyNoteColor,
  );

  // Calculate preview panel margins based on sidebar visibility
  // this is so that sidebar doesn't block preview panel
  const isNodeExplorerVisible =
    isNodeExplorerMaximized || isNodeExplorerHovering;
  const previewLeftMargin = isNodeExplorerVisible ? nodeExplorerPanelSize : 0;

  const isNodeFocused = selectedNodeIds.length > 0;
  const { groupSelected, ungroupFromContext, canGroup, eligibleCount } =
    useNodeGrouping();
  const isCanvasEditingLocked = !!(isLocked || isWorkflowLocked);

  const { applyBufferedMutations } = useWorkflowMutationApplier({
    isEditingLocked: isCanvasEditingLocked,
    isWorkflowLockActive: !!isWorkflowLocked,
  });
  // Mirrors the chat side's draft-key identity for an unsaved canvas (see
  // onMutation in use-agent-chat.ts) instead of collapsing every unsaved
  // canvas to the same null: null would make dispatchCanvasApplyMutations
  // skip its mismatch check entirely, letting a proposal frozen against a
  // saved workflow or a different draft land on whatever blank canvas is
  // mounted now.
  const bridgeWorkflowId = useWorkflowStore((s) =>
    s.currentWorkflow?.id && s.currentWorkflow.id !== 'new'
      ? s.currentWorkflow.id
      : `new:${s.currentWorkflow?.createdAt ?? ''}`,
  );
  // Embedded, the chat rail belongs to the host and sits beside this editor
  // rather than inside it, so it cannot be handed the apply handler as a
  // prop -- mirrors the run dispatcher in canvas-run-bridge-store. Publish
  // it (and whether it's locked) instead, and withdraw it on unmount so a
  // chat outliving the canvas can't apply to a graph no longer on screen.
  const setCanvasApplyMutationsBridge = useCanvasApplyMutationsBridgeStore(
    (state) => state.setBridge,
  );
  useEffect(() => {
    if (!embedded) return;
    setCanvasApplyMutationsBridge({
      apply: applyBufferedMutations,
      disabled: isCanvasEditingLocked,
      disabledReason: isCanvasEditingLocked
        ? getEditingLockedWarning(!!isWorkflowLocked)
        : undefined,
      workflowId: bridgeWorkflowId,
    });
    return () => setCanvasApplyMutationsBridge(null);
  }, [
    embedded,
    applyBufferedMutations,
    isCanvasEditingLocked,
    isWorkflowLocked,
    bridgeWorkflowId,
    setCanvasApplyMutationsBridge,
  ]);

  const isConsoleSidebarVisible =
    isConsoleSidebarMaximized ||
    isConsoleSidebarHovering ||
    (isNodeFocused && isConsolePanelOpen);
  const previewRightMargin = isConsoleSidebarVisible
    ? consoleSidebarPanelSize
    : 0;

  // Expose getNodesBounds to parent component
  useEffect(() => {
    if (onGetNodesBoundsReady) {
      onGetNodesBoundsReady(getNodesBounds);
    }
  }, [getNodesBounds, onGetNodesBoundsReady]);

  // Expose fitView to parent component
  useEffect(() => {
    if (onFitViewReady) {
      onFitViewReady(fitView);
    }
  }, [fitView, onFitViewReady]);

  // Auto-pan to keep selected node visible when sidebars open
  useKeepSelectedNodeVisible({ panelGroupWidth });

  const [dndPayload, setDndPayload] = useDnD();
  const [isMiniMapVisible, setIsMiniMapVisible] = useState(false);
  const [isSpacePanning, setIsSpacePanning] = useState(false);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setIsSpacePanning(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePanning(false);
      }
    };
    const onWindowBlur = () => setIsSpacePanning(false);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  const [interactionMode, setInteractionMode] = useState<InteractionMode>(
    () => {
      try {
        const stored = localStorage.getItem(INTERACTION_MODE_KEY);
        if (stored === 'mouse' || stored === 'hand') return stored;
      } catch {
        // localStorage may be unavailable
      }
      return 'mouse';
    },
  );
  const handleModeChange = useCallback((mode: InteractionMode) => {
    try {
      // Sticky-note mode is one-shot and transient — never persist it.
      if (mode === 'mouse' || mode === 'hand') {
        localStorage.setItem(INTERACTION_MODE_KEY, mode);
      }
    } catch {
      // localStorage may be unavailable
    }
    setInteractionMode(mode);
  }, []);
  // TEMPORARILY DISABLED: Lock the workflow if user is not the owner
  // TODO: Re-enable owner blocking feature later
  // useEffect(() => {
  //   if (!isOwner) {
  //     setIsLocked(true);
  //   } else {
  //     setIsLocked(false);
  //   }
  // }, [isOwner]);
  const reactFlowWrapper = useRef(null);
  const edgeReconnectSuccessful = useRef<boolean>(true);
  const dragOverRafRef = useRef<number>(0);

  // Plus icon indicator state
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingHandleType, setConnectingHandleType] = useState<
    'source' | 'target' | null
  >(null);
  const [plusIconPosition, setPlusIconPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showPlusIcon, setShowPlusIcon] = useState(false);
  const [isDraggingFromInputPort, setIsDraggingFromInputPort] = useState(false);

  const previewPanelClassName = `relative flex flex-wrap gap-2 flex-row z-40 ${isPreviewVisible ? 'justify-between  bg-black/50' : 'justify-end'}`;
  const previewPanelStyle = {
    height: isPreviewVisible ? '100vh' : 'auto',
    padding: '8px',
    marginLeft: previewLeftMargin,
    marginRight:
      previewRightMargin && !isConsoleSidebarMaximized
        ? previewRightMargin + 8
        : previewRightMargin,
    ...(!previewLeftMargin || !previewRightMargin
      ? {
          paddingTop: '66px',
        }
      : {
          paddingTop: '8px',
        }),
    paddingBottom: '70px',
  };

  // Handle keyboard events for copy/paste/undo/redo
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'z',
        shiftKey: false,
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          if (canUndo()) {
            undo();
          }
        },
      },
      {
        key: 'z',
        shiftKey: true,
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          if (canRedo()) {
            redo();
          }
        },
      },
      {
        key: 'y',
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          if (canRedo()) {
            redo();
          }
        },
      },
      {
        key: 'c',
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          onCopyNode();
        },
      },
      {
        key: 'x',
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          if (selectedNodeIds.length > 0) {
            onCopyNode();
            onNodesEdgesDelete();
          }
        },
      },
      {
        key: 'v',
        handler: () => {
          if (isLocked || isWorkflowLocked) return;
          handlePaste();
        },
      },
      {
        key: 'Escape',
        requireModifier: false,
        handler: () => {
          if (interactionMode !== 'sticky-note') return;
          handleModeChange('mouse');
        },
      },
    ],
    shouldIgnore: shouldIgnoreKeyboardEvent,
  });

  // Track mouse movement during connection to show/hide plus icon
  useEffect(() => {
    if (!isConnecting) return;

    // this is solely to determine whether to render the plus icon on the left or right of the mouse
    if (connectingHandleType !== 'source') {
      setIsDraggingFromInputPort(true);
    } else {
      setIsDraggingFromInputPort(false);
    }

    const CONNECTION_RADIUS = 30; // ReactFlow default connection snap radius

    const handleMouseMove = (event: MouseEvent) => {
      // Use elementFromPoint to get the element at the mouse position
      // This is more reliable than event.target during drag operations
      const elementAtPoint = document.elementFromPoint(
        event.clientX,
        event.clientY,
      ) as HTMLElement | null;

      // Check if mouse is directly over a ReactFlow handle
      const isOverHandle =
        elementAtPoint?.closest('.react-flow__handle') !== null ||
        elementAtPoint?.classList.contains('react-flow__handle') ||
        // Also check for data attributes that might be used
        elementAtPoint?.hasAttribute('data-handleid') ||
        elementAtPoint?.hasAttribute('data-handlepos');

      if (isOverHandle) {
        setShowPlusIcon(false);
        setPlusIconPosition(null);
        return;
      }

      // Check if mouse is within connection radius of any handle
      // This detects when ReactFlow would snap to a handle
      const allHandles = document.querySelectorAll('.react-flow__handle');
      let isWithinSnapDistance = false;

      for (const handle of allHandles) {
        const handleRect = handle.getBoundingClientRect();
        const handleCenterX = handleRect.left + handleRect.width / 2;
        const handleCenterY = handleRect.top + handleRect.height / 2;

        // Calculate distance from mouse to handle center
        const distance = Math.sqrt(
          Math.pow(event.clientX - handleCenterX, 2) +
            Math.pow(event.clientY - handleCenterY, 2),
        );

        // If within connection radius, hide the plus icon
        if (distance <= CONNECTION_RADIUS) {
          isWithinSnapDistance = true;
          break;
        }
      }

      if (isWithinSnapDistance) {
        setShowPlusIcon(false);
        setPlusIconPosition(null);
      } else {
        // Show plus icon at mouse position when not over or near a handle
        setPlusIconPosition({ x: event.clientX, y: event.clientY });
        setShowPlusIcon(true);
      }
    };

    const handleMouseUp = () => {
      // Clean up on mouse up (connection end will also be called)
      setIsConnecting(false);
      setConnectingHandleType(null);
      setShowPlusIcon(false);
      setPlusIconPosition(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isConnecting, connectingHandleType]);

  // Auto-fit view when workflow finishes loading and nodes are available
  const [hasInitiallyFitted, setHasInitiallyFitted] = useState(false);

  // Reset fit flag when a new workflow starts loading
  useEffect(() => {
    if (isLoadingWorkflow || isLoadingTemplate) {
      setHasInitiallyFitted(false);
    }
  }, [isLoadingWorkflow, isLoadingTemplate]);

  useEffect(() => {
    // Only fit view when:
    // 1. Workflow/template loading is complete
    // 2. Nodes are available
    // 3. Haven't fitted yet
    const isWorkflowLoaded = !isLoadingWorkflow && !isLoadingTemplate;
    if (isWorkflowLoaded && nodes.length > 0 && !hasInitiallyFitted) {
      // Use setTimeout to ensure ReactFlow has fully rendered the nodes
      const timeoutId = setTimeout(() => {
        fitView({ padding: 0.1 });
        setHasInitiallyFitted(true);
      }, 200); // Increased timeout to ensure nodes are fully rendered

      return () => clearTimeout(timeoutId);
    }
  }, [
    nodes.length,
    fitView,
    hasInitiallyFitted,
    isLoadingWorkflow,
    isLoadingTemplate,
  ]);

  // Update node data with statuses (results are now stored directly in nodes)
  const nodesWithStatuses = useMemo(() => {
    const nodesWithStatuses = nodes.map((node) => {
      // Get node-specific status events
      const nodeStatusesForNode = nodeStatuses[node.id] || [];
      const latestNodeStatus =
        [...nodeStatusesForNode]
          .reverse()
          .find(
            (event) => event.event_type !== WorkflowEventTypes.LIVE_TRANSCRIPT,
          ) ?? null;

      // Per-node status flags derived from the event log.  Pure
      // helper so it can be unit-tested without mounting the editor;
      // see ``helper/derive-node-status.ts`` for the interleave
      // semantics (RETRY_SCHEDULED supersedes prior emit_error etc.).
      const { isCompleted, hasError, isRetrying, isExecutingFromEvents } =
        deriveNodeStatusFromEvents(nodeStatusesForNode);
      const isExecuting = isExecutingWorkflow && isExecutingFromEvents;

      // Ensure node has valid position data - this fixes the ReactFlow error
      const validPosition = {
        x: typeof node.position?.x === 'number' ? node.position.x : 0,
        y: typeof node.position?.y === 'number' ? node.position.y : 0,
      };

      // ``isCached`` is true when this node's result was seeded
      // from a previous run via the rerun-from-node flow.  Drives
      // the "Cached" badge on the node card so the user can see
      // at a glance which nodes ran fresh vs reused output.
      const isCached = cachedNodeIds?.has(node.id) ?? false;

      // ``isPausedWaiting`` is true when:
      //   - the workflow is paused at the run level, AND
      //   - this node was in flight when pause happened (i.e. it
      //     emitted BEGIN events but hasn't completed/failed).
      // The cancelled activity surfaces as "in flight" forever
      // from the FE's perspective because the cancellation never
      // produced a completion event.  Without this flag the node
      // would keep its yellow "executing" spinner indefinitely
      // while the workflow is paused, which reads as "still
      // running" and confuses the user.  When paused, GenericNode
      // swaps the yellow spinner for an amber Pause badge.
      const isPausedWaiting = isPausedProp && isExecuting && !isCompleted;

      // ``isRerunnable`` gates the per-node "play" button.  Show
      // it only in contexts where re-running this single node makes
      // sense:
      //
      //   * isPaused        -- the workflow is parked; clicking
      //                        play sends a resume signal that
      //                        invalidates this node + downstream.
      //   * isHistoricalData -- the user is viewing a past run;
      //                        clicking play starts a fresh run
      //                        seeded from the cached results.
      //
      // AND require that the node was actually executed (has a
      // terminal event in its status list).  Both successful and
      // failed nodes qualify -- failed nodes are often the most
      // useful rerun targets ("fix the error and retry from here").
      // Never show on a node that's currently executing or in the
      // amber paused-waiting state -- the click would race with
      // an in-flight cancellation.
      const wasExecuted = isCompleted || hasError;
      const isRerunnable =
        (isPausedProp || isHistoricalData) &&
        wasExecuted &&
        !isExecuting &&
        !isPausedWaiting;

      return {
        ...node,
        position: validPosition,
        data: {
          ...node.data,
          // WebSocket status updates
          nodeStatus: latestNodeStatus,
          nodeStatusEvents: nodeStatusesForNode,
          isConnected,
          isExecuting,
          hasError,
          isCompleted,
          isCached,
          isPausedWaiting,
          isRetrying,
          isRerunnable,
          progressPercent: undefined, // Not available in new event structure
          statusMessage:
            latestNodeStatus?.event_type === WorkflowEventTypes.STATUS_UPDATE
              ? (latestNodeStatus as StatusUpdateEvent).message
              : latestNodeStatus && isNodeEvent(latestNodeStatus)
                ? latestNodeStatus.dynamic_message
                : undefined,
          // Global loading state
          isLoading: isExecutingWorkflow,
          // Workflow lock state
          isLocked,
          isWorkflowLocked,
        },
      };
    });

    return nodesWithStatuses;
  }, [
    nodes,
    nodeStatuses,
    isExecutingWorkflow,
    isLocked,
    isWorkflowLocked,
    cachedNodeIds,
    isPausedProp,
    isHistoricalData,
    isConnected,
  ]);

  // Context menu handlers
  const handleNodeAdd = useCallback(
    (nodeStruct: NodeStruct, position?: { x: number; y: number }) => {
      if (isLocked) return;
      if (
        isWorkflowLocked &&
        !WORKFLOW_LOCK_ALLOWED_SUBTYPES.has(nodeStruct.node_sub_type)
      ) {
        notification.warning(
          'This workflow is locked. Only File Input and Text Input nodes can be added.',
        );
        return;
      }
      if (position) {
        // Convert screen coordinates to flow coordinates
        const flowPosition = screenToFlowPosition(position);
        addNode(nodeStruct, flowPosition);
      } else {
        addNode(nodeStruct);
      }

      if (!isExecutingWorkflow) {
        setActiveTab('configure');
      }
      setConsolePanelOpen(true);
    },
    [
      addNode,
      screenToFlowPosition,
      isExecutingWorkflow,
      setActiveTab,
      setConsolePanelOpen,
      isLocked,
      isWorkflowLocked,
      isHistoricalData,
      notification,
    ],
  );

  // Viewport in flow coords, insetted by sidebar overlays
  const getViewportInfo = useCallback(() => {
    const { x, y, zoom } = getViewport();
    const rect = (
      reactFlowWrapper.current as HTMLElement | null
    )?.getBoundingClientRect();
    const width = rect?.width ?? window.innerWidth;
    const height = rect?.height ?? window.innerHeight;

    const leftInset = previewLeftMargin / zoom;
    const rightInset = previewRightMargin / zoom;

    return {
      center: {
        x:
          (-x +
            previewLeftMargin +
            (width - previewLeftMargin - previewRightMargin) / 2) /
          zoom,
        y: (-y + height / 2) / zoom,
      },
      bounds: {
        x: -x / zoom + leftInset,
        y: -y / zoom,
        w: width / zoom - leftInset - rightInset,
        h: height / zoom,
      },
    };
  }, [getViewport, previewLeftMargin, previewRightMargin]);

  const handlePaste = useCallback(
    (position?: { x: number; y: number }) => {
      if (isLocked || isWorkflowLocked) return;

      const { center, bounds } = getViewportInfo();

      if (position) {
        const flowPosition = screenToFlowPosition(position);
        onPasteNode({ offset: flowPosition, viewportBounds: bounds });
      } else {
        onPasteNode({ viewportCenter: center, viewportBounds: bounds });
      }
    },
    [
      onPasteNode,
      getViewportInfo,
      screenToFlowPosition,
      isLocked,
      isWorkflowLocked,
      isHistoricalData,
      notification,
    ],
  );

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      if (isLocked || isWorkflowLocked) return;
      // when a connection is dropped on empty space... create a SelectNode
      if (connectionState.toNode === null && connectionState.fromNode) {
        const { clientX, clientY } =
          'changedTouches' in event ? event.changedTouches[0] : event;

        const fromHandleType: 'target' | 'source' =
          connectionState.fromHandle?.type || 'source';
        const fromNodeStruct = connectionState.fromNode!.data
          .struct as NodeStruct;
        const fromHandleId = connectionState.fromHandle?.id || null;
        const fromNodeId = connectionState.fromNode!.id;

        const isDraggingFromInput = fromHandleType === 'target';

        // Calculate position - offset left when dragging from input port
        const position = screenToFlowPosition({
          x: isDraggingFromInput ? clientX - 220 : clientX,
          y: clientY - 152,
        });

        // Get compatible nodes and accepted types based on direction
        let compatibleNodes: NodeStruct[];
        let acceptedTypes: string[];

        if (isDraggingFromInput) {
          compatibleNodes = getCompatibleSourceNodes(
            fromNodeStruct,
            fromHandleId || undefined,
          );

          // Find the input port to get accepted types
          const inputPort =
            fromNodeStruct.input_ports.find((p) => p.id === fromHandleId) ||
            fromNodeStruct.params.find(
              (p) => p.bindable_config?.input_port?.id === fromHandleId,
            )?.bindable_config?.input_port;

          acceptedTypes = inputPort?.port_type.accepted_types || [];
        } else {
          compatibleNodes = getCompatibleNodes(fromNodeStruct);
          acceptedTypes =
            fromNodeStruct.output_ports[0]?.port_type.accepted_types || [];
        }

        // If no compatible nodes found, don't create SelectNode
        if (compatibleNodes.length === 0) return;

        // Delegate the state write to the store so it reads fresh nodes/edges
        // via get() instead of the closure captured at mousedown time. This
        // prevents the stale-array overwrite that reverted uncommitted text.
        createSelectNodeForConnection({
          fromNodeId,
          fromHandleId,
          fromHandleType,
          position,
          compatibleNodes,
          acceptedTypes,
        });
      }
    },
    [
      screenToFlowPosition,
      // NOTE: nodes and edges are intentionally omitted — the store action
      // reads them via get() so the closure never goes stale.
      getCompatibleNodes,
      getCompatibleSourceNodes,
      createSelectNodeForConnection,
      isLocked,
      isWorkflowLocked,
    ],
  );

  // The multi-selection rect is `pointer-events: none` (see
  // generic-node/style.css) so clicks on the gaps between selected nodes
  // fall through to the pane underneath. React Flow's Pane unconditionally
  // clears the whole selection on any pane click, so swallow gap clicks here
  // in the capture phase to keep them inert, matching pre-fix behavior.
  //
  // `.react-flow__pane` is the outermost canvas container — it's an
  // ancestor of every node and edge, not a sibling — so a click on a node
  // or edge also matches `closest('.react-flow__pane')`. Those must be
  // excluded first, or this would swallow clicks meant for onNodeClick too.
  const handleSelectionGapClickCapture = useCallback(
    (event: React.MouseEvent) => {
      if (selectedNodeIds.length <= 1) return;
      const target = event.target as HTMLElement;
      if (
        target.closest('.react-flow__node') ||
        target.closest('.react-flow__edge')
      ) {
        return;
      }
      if (!target.closest('.react-flow__pane')) return;

      const selectionBox = document
        .querySelector('.react-flow__nodesselection-rect')
        ?.getBoundingClientRect();
      if (!selectionBox) return;

      const isInsideSelectionBox =
        event.clientX >= selectionBox.left &&
        event.clientX <= selectionBox.right &&
        event.clientY >= selectionBox.top &&
        event.clientY <= selectionBox.bottom;
      if (isInsideSelectionBox) {
        event.stopPropagation();
      }
    },
    [selectedNodeIds],
  );

  return (
    <div
      className={`${className} reactflow-wrapper`}
      ref={reactFlowWrapper}
      onClickCapture={handleSelectionGapClickCapture}
    >
      <CanvasContextMenu
        onNodeAdd={handleNodeAdd}
        onPaste={handlePaste}
        isLocked={isCanvasEditingLocked}
        onRerunFromNode={onRerunFromNode}
        // ``isHistoricalData`` on the menu actually means "the
        // re-run menu item is meaningful right now".  True in
        // both historical view AND while a live workflow is
        // paused -- both contexts support re-run-from-here via
        // the same dispatcher (see editor's requestRerunFromHere).
        isHistoricalData={isHistoricalData || isPausedProp}
        onGroupNodes={groupSelected}
        onUngroupNodes={ungroupFromContext}
        canGroupSelection={canGroup}
        groupEligibleCount={eligibleCount}
      >
        {/* NodeActionsProvider wraps the ReactFlow surface so every
            GenericNode rendered inside can read editor-scoped per-
            node action callbacks (currently just onRerunFromHere
            for the play button) without us having to stuff them
            onto each node's data payload. */}
        <NodeActionsProvider onRerunFromHere={onRerunFromHere}>
          <ReactFlow
            className={cn(
              interactionMode === 'sticky-note'
                ? 'cursor-mode-sticky-note'
                : interactionMode === 'mouse'
                  ? 'cursor-mode-mouse'
                  : 'cursor-mode-hand',
              isSpacePanning && 'space-panning',
            )}
            nodes={nodesWithStatuses}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onPaneClick={(event) => {
              onDismissStatusBarCompleted?.();
              setGroupContainerFocusedId(null);
              // Sticky-note placement: one click drops a note at the cursor
              // and auto-reverts to mouse mode (one-shot).
              if (
                interactionMode === 'sticky-note' &&
                !isLocked &&
                !isWorkflowLocked
              ) {
                const offset = getStickyNotePlacementScreenOffset(getZoom());
                const position = screenToFlowPosition({
                  x: event.clientX - offset.x,
                  y: event.clientY - offset.y,
                });
                addStickyNote(position, {
                  backgroundColor: pendingStickyNoteColor,
                });
                handleModeChange('mouse');
              }
            }}
            onConnect={isLocked || isWorkflowLocked ? undefined : onConnect}
            onConnectStart={
              isLocked || isWorkflowLocked
                ? undefined
                : (_event, params) => {
                    const handleType = params.handleType as 'source' | 'target';
                    setActiveConnect({
                      side: handleType,
                      nodeId: params.nodeId || '',
                      handleId: params.handleId || '',
                    });
                    // Start tracking connection for plus icon (only for source handles)
                    setIsConnecting(true);
                    setConnectingHandleType(handleType);
                    setShowPlusIcon(false);
                    setPlusIconPosition(null);
                  }
            }
            onConnectEnd={(e, state) => {
              clearActiveConnect();
              // Clean up plus icon state
              setIsConnecting(false);
              setConnectingHandleType(null);
              setShowPlusIcon(false);
              setPlusIconPosition(null);
              if (!isLocked && !isWorkflowLocked) {
                onConnectEnd(e, state);
              }
            }}
            onReconnect={
              isLocked || isWorkflowLocked
                ? undefined
                : (oldEdge, newConnection) => {
                    onReconnect(
                      oldEdge,
                      newConnection,
                      edgeReconnectSuccessful,
                    );
                  }
            }
            onReconnectStart={
              isLocked || isWorkflowLocked
                ? undefined
                : (_) => onReconnectStart(edgeReconnectSuccessful)
            }
            onReconnectEnd={
              isLocked || isWorkflowLocked
                ? undefined
                : (_, edge) => {
                    onReconnectEnd(edgeReconnectSuccessful, edge);
                  }
            }
            onDelete={
              isLocked || isWorkflowLocked
                ? undefined
                : ({ nodes, edges }) =>
                    onNodesEdgesDelete({
                      nodes: nodes as CustomNode[],
                      edges: edges as CustomEdge[],
                    })
            }
            onSelectionChange={onSelectionChange}
            onNodeClick={onNodeClick}
            onNodeDragStart={(_event, node, draggedNodes) => {
              if (node.type === NodeType.GroupNode) {
                setGroupContainerFocusedId(node.id);
              }

              // Only deselect if dragging a single node that wasn't already selected
              // This prevents blue highlight when picking up an unselected node
              // but preserves selection when dragging an already-selected node
              if (
                draggedNodes.length === 1 &&
                !selectedNodeIds.includes(node.id) &&
                node.type !== NodeType.GroupNode
              ) {
                setReactFlowNodes((nds) =>
                  nds.map((n) => ({
                    ...n,
                    selected: false,
                    data: { ...n.data, isSelected: false },
                  })),
                );
                deselectAllNodes();
              }
            }}
            onNodeDrag={(event, _node, draggedNodes) => {
              if (isLocked || isWorkflowLocked) return;
              const { clientX, clientY } = event;
              const draggedIds = new Set(draggedNodes.map((n) => n.id));
              cancelAnimationFrame(dragOverRafRef.current);
              dragOverRafRef.current = requestAnimationFrame(() => {
                setDragOverNodeId(
                  findDropTargetId(
                    { clientX, clientY },
                    screenToFlowPosition,
                    getIntersectingNodes,
                    draggedIds,
                  ),
                );
              });
            }}
            onNodeDragStop={(_event, node, draggedNodes) => {
              setDragOverNodeId(null);

              if (node.type === NodeType.GroupNode) {
                // onSelectionChange can fire before drag end and clear focus
                // (and can leave the children deselected too, since dragging
                // doesn't go through selectGroupChildren); re-assert both
                // after React Flow finishes its selection callbacks so
                // keyboard Delete/Backspace and the Ungroup toolbar's
                // actions still target the group's children post-drag.
                setTimeout(() => {
                  setGroupContainerFocusedId(node.id);
                  selectGroupChildren(node.id);
                }, 0);
              }

              // Auto-connect dragged nodes to the drop target if applicable
              if (!isLocked && !isWorkflowLocked) {
                const draggedIds = new Set(draggedNodes.map((n) => n.id));
                const dropTargetId = findDropTargetId(
                  _event,
                  screenToFlowPosition,
                  getIntersectingNodes,
                  draggedIds,
                );
                const dropTarget = dropTargetId
                  ? (nodes.find((n) => n.id === dropTargetId) as
                      | CustomNode
                      | undefined)
                  : undefined;

                if (dropTarget?.data?.struct) {
                  const targetStruct = dropTarget.data.struct;

                  // Determine connection direction from the first connectable node
                  const firstResult = draggedNodes.reduce<
                    ReturnType<typeof resolveAutoConnection>
                  >((found, dn) => {
                    if (found) return found;
                    const s = (dn as CustomNode).data?.struct;
                    return s ? resolveAutoConnection(s, targetStruct) : null;
                  }, null);

                  // Suppress onConnect snapshots so the entire auto-connect +
                  // reposition is a single undo step.
                  withSuppressedSnapshots(() => {
                    draggedNodes.forEach((draggedNode) => {
                      const droppedStruct = (draggedNode as CustomNode).data
                        ?.struct;
                      if (!droppedStruct) return;
                      applyAutoConnection(
                        draggedNode.id,
                        droppedStruct,
                        dropTarget,
                        targetStruct,
                        onConnect,
                        isValidEdgeConnection,
                      );
                    });
                  });

                  // Reposition dragged nodes relative to the drop target.
                  // Read fresh nodes from the store because applyAutoConnection
                  // → onConnect may have mutated node data (e.g. addParamPortConn).
                  if (firstResult) {
                    const freshNodes = useNodeStore.getState().nodes;
                    const freshNodeById = new Map(
                      freshNodes.map((n) => [n.id, n]),
                    );
                    const freshDropTarget =
                      freshNodeById.get(dropTarget.id) ?? dropTarget;
                    // calculateDropPosition works in whatever coordinate space
                    // dropTarget.position is in, which is relative to its
                    // parent group (if any) rather than the canvas. Use the
                    // absolute position (resolved from the node tree, not
                    // React Flow's measured internals, which can be
                    // undefined) so the computed basePos is absolute too,
                    // then convert back to each dragged node's own
                    // parent-relative space below — otherwise a grouped
                    // dragged node (or a target in a different group) would
                    // land in the wrong spot.
                    const dropTargetAbsolutePosition = getAbsolutePosition(
                      freshDropTarget,
                      freshNodeById,
                    );
                    const basePos = calculateDropPosition(
                      dropTargetAbsolutePosition,
                      {
                        ...freshDropTarget,
                        position: dropTargetAbsolutePosition,
                      },
                      draggedNodes.length,
                      firstResult.direction,
                    );
                    // This reposition bypasses onNodesChange (the only other
                    // place expandGroupNodes normally runs from), so a
                    // grouped child moved into/out of its parent's bounds
                    // here would otherwise leave the container's
                    // width/height stale until some unrelated canvas edit.
                    setNodes(
                      expandGroupNodes(
                        repositionDraggedNodesAbsolute(
                          freshNodes,
                          draggedNodes.map((dn) => dn.id),
                          basePos,
                        ),
                      ),
                    );
                  }

                  // Existing canvas nodes won't trigger onNodesChange('dimensions'),
                  // so explicitly save the final state as the undo point.
                  useNodeStore.getState().saveSnapshot();
                }
              }

              // Only deselect if dragging a single node that wasn't already selected
              if (
                draggedNodes.length === 1 &&
                !selectedNodeIds.includes(node.id) &&
                node.type !== NodeType.GroupNode
              ) {
                // Note: setTimeout of 0ms is needed here to prevent a deslection/selection bug due to onNodeDragStop being called BEFORE
                // React Flow finishes its internal callback (like onSelectionChange).
                setTimeout(() => {
                  setReactFlowNodes((nds) =>
                    nds.map((n) => ({
                      ...n,
                      selected: false,
                      data: { ...n.data, isSelected: false },
                    })),
                  );
                  deselectAllNodes();
                }, 0);
              }
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onDrop={
              isLocked || isWorkflowLocked
                ? undefined
                : (e: React.DragEvent<HTMLDivElement>) => {
                    const dropTargetNodeId =
                      findDropTargetId(
                        e,
                        screenToFlowPosition,
                        getIntersectingNodes,
                      ) ?? undefined;

                    const newNodeId = onDrop(
                      e,
                      dndPayload,
                      screenToFlowPosition,
                      dropTargetNodeId,
                    );
                    setDndPayload(null);
                    // Only open configure tab if a node was actually created
                    if (newNodeId) {
                      if (!isExecutingWorkflow) {
                        setActiveTab('configure');
                      }
                      setConsolePanelOpen(true);
                    }
                  }
            }
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
              onDragOver(e);
              if (!dndPayload) return;
              const { clientX, clientY } = e;
              cancelAnimationFrame(dragOverRafRef.current);
              dragOverRafRef.current = requestAnimationFrame(() => {
                setDragOverNodeId(
                  findDropTargetId(
                    { clientX, clientY },
                    screenToFlowPosition,
                    getIntersectingNodes,
                  ),
                );
              });
            }}
            onDragLeave={() => setDragOverNodeId(null)}
            // NOTE: fitView only zoom to the first new node added on a blank canvas
            // for subsequent nodes, the zoom behavior is controlled by the options param of the addNode fn in node-store
            fitView
            fitViewOptions={{
              maxZoom: 1,
            }}
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{
              animated: true,
              style: {
                stroke: 'var(--color-muted-foreground)',
                strokeWidth: 2,
              },
              type: 'smoothstep',
            }}
            style={{ background: 'var(--color-background)' }}
            connectionRadius={30}
            // Lock/Unlock functionality (canvas lock OR workflow lock)
            nodesDraggable={!isLocked && !isWorkflowLocked}
            nodesConnectable={!isLocked && !isWorkflowLocked}
            elementsSelectable={true} // Always allow selection for viewing
            // Interaction mode controls
            panOnDrag={
              isSpacePanning
                ? true
                : interactionMode === 'sticky-note'
                  ? false
                  : interactionMode === 'hand'
                    ? true
                    : [1]
            }
            zoomOnScroll={!isLocked && !isTextareaHovered}
            // zoomOnDoubleClick={!isLocked}
            // zoomOnPinch={!isLocked}
            panOnScroll={interactionMode === 'mouse' && !isTextareaHovered}
            selectionOnDrag={
              !isSpacePanning &&
              !isLocked &&
              !isWorkflowLocked &&
              interactionMode === 'mouse'
            }
            proOptions={{ hideAttribution: true }}
            deleteKeyCode={
              isLocked || isWorkflowLocked ? null : ['Delete', 'Backspace']
            }
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

            {/* Multi-select Group toolbar — floats above the current selection */}
            <MultiSelectGroupToolbar
              onGroup={groupSelected}
              onCopy={() => onCopyNode()}
              onDuplicate={() => onDuplicateNode()}
              onDelete={() => onNodesEdgesDelete()}
              isLocked={!!isCanvasEditingLocked}
            />

            {/* Ungroup toolbar — floats above the focused group container */}
            <UngroupToolbarOverlay isLocked={!!isCanvasEditingLocked} />

            {isMiniMapVisible && (
              <MiniMap
                position='bottom-left'
                nodeStrokeWidth={3}
                pannable
                zoomable
                className='mb-20!'
                style={{
                  background: 'var(--color-background)',
                  border: '2px solid var(--color-border)',
                }}
                maskColor='oklch(0.4 0 100 / 80%)'
                nodeColor='var(--color-muted-foreground)'
                nodeStrokeColor='var(--color-muted-foreground)'
              />
            )}

            {/* Chat shortcut bar - hides itself when chat tab is open. The
                shortcut targets the node explorer's chat tab, so it only has
                something to open where that explorer is the chat: not when
                embedded, and not on the truly-new canvas under the unified
                chat design, where the operator rail replaces it instead. An
                existing workflow that falls through to this standalone page
                (flag on, no primary agent) still has the explorer, so it
                keeps this shortcut too. */}
            {!embedded && (!isCanvasA2aChatEnabled || !isNewWorkflow) && (
              <Panel
                position='bottom-left'
                className='mb-2 ml-2'
                style={{ zIndex: 30 }}
              >
                <ChatShortcutBar panelGroupWidth={panelGroupWidth} />
              </Panel>
            )}

            <Panel position='bottom-center' style={{ zIndex: 30 }}>
              <MouseControlBar
                mode={interactionMode}
                onModeChange={handleModeChange}
                isLocked={isLocked}
                isWorkflowLocked={isWorkflowLocked}
                onToggleLock={onToggleLock}
                isOwner={isOwner}
                onCloneWorkflow={onCloneWorkflow}
                setIsMiniMapVisible={() =>
                  setIsMiniMapVisible(!isMiniMapVisible)
                }
                onAutoLayout={handleAutoLayout}
                panelGroupWidth={panelGroupWidth}
              />
            </Panel>

            <Panel position='bottom-right' style={{ zIndex: 30 }}>
              {addNodesEnabled ? (
                <AddNodesButton disabled={isLocked || isWorkflowLocked} />
              ) : (
                <UtilityPanel panelGroupWidth={panelGroupWidth} />
              )}
            </Panel>

            {/* Preview Panel */}
            {isPreviewVisible && previewData && (
              <div
                className={previewPanelClassName}
                style={previewPanelStyle}
                onClick={isPreviewVisible ? hidePreview : undefined}
              >
                <div className='z-40 flex-1 w-full max-h-full overflow-auto flex min-w-0'>
                  <ResultPreviewPanel
                    result={previewData.result}
                    reactFlowGeneratedId={previewData.reactFlowGeneratedId}
                    statusEvents={previewData.statusEvents}
                    isLoading={previewData.isLoading}
                    isVisible={isPreviewVisible}
                    onClose={hidePreview}
                    icon={previewData.icon}
                    displayName={previewData.displayName}
                    nodeType={previewData.nodeType}
                  />
                </div>
              </div>
            )}

            {/* Files Preview Panel */}
            {isFilesPreviewVisible && filesPreviewData && (
              <div
                className={`${previewPanelClassName} items-center`}
                style={{ ...previewPanelStyle, height: '100vh' }}
                onClick={hideFilesPreview}
              >
                <div className='z-40 flex-1 w-full max-h-full overflow-auto flex min-w-0'>
                  <FilesPreviewPanel
                    files={filesPreviewData.files}
                    nodeDisplayName={filesPreviewData.displayName}
                    icon={filesPreviewData.icon}
                    nodeType={filesPreviewData.nodeType}
                    isVisible={isFilesPreviewVisible}
                    isLoading={filesPreviewData.isLoading}
                    error={filesPreviewData.error}
                    onClose={hideFilesPreview}
                  />
                </div>
              </div>
            )}

            {/* Plus icon indicator for empty space connections */}
            {showPlusIcon && plusIconPosition && (
              <div
                className='fixed pointer-events-none z-50'
                style={{
                  left: `${isDraggingFromInputPort ? plusIconPosition.x - 30 : plusIconPosition.x + 30}px`,
                  top: `${plusIconPosition.y}px`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 shadow-lg'>
                  <Plus className='w-5 h-5 text-white' strokeWidth={2.5} />
                </div>
              </div>
            )}
            <StickyNotePlacementPreview
              active={
                interactionMode === 'sticky-note' &&
                !isLocked &&
                !isWorkflowLocked
              }
              color={pendingStickyNoteColor}
            />
          </ReactFlow>
          <UploadProgressWidget />
        </NodeActionsProvider>
      </CanvasContextMenu>
    </div>
  );
};

const NodeExplorerSidebarHeaderButtons = () => {
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const { workflowId } = useParams();
  const { isShareClientDataWarningEnabled } = useLaunchDarkly();
  const { isMaximized: isPanelOpen } = useNodeExplorerStore();

  const showWarningBar =
    isShareClientDataWarningEnabled &&
    currentWorkflow?.default_role != null &&
    currentWorkflow.default_role !== 'workflow:viewer';

  // Only trust the store once it holds the workflow this route is showing --
  // otherwise the header keeps the previous name, or claims "New Workflow" for
  // an existing one, while the next load is in flight.
  let headerTitle: string | null = null;
  if (workflowId === 'new') {
    headerTitle = 'New Workflow';
  } else if (currentWorkflow && currentWorkflow.id === workflowId) {
    headerTitle = currentWorkflow.name;
  }

  return (
    <div className='flex items-center gap-2 min-w-0 flex-1 overflow-hidden'>
      <div
        className={`min-w-0 overflow-hidden ${!isPanelOpen ? 'max-w-[350px]' : ''}`}
      >
        {headerTitle === null ? (
          <Skeleton className='h-4 w-32' />
        ) : (
          <TruncateTextWithTooltip
            text={headerTitle}
            className='text-sm font-medium'
          />
        )}
      </div>
      {showWarningBar && (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className='size-4 shrink-0 text-amber-600 dark:text-amber-400' />
          </TooltipTrigger>
          <TooltipContent>
            This workflow is accessible to all org members. It should not
            contain client data.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export interface WorkflowEditorPageProps {
  /**
   * Renders the editor inside a host pane (the chat-home surface slot)
   * instead of as a route: the node explorer sidebar is dropped in favour of
   * the host's own chat rail, and the page's route-level behaviours — the
   * `?runId=` deep link and the unsaved-changes navigation blocker — are
   * suppressed so they cannot hijack the host's URL or navigation. The host
   * names a run through `runIdOverride` instead, and can opt back into the
   * unsaved-changes blocker via `unsavedChangesWarningEnabled` when it is
   * this route's own anchor rather than a layered peek.
   */
  embedded?: boolean;
  /** Workflow to open when there is no `:workflowId` route param. */
  workflowIdOverride?: string;
  /**
   * The host's side of the `?runId=` deep link when embedded, since the pane
   * cannot read the host's address bar: the run to open on the canvas. A live
   * run re-attaches to its session; a finished one loads for review.
   */
  runIdOverride?: string;
  /**
   * The other half of that binding: told each run the pane moves to, or null
   * when it clears one, so the host's URL keeps naming what is on screen.
   */
  onRunChange?: (runId: string | null) => void;
  /**
   * Re-enables the unsaved-changes navigation blocker while embedded. Only
   * meaningful when this workflow is the host route's own anchor surface —
   * for a layered peek at another workflow (chat-home, artifact viewer) the
   * blocker would trap the user in chat over edits made in a side pane.
   */
  unsavedChangesWarningEnabled?: boolean;
  /**
   * Called when the user activates "View Artifacts" from the console toolbar.
   * When supplied the gallery opens in the host surface slot instead of
   * navigating to the standalone /artifacts page.
   */
  onViewArtifacts?: (workflowId: string) => void;
  /**
   * Called after a successful delete when embedded, since the route
   * navigation that closes the standalone page is suppressed here -- without
   * it the pane keeps showing a workflow that no longer exists.
   */
  onDeleted?: () => void;
}

export const WorkflowEditorPage: React.FC<WorkflowEditorPageProps> = ({
  embedded = false,
  workflowIdOverride,
  runIdOverride,
  onRunChange,
  unsavedChangesWarningEnabled = false,
  onViewArtifacts,
  onDeleted,
}) => {
  const { workflowId: routeWorkflowId } = useParams();
  const workflowId = workflowIdOverride ?? routeWorkflowId;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useOrgNavigate();
  const notification = useNotification();
  // The chat rail beside this canvas renders the review for a paused node
  // off this bridge -- it has no run card for a run reopened from Past Runs
  // or started before a reload, but the canvas still sees the node waiting.
  const setCanvasPausedNode = useCanvasPauseBridgeStore(
    (state) => state.setPausedNode,
  );
  const clearCanvasPausedNode = useCanvasPauseBridgeStore(
    (state) => state.clearPausedNode,
  );
  useEffect(() => () => clearCanvasPausedNode(), [clearCanvasPausedNode]);
  const {
    initialParams,
    setUrlState: writeRunDeepLink,
    clearUrlState: clearRunDeepLink,
  } = useWorkflowDeepLink(!embedded, {
    workflowId,
    runId: runIdOverride,
    onRunChange,
  });
  // Embedded, a run the editor writes comes straight back as runIdOverride.
  // It is already on screen, so mark it applied or the deep-link effect
  // would load it a second time; clearing it lets the same run be reopened
  // from a card later. Standalone, the router never observes the URL write,
  // so the effect does not re-fire for it and the marker stays the effect's.
  const appliedDeepLinkRunIdRef = useRef<string | null>(null);
  const setUrlState = useCallback(
    (params: Partial<DeepLinkParams>) => {
      if (embedded && params.runId !== undefined) {
        appliedDeepLinkRunIdRef.current = params.runId;
      }
      writeRunDeepLink(params);
    },
    [embedded, writeRunDeepLink],
  );
  const clearUrlState = useCallback(() => {
    if (embedded) appliedDeepLinkRunIdRef.current = null;
    clearRunDeepLink();
  }, [embedded, clearRunDeepLink]);
  const { user, isAdmin } = useContext(AuthContext);
  const {
    isShareClientDataWarningEnabled,
    isWorkflowLockingEnabled,
    isWorkflowSchedulingEnabled,
    isCanvasA2aChatEnabled,
    isArtifactsEnabled,
  } = useLaunchDarkly();
  const [nodeStatuses, setNodeStatuses] = useState<
    Record<string, NodeStatusEvent[]>
  >({});
  // Ref to access nodeStatuses in useEffect without causing re-subscriptions
  const nodeStatusesRef = useRef(nodeStatuses);
  nodeStatusesRef.current = nodeStatuses;

  // Batch WebSocket events into a single state update per animation frame
  // to avoid one re-render per incoming event during rapid streaming
  const pendingEventsRef = useRef<Map<string, NodeStatusEvent[]>>(new Map());
  const flushScheduledRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);

  const flushPendingEvents = useCallback(() => {
    flushScheduledRef.current = false;
    rafIdRef.current = null;
    const pending = pendingEventsRef.current;
    if (pending.size === 0) return;

    // Snapshot and clear BEFORE calling setState -- the updater function may
    // run asynchronously due to React 18 automatic batching, so we must not
    // reference `pending` inside it (it could already be mutated by new WS events).
    const batch = new Map(pending);
    pending.clear();

    setNodeStatuses((prev) => {
      const next = { ...prev };
      batch.forEach((events, key) => {
        next[key] = trimLiveTranscriptEvents([...(prev[key] || []), ...events]);
      });
      return next;
    });
  }, []);

  const queueNodeStatusEvent = useCallback(
    (key: string, event: NodeStatusEvent) => {
      const pending = pendingEventsRef.current;
      if (!pending.has(key)) pending.set(key, []);
      pending.get(key)!.push(event);

      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        rafIdRef.current = requestAnimationFrame(flushPendingEvents);
      }
    },
    [flushPendingEvents],
  );

  // Cancel any pending RAF on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
        flushScheduledRef.current = false;
      }
    };
  }, []);

  // Ref to store resetBaseline function (populated after useAutoSave hook)
  const resetBaselineRef = useRef<(() => void) | null>(null);

  const [isExecutingWorkflow, setIsExecutingWorkflow] = useState(false);
  const isExecutingWorkflowRef = useRef(false);
  isExecutingWorkflowRef.current = isExecutingWorkflow;

  // Track chat-initiated runs so we can poll for promoted artifacts
  const chatRunPendingRef = useRef(false);
  const chatRunIdRef = useRef<string | null>(null);
  // The run the current chat request dispatched, handed back to the chat so
  // it can follow the run. Separate from chatRunIdRef, which the run-ended
  // effect consumes and clears on paths that finalize before we get here.
  const chatDispatchedRunIdRef = useRef<string | null>(null);
  const chatRunSignalSeqRef = useRef(0);
  const [chatRunEndedSignal, setChatRunEndedSignal] =
    useState<ChatRunEndedSignal | null>(null);
  // Artifact viewer panel state — when set, replaces the canvas
  const [artifactPreview, setArtifactPreview] = useState<{
    artifactId: string;
    userRole?: ArtifactShareRole;
  } | null>(null);
  const [workflowStartedAt, setWorkflowStartedAt] = useState<string | null>(
    null,
  );

  // ── Pause / resume state ────────────────────────────────────
  //
  // ``isPaused`` tracks the workflow_run.status=PAUSED condition.
  // It's set when the lifecycle/paused WS event arrives (or
  // optimistically by the pause click handler) and cleared on
  // lifecycle/resumed.
  //
  // ``pauseState`` mirrors the JSONB snapshot the backend wrote to
  // workflow_run.pause_state.  The status bar reads it for the
  // "23 done · 4 cancelled mid-run" sub-text.
  //
  // ``pauseSnapshot`` is the FE's own snapshot of nodes/edges at
  // pause time, used by computeEditedNodeIds to figure out which
  // nodes the user changed.  Cleared on resume.
  //
  // ``isPausing`` / ``isResuming`` are transient client-side
  // affordances guarding double-clicks while we wait for the WS
  // lifecycle event to land.
  //
  // ``cachedNodeIds`` is set when the run started via rerun-from-node:
  // the lifecycle/rerun_seeded event lists which nodes were seeded
  // from the source run; the FE uses this to render a "Cached"
  // badge on those nodes.
  const [isPaused, setIsPaused] = useState(false);
  const [, setPauseState] = useState<PauseStateSnapshot | null>(null);
  const [pauseSnapshot, setPauseSnapshot] = useState<PauseSnapshot | null>(
    null,
  );
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  // Transient client-side affordance for Stop guards the toolbar
  // between the stop click and the backend's termination confirmation (see stopWatchdogRef below).
  const [isStopping, setIsStopping] = useState(false);

  // The BE can park the workflow on N parallel clarification requests
  // (parallel LLM nodes calling ``ask_user_clarification``). Tracked as
  // a FIFO queue keyed on ``nodeId``: appended by
  // ``CLARIFICATION_REQUESTED``, pruned by ``CLARIFICATION_RESOLVED``,
  // and answered oldest-first to match the transcript order. The type is
  // imported from the sidebar (single source of truth) so producer (this
  // file) and consumer (sidebar) can't drift.
  const [pendingClarifications, setPendingClarifications] = useState<
    PendingClarification[]
  >([]);
  // Render-synced mirror so long-lived WS callbacks can read the current
  // queue without adding it to their deps (which would re-bind the WS
  // subscription on every mutation).
  const pendingClarificationsRef = useRef<PendingClarification[]>([]);
  pendingClarificationsRef.current = pendingClarifications;
  // A queue rather than a single slot: two resolutions in one batched tick
  // used to collapse into "last wins", leaving the other card unflipped.
  const [resolvedClarificationsQueue, setResolvedClarificationsQueue] =
    useState<
      Array<{
        requestedAt: number;
        nodeId: string;
        timedOut: boolean;
        skipped: boolean;
        abandoned: boolean;
        resolvedAt: number;
      }>
    >([]);
  // Whether we already auto-opened the sidebar for this clarification,
  // so a re-render doesn't reopen it after the user closed it.
  const clarificationAutoOpenedRef = useRef(false);
  // Sessions we've already attempted reload-recovery for, so re-running
  // the effect for the same session doesn't re-fetch / duplicate cards.
  const recoveredClarificationsForSessionRef = useRef<string | null>(null);
  const [cachedNodeIds, setCachedNodeIds] = useState<Set<string>>(
    () => new Set(),
  );
  // Lineage tuple for the current rerun-from-node, if any.
  // `session` is the rerun's own session_id; `parent` is the source run
  // it inherits cached results from. Stored as a pair (instead of just
  // `parentSessionId`) so the lookup auto-invalidates when the user
  // navigates to a different past run — at which point `currentSession`
  // changes but the stored `session` no longer matches, and the parent
  // is correctly treated as inapplicable. Pairs with `cachedNodeIds`:
  // cached/seeded eval nodes don't get fresh OpenSearch eval docs in
  // the new session, so the Evals tab falls back to `parent` for them.
  const [rerunLineage, setRerunLineage] = useState<{
    session: string;
    parent: string;
  } | null>(null);
  // Refs so callbacks can read the freshest values without growing
  // their dependency arrays (which would re-subscribe the WS handler
  // every time a piece of pause state changed).
  const isPausedRef = useRef(false);
  isPausedRef.current = isPaused;
  const isPausingRef = useRef(false);
  isPausingRef.current = isPausing;
  const isResumingRef = useRef(false);
  isResumingRef.current = isResuming;
  const pauseSnapshotRef = useRef<PauseSnapshot | null>(null);
  pauseSnapshotRef.current = pauseSnapshot;

  // Watchdog timers for pause/resume.  Declared up here (before
  // finalizeWorkflowExecution) so the cleanup in finalize can read
  // and clear them.
  const pauseWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  const resumeWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  // Watchdog for Stop -- mirrors pauseWatchdogRef. If the backend
  // never confirms the stop (worker died silently, WS never
  // delivers termination), clears isStopping and warns instead of
  // leaving the toolbar stuck mid-stop forever.
  const stopWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  // Set synchronously when the user clicks Stop so an in-flight resume HTTP
  // call can be aborted and lifecycle/resumed ignored after stop wins.
  const stopRequestedRef = useRef(false);
  const resumeAbortControllerRef = useRef<AbortController | null>(null);
  // Set when a lifecycle/resumed WS event is dropped because a stop was
  // in flight. If the stop then fails, the resume it silently discarded
  // actually went through on the backend, so we replay it -- otherwise
  // the UI is stuck showing "paused"/"resuming" for a run that is really
  // executing.
  const droppedResumedDuringStopRef = useRef(false);
  // Session id from getRunSession() before handleRun / handleRerunFromNode
  // reaches runWorkflow. Lets Stop work during the bootstrap window.
  const bootstrapSessionIdRef = useRef<string | null>(null);
  // Bumped on each run bootstrap so stale async paths bail after Stop.
  const runBootstrapGenerationRef = useRef(0);
  // Survives RunTimerPill unmount/remount (e.g. closing the console sidebar
  // while paused) so the timer can restore its display and resume correctly.
  const timerElapsedMsRef = useRef<number>(0);

  /**
   * Finalizes the current workflow execution in the UI layer.
   * Marks the workflow as no longer executing and notifies the WebSocket service.
   * Does NOT send a stop/cancel signal to the backend workflow engine.
   */
  const finalizeWorkflowExecution = useCallback(() => {
    setIsExecutingWorkflow(false);
    isExecutingWorkflowRef.current = false;
    clearCanvasPausedNode();
    websocketService.markWorkflowCompleted();
    stopRequestedRef.current = false;
    droppedResumedDuringStopRef.current = false;
    bootstrapSessionIdRef.current = null;
    resumeAbortControllerRef.current?.abort();
    resumeAbortControllerRef.current = null;
    // Clear pause state on terminal transitions so the next run
    // starts with a clean slate.
    setIsPaused(false);
    setPauseState(null);
    setPauseSnapshot(null);
    setIsPausing(false);
    setIsResuming(false);
    setIsStopping(false);
    // ``cachedNodeIds`` is INTENTIONALLY NOT cleared here.  After
    // a rerun-from-node completes, the user wants to keep seeing
    // which nodes were re-used vs which were freshly executed --
    // the teal "cached" badge is part of the run's identity, not
    // its lifecycle.  It gets cleared in two places where it
    // actually IS meaningless:
    //   * handleRun (fresh run from scratch -- no caching applies)
    //   * the lifecycle/rerun_seeded WS handler (which REPLACES
    //     the set with the new run's seeded IDs anyway)
    // Wiping it on run-end was the cause of cached badges
    // disappearing the moment the workflow finished.
    // Cancel any in-flight pause/resume watchdogs.  If pause was
    // requested but the workflow finished naturally first, the
    // watchdog would otherwise fire with a misleading warning.
    if (pauseWatchdogRef.current !== null) {
      clearTimeout(pauseWatchdogRef.current);
      pauseWatchdogRef.current = null;
    }
    if (resumeWatchdogRef.current !== null) {
      clearTimeout(resumeWatchdogRef.current);
      resumeWatchdogRef.current = null;
    }
    if (stopWatchdogRef.current !== null) {
      clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = null;
    }
    timerElapsedMsRef.current = 0;
  }, []);

  // Collect fallback files from canvas artifact nodes (already spawned by
  // handleExecutionResultEvent) and set the signal so the node-explorer
  // sidebar can poll for promoted artifacts. Called both from the
  // isExecutingWorkflow-watching effect below (the normal case) and directly
  // from handleRun's post-dispatch abort path, for when the run already
  // finalized before dispatch continuation resumed — that transition to
  // isExecutingWorkflow=false already happened and won't happen again, so
  // waiting on the ref+effect would miss it.
  const emitChatRunEndedSignal = useCallback((runId: string) => {
    const { nodes: allNodes } = useNodeStore.getState();
    const fallbackFiles: FallbackChatFile[] = allNodes
      .filter((n) => n.type === NodeType.ArtifactNode)
      .map((n) => {
        const d = n.data as unknown as ArtifactNodeData;
        return {
          name: d.file?.name ?? 'artifact',
          mimeType: d.file?.mime_type,
        };
      });

    setChatRunEndedSignal({
      seq: ++chatRunSignalSeqRef.current,
      runId: runId === '__no_run_id__' ? null : runId,
      fallbackFiles,
    });
  }, []);

  const applyStopSuccessSideEffects = useCallback(
    (sessionId: string) => {
      markUserStoppedRun(sessionId);

      const pendingOnStop = pendingClarificationsRef.current;
      if (pendingOnStop.length > 0) {
        setResolvedClarificationsQueue((prev) => {
          const additions = pendingOnStop
            .filter(
              (p) =>
                !prev.some(
                  (r) =>
                    r.nodeId === p.nodeId && r.requestedAt === p.requestedAt,
                ),
            )
            .map((p) => ({
              requestedAt: p.requestedAt,
              nodeId: p.nodeId,
              timedOut: false,
              skipped: true,
              abandoned: false,
              resolvedAt: Date.now(),
            }));
          return additions.length > 0 ? [...prev, ...additions] : prev;
        });
        setPendingClarifications([]);
      }

      finalizeWorkflowExecution();
    },
    [finalizeWorkflowExecution],
  );

  /**
   * Returns true when an in-flight run bootstrap should bail out.
   * Handles Stop clicks that land before runWorkflow / rerunFromNode dispatch.
   */
  const abortRunBootstrapIfRequested = useCallback(
    async (
      bootstrapGeneration: number,
      sessionId: string | null,
      opts?: { runDispatched?: boolean },
    ): Promise<boolean> => {
      if (
        runBootstrapGenerationRef.current !== bootstrapGeneration ||
        !isExecutingWorkflowRef.current
      ) {
        return true;
      }
      if (!stopRequestedRef.current) {
        return false;
      }

      stopRequestedRef.current = false;
      bootstrapSessionIdRef.current = null;

      if (opts?.runDispatched && sessionId) {
        const { currentWorkflow: wf } = useWorkflowStore.getState();
        const org = useOrgStore.getState().currentOrg;
        if (wf?.id && org?.orgid) {
          try {
            const response = await stopWorkflow(wf.id, sessionId, org.orgid);
            if (
              !(
                ('detail' in response && typeof response.detail === 'string') ||
                response.success === false
              )
            ) {
              if (response.was_already_completed) {
                notification.info(
                  'This workflow had already finished. Run status synced.',
                );
              } else {
                notification.success('Workflow stopped successfully');
              }
              applyStopSuccessSideEffects(sessionId);
              return true;
            }
            // Stop call returned an error — the backend run may still be live.
            const errorMsg =
              'detail' in response && typeof response.detail === 'string'
                ? response.detail
                : response.message;
            notification.error('Failed to stop workflow: ' + errorMsg);
            bootstrapSessionIdRef.current = sessionId;
            return true;
          } catch (error) {
            notification.error(
              error instanceof Error
                ? error.message
                : 'Failed to stop workflow. Please try again.',
            );
            bootstrapSessionIdRef.current = sessionId;
            return true;
          }
        }
      }

      finalizeWorkflowExecution();
      notification.info('Workflow run cancelled');
      return true;
    },
    [applyStopSuccessSideEffects, finalizeWorkflowExecution, notification],
  );

  // Watchdog for Stop -- mirrors clearPauseWatchdog/clearResumeWatchdog
  // (declared further below, alongside handlePauseWorkflow) but needs to
  // exist here since handleStopWorkflow uses it and is declared first.
  const clearStopWatchdog = useCallback(() => {
    if (stopWatchdogRef.current !== null) {
      clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = null;
    }
  }, []);

  /**
   * Stops the running workflow via the API, shows a notification, then
   * finalizes the UI state.  Wired to the RunTimerPill's stop button.
   */
  const handleStopWorkflow = useCallback(async () => {
    // Preempt any in-flight resume so play-then-immediate-stop cannot
    // deliver a resume signal after (or racing with) this cancel.
    stopRequestedRef.current = true;
    resumeAbortControllerRef.current?.abort();
    resumeAbortControllerRef.current = null;
    if (resumeWatchdogRef.current !== null) {
      clearTimeout(resumeWatchdogRef.current);
      resumeWatchdogRef.current = null;
    }
    setIsResuming(false);

    const { currentWorkflow: wf, currentSession: session } =
      useWorkflowStore.getState();
    const org = useOrgStore.getState().currentOrg;

    if (!wf?.id || !org?.orgid) {
      stopRequestedRef.current = false;
      notification.error('Missing workflow information');
      return;
    }

    const sessionToStop = session ?? bootstrapSessionIdRef.current;
    if (!sessionToStop) {
      // Bootstrap phase: session not minted yet. handleRun / handleRerunFromNode
      // will honor stopRequestedRef once getRunSession() returns.
      if (!isExecutingWorkflowRef.current) {
        stopRequestedRef.current = false;
        notification.error('No workflow is running');
      }
      return;
    }

    setIsStopping(true);

    // Arm the watchdog: if the backend never confirms the stop (worker
    // died silently, WS never delivers termination), clear isStopping
    // and surface a helpful message instead of leaving the toolbar
    // stuck mid-stop forever. Mirrors the pause watchdog above.
    clearStopWatchdog();
    stopWatchdogRef.current = setTimeout(() => {
      stopWatchdogRef.current = null;
      setIsStopping(false);
      if (isExecutingWorkflowRef.current) {
        notification.warning(
          'Stop is taking longer than expected. The workflow may have ' +
            'finished its current activity already, or the temporal worker ' +
            'may be running an older build. Check the worker logs and ' +
            'try again if needed.',
        );
      }
    }, 15000);

    try {
      const response = await stopWorkflow(wf.id, sessionToStop, org.orgid);

      if (
        ('detail' in response && typeof response.detail === 'string') ||
        response.success === false
      ) {
        const errorMsg =
          'detail' in response && typeof response.detail === 'string'
            ? response.detail
            : response.message;
        notification.error('Failed to stop workflow: ' + errorMsg);
        stopRequestedRef.current = false;
        setIsStopping(false);
        clearStopWatchdog();
        if (droppedResumedDuringStopRef.current) {
          droppedResumedDuringStopRef.current = false;
          setIsPaused(false);
          setPauseState(null);
          setPauseSnapshot(null);
        }
        return;
      }

      if (response.was_already_completed) {
        clearStopWatchdog();
        setIsStopping(false);
        notification.info(
          'This workflow had already finished. Run status synced.',
        );
        applyStopSuccessSideEffects(sessionToStop);
        return;
      }

      // Signal accepted -- keep isStopping true and wait for the
      // backend's termination confirmation (handleTerminationEvent /
      // handleExecutionResultEvent) before toasting or tearing down the
      // run, mirroring how pause waits for lifecycle/paused.
      // finalizeRacedTerminalEvent decides there whether the eventual
      // terminal event is a genuine stop, a race with natural
      // completion, or an unrelated failure -- it does NOT mark the
      // run as user-stopped here, so a real failure during the wait
      // still surfaces its own "Run Failed" toast instead of being
      // silently swallowed.
    } catch (error) {
      stopRequestedRef.current = false;
      setIsStopping(false);
      clearStopWatchdog();
      if (droppedResumedDuringStopRef.current) {
        droppedResumedDuringStopRef.current = false;
        setIsPaused(false);
        setPauseState(null);
        setPauseSnapshot(null);
      }
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to stop workflow. Please try again.',
      );
    }
  }, [applyStopSuccessSideEffects, clearStopWatchdog, notification]);

  const [isLocked, setIsLocked] = useState(false);
  const [isPastRunsPanelOpen, setIsPastRunsPanelOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [isRunNotificationDismissed, setIsRunNotificationDismissed] =
    useState(false);
  // Tracks the agentic-backend session_id for the selected past run.
  // This is what platform-api stores as `workflow_run_id` on eval results in
  // OpenSearch, so the Evals tab needs it (not selectedRunId, which is the
  // platform DB row id).
  const [selectedRunSessionId, setSelectedRunSessionId] = useState<
    string | null
  >(null);
  // created_at of the open past run (null for the latest/current run) — drives
  // the amber indicator + date on the Past Runs button.
  const [selectedRunCreatedAt, setSelectedRunCreatedAt] = useState<
    string | null
  >(null);
  const [isLoadingPastRun, setIsLoadingPastRun] = useState(false);
  const [isLoadingDeepLinkedRun, setIsLoadingDeepLinkedRun] = useState(() =>
    Boolean(initialParams.runId),
  );
  const [isStoppingIntervention, setIsStoppingIntervention] = useState(false);

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishingLoaderOpen, setIsPublishingLoaderOpen] = useState(false);
  const [isSaveWorkflowModalOpen, setIsSaveWorkflowModalOpen] = useState(false);
  const [saveWorkflowModalMode, setSaveWorkflowModalMode] = useState<
    'save' | 'editInfo' | 'template' | 'duplicate' | 'clone'
  >('save');
  const [publishingProgress, setPublishingProgress] = useState(0);
  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDuplicateWorkflowName, setPendingDuplicateWorkflowName] =
    useState<string | null>(null);
  const [shouldOpenDuplicateInNewTab, setShouldOpenDuplicateInNewTab] =
    useState(false);
  const [isForceSave, setIsForceSave] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const selectedNodeIdsFromStore = useNodeStore(
    useShallow(selectSelectedNodeIds),
  );
  const {
    nodes,
    edges,
    updateNodeResult,
    fetchNodes,
    isTextareaHovered,
    setNodes,
    setEdges,
    saveSnapshot,
    setLoadingNodes,
    setCancellableNodes,
    setLastSelectedNodeId,
    setGroupContainerFocusedId,
    reduceSelectionToNode,
    isLoadingNodeList,
    deselectAllNodes,
  } = useNodeStore();
  const { currentOrg } = useOrgStore();
  const {
    setMaximized: setNodeExplorerMaximized,
    reset: resetNodeExplorer,
    isMaximized: isNodeExplorerMaximized,
    panelSize: nodeExplorerPanelSize,
  } = useNodeExplorerStore();
  const groupedNodes = useNodeStore((s) => s.groupedNodes);
  const nodeIdsKey = useMemo(() => nodes.map((n) => n.id).join(','), [nodes]);

  // Catalog identity set — only recomputed when the catalog data changes.
  // Not gated on isLoadingNodeList so background refreshes don't flicker.
  const catalogIdentities = useMemo(() => {
    if (Object.keys(groupedNodes).length === 0) return null;
    return buildCatalogIdentitySet(groupedNodes);
  }, [groupedNodes]);

  // Computed synchronously so the run button is immediately correct on the same
  // render that isLoadingNodeList becomes false — no effect-delay gap.
  const hasDisabledNodes = useMemo(() => {
    if (!catalogIdentities) return false;
    return nodes.some((node) => {
      const struct = node.data?.struct;
      if (!struct) return false;
      if (
        node.type === NodeType.SelectNode ||
        node.type === NodeType.StickyNoteNode ||
        node.type === NodeType.GroupNode
      )
        return false;
      return !catalogIdentities.has(getCatalogNodeIdentityKey(struct));
    });
  }, [nodes, catalogIdentities]);

  const canRunWorkflowGraph = useMemo(
    () => hasExecutableWorkflowNodes(nodes),
    [nodes],
  );

  // Toggle lock function
  const toggleLock = useCallback(() => {
    // TEMPORARILY DISABLED: If user is not the owner, keep the lock locked
    // TODO: Re-enable owner blocking feature later
    // if (!isWorkflowOwner && isLocked) {
    //   return;
    // }
    setIsLocked((prev) => {
      const newLockedState = !prev;
      // When locking, deselect all edges
      if (newLockedState) {
        setEdges(
          edges.map((edge) => ({
            ...edge,
            selected: false,
          })),
        );
      }
      return newLockedState;
    });
  }, [edges, setEdges]);

  // Custom condition: ignore when input is focused OR textarea is hovered
  const shouldIgnoreKeyboardEvent = useCallback(() => {
    const activeElement = document.activeElement;
    const isInputFocused =
      activeElement?.tagName === 'INPUT' ||
      activeElement?.tagName === 'TEXTAREA' ||
      activeElement?.getAttribute('contenteditable') === 'true' ||
      activeElement?.closest('[contenteditable="true"]') !== null;

    // Don't trigger node operations if user is typing in a text field or hovering over textarea
    return isInputFocused || isTextareaHovered;
  }, [isTextareaHovered]);

  // Store getNodesBounds function from NodeCanvas
  const getNodesBoundsRef = useRef<
    ((nodes: Node[]) => Parameters<typeof getViewportForBounds>[0]) | null
  >(null);

  // Store fitView function from NodeCanvas for import functionality
  const fitViewRef = useRef<
    ((options?: { padding?: number; duration?: number }) => void) | null
  >(null);
  const {
    currentWorkflow,
    savedWorkflows,
    saveWorkflow,
    createBlankWorkflow,
    loadTemplateWorkflow,
    setCurrentSession,
    setCurrentWorkflow,
    setPublishToApi,
    loadedNodeStatuses,
    isHistoricalData,
    lastExecutionTimestamp,
    clearLoadedNodeStatuses,
    restoreCurrentVersion,
    getSavedWorkflow,
    loadWorkflow,
    deleteWorkflow,
    isLoadingWorkflow,
    isLoadingTemplate,
    resetLoadingState,
    duplicateWorkflow,
    currentSession,
    setPersistStatusHighlight,
    setIsHistoricalData,
    setLoadedNodeStatuses,
    setLastExecutionTimestamp,
    interventionState,
    clearInterventionState,
    isWorkflowAdmin,
  } = useWorkflowStore();

  // Pause click: optimistic state, fire backend signal, lifecycle/paused
  // event lands shortly after and confirms.  We snapshot nodes/edges
  // at the click point so the diff is anchored to what the user
  // saw when they paused.
  //
  // ``isPausing`` stays TRUE for the entire window between click and
  // the lifecycle/paused WS event landing -- the network round trip
  // alone can be much shorter than the workflow's actual pause
  // (cancelling an in-flight LLM call takes a few seconds).  If we
  // cleared isPausing the moment fetch returned, the bar would
  // briefly revert to "Running" and the user would think nothing
  // happened.  A 15-second safety timeout clears the state and
  // surfaces a notification if the WS event never lands.
  // pauseWatchdogRef / resumeWatchdogRef are declared above so
  // finalizeWorkflowExecution can clear them too.

  const clearPauseWatchdog = useCallback(() => {
    if (pauseWatchdogRef.current !== null) {
      clearTimeout(pauseWatchdogRef.current);
      pauseWatchdogRef.current = null;
    }
  }, []);

  const clearResumeWatchdog = useCallback(() => {
    if (resumeWatchdogRef.current !== null) {
      clearTimeout(resumeWatchdogRef.current);
      resumeWatchdogRef.current = null;
    }
  }, []);

  const handlePauseWorkflow = useCallback(async () => {
    if (!currentWorkflow?.id || !currentSession || !currentOrg?.orgid) {
      notification.error('Missing workflow or session information');
      return;
    }
    if (!isExecutingWorkflowRef.current || isPausedRef.current) {
      return;
    }
    setIsPausing(true);
    // Take the snapshot BEFORE the network call so we capture the
    // exact state the user saw.  If the request fails we still
    // surface the snapshot so the user can retry; the editor's
    // diff stays correct.
    setPauseSnapshot(takePauseSnapshot(nodes, edges));

    // Arm the watchdog: if lifecycle/paused doesn't arrive within
    // 15s, clear isPausing and surface a helpful message.  A real
    // pause typically completes within 1-3 seconds (WS round trip
    // + activity-cancel propagation); 15s gives generous slack
    // for slow LLM cancellations.
    clearPauseWatchdog();
    pauseWatchdogRef.current = setTimeout(() => {
      pauseWatchdogRef.current = null;
      setIsPausing(false);
      // Only surface the warning if we still aren't paused.  A
      // race where the event arrives at second 14.9 shouldn't
      // trigger the message.
      if (!isPausedRef.current) {
        notification.warning(
          'Pause is taking longer than expected. The workflow may have ' +
            'finished its current activity already, or the temporal worker ' +
            'may be running an older build. Check the worker logs and ' +
            'try again if needed.',
        );
      }
    }, 15000);

    try {
      const response = await pauseWorkflow(
        currentWorkflow.id,
        currentSession,
        currentOrg.orgid,
      );
      if ('detail' in response && typeof response.detail === 'string') {
        throw new Error(response.detail);
      }
      // Already-completed path: BE detected the workflow was dead
      // (worker died, Temporal retention dropped, etc.) and
      // reconciled the PG row to a terminal status.  Tear down the
      // live-run UI quietly -- no error toast, just a "this run is
      // already done" notice -- and let finalizeWorkflowExecution
      // clear the rest of the local state.
      if (response.was_already_completed) {
        clearPauseWatchdog();
        setPauseSnapshot(null);
        setIsPausing(false);
        notification.info(
          'This workflow has already finished. Refreshing run status...',
        );
        finalizeWorkflowExecution();
        return;
      }
    } catch (error) {
      console.error('Failed to pause workflow:', error);
      notification.error(
        error instanceof Error ? error.message : 'Failed to pause workflow',
      );
      // Roll back optimistic state on failure.
      setPauseSnapshot(null);
      setIsPausing(false);
      clearPauseWatchdog();
    }
  }, [
    currentWorkflow?.id,
    currentSession,
    currentOrg?.orgid,
    nodes,
    edges,
    notification,
    clearPauseWatchdog,
    finalizeWorkflowExecution,
  ]);

  // Low-level resume primitive shared between the toolbar Resume
  // button and the per-node "play" button.  Encapsulates the
  // 15-second watchdog and the toast UX so both call-sites stay in
  // lockstep on UX details.
  //
  // Caller-supplied options:
  //   * editedNodeIds  -- the list to send to the BE.  The toolbar
  //     button derives this from ``computeEditedNodeIds`` (a diff
  //     against the pause snapshot).  The play button forces the
  //     clicked node into the list (UNION'd with any edits the user
  //     made during pause) so the BE invalidates from there.
  //   * successToast   -- function so callers can craft context-
  //     aware messages.  Receives the final editedNodeIds for
  //     templating ("Resume requested with N edits..." vs
  //     "Re-running from <Node X>...").
  //   * onRollback     -- optional cleanup for callers that did
  //     extra optimistic state.  Fires on any failure path
  //     (validation, network, BE error).
  //
  // Returns true if the signal was accepted by the BE, false on
  // any failure.  The lifecycle/resumed WS event clears
  // ``isResuming`` and ``isPaused``; this function does not touch
  // them on success.
  const sendResumeSignal = useCallback(
    async (opts: {
      editedNodeIds: string[];
      successToast: (editedNodeIds: string[]) => string;
      onRollback?: () => void;
    }): Promise<boolean> => {
      if (!currentWorkflow?.id || !currentSession || !currentOrg?.orgid) {
        notification.error('Missing workflow or session information');
        return false;
      }
      if (!isPausedRef.current) {
        return false;
      }
      if (stopRequestedRef.current) {
        return false;
      }
      resumeAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      resumeAbortControllerRef.current = abortController;
      setIsResuming(true);
      clearResumeWatchdog();
      resumeWatchdogRef.current = setTimeout(() => {
        resumeWatchdogRef.current = null;
        setIsResuming(false);
        if (isPausedRef.current) {
          notification.warning(
            'Resume is taking longer than expected. Check the temporal ' +
              'worker logs and try again if needed.',
          );
        }
      }, 15000);

      try {
        const hasEdits = opts.editedNodeIds.length > 0;
        // Strip artifact nodes/edges before sending: they are frontend-only
        // display constructs and the backend's Pydantic model requires
        // source_node_id / target_node_id fields that artifact edges lack.
        const { filteredNodes: resumeNodes, filteredEdges: resumeEdges } =
          removeAllArtifactNodes(nodes, edges);
        const response = await resumeWorkflow(
          currentWorkflow.id,
          currentSession,
          currentOrg.orgid,
          hasEdits
            ? {
                payload: {
                  id: currentWorkflow.id,
                  session_id: currentSession,
                  name: currentWorkflow.name,
                  nodes: resumeNodes,
                  edges: resumeEdges,
                  metadata: currentWorkflow.metadata,
                },
                editedNodeIds: opts.editedNodeIds,
              }
            : {},
          abortController.signal,
        );
        // Checking this call's own signal (rather than stopRequestedRef,
        // which can be reset by a concurrent stop or a second resume by
        // the time this promise settles) is what correctly identifies
        // THIS request as superseded, regardless of what superseded it.
        if (abortController.signal.aborted) {
          return false;
        }
        if ('detail' in response && typeof response.detail === 'string') {
          throw new Error(response.detail);
        }
        // Already-completed path -- BE found the workflow dead and
        // reconciled.  Clear pause UI immediately rather than wait
        // for a lifecycle event that will never come.
        if (response.was_already_completed) {
          clearResumeWatchdog();
          setIsResuming(false);
          setIsPaused(false);
          setPauseState(null);
          setPauseSnapshot(null);
          notification.info(
            'This workflow has already finished. Refreshing run status...',
          );
          finalizeWorkflowExecution();
          opts.onRollback?.();
          return false;
        }
        notification.success(opts.successToast(opts.editedNodeIds));
        // Leave isPaused / pauseSnapshot / isResuming to be
        // cleared by the lifecycle/resumed WS handler so the bar
        // shows "Resuming..." until the BE actually transitions.
        resumeAbortControllerRef.current = null;
        return true;
      } catch (error) {
        if (abortController.signal.aborted) {
          return false;
        }
        console.error('Failed to resume workflow:', error);
        notification.error(
          error instanceof Error ? error.message : 'Failed to resume workflow',
        );
        setIsResuming(false);
        clearResumeWatchdog();
        opts.onRollback?.();
        return false;
      }
    },
    [
      currentWorkflow?.id,
      currentWorkflow?.name,
      currentWorkflow?.metadata,
      currentSession,
      currentOrg?.orgid,
      nodes,
      edges,
      notification,
      clearResumeWatchdog,
      finalizeWorkflowExecution,
    ],
  );

  // Resume click: same sticky pattern as pause -- isResuming stays
  // true until the lifecycle/resumed WS event lands or the 15s
  // watchdog clears it.  Now a thin wrapper around sendResumeSignal
  // so the per-node play button can reuse the same plumbing.
  const handleResumeWorkflow = useCallback(async () => {
    const snap = pauseSnapshotRef.current;
    const editedIds = snap ? computeEditedNodeIds(snap, nodes, edges) : [];
    await sendResumeSignal({
      editedNodeIds: editedIds,
      successToast: () => 'Workflow resumed',
    });
  }, [nodes, edges, sendResumeSignal]);

  // True until the workflow data and node catalog are fully loaded.
  // Only blocks on node list loading when the catalog isn't available yet —
  // background refreshes (with cached data) don't trigger the overlay.
  const isBlockingNodeListLoad =
    isLoadingNodeList && Object.keys(groupedNodes).length === 0;
  const isWorkflowFullyLoading =
    isLoadingWorkflow || isLoadingTemplate || isBlockingNodeListLoad;

  const panelGroupRef = useRef<HTMLDivElement>(null);
  const [panelGroupWidth, setPanelGroupWidth] = useState(1400);

  useEffect(() => {
    const element = panelGroupRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPanelGroupWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // WebSocket integration
  const { isConnected, connectionError, connect, subscribeToAll } =
    useWebSocket();

  // Chat WebSocket integration
  const { connect: connectChat, disconnect: disconnectChat } =
    useChatWebSocket();

  // Chat UI state actions
  const {
    setChatConnected,
    setChatConnecting,
    setChatDenied,
    setChatAwaitingInput,
    setChatUnavailable,
    resetChatUI,
  } = useChatUIStore.getState();

  // Track the currently selected node and workflow session for chat
  const currentChatNodeIdRef = useRef<string | null>(null);
  const currentChatSessionRef = useRef<string | null>(null);

  // Cleanup chat WebSocket connection on component unmount
  useEffect(() => {
    return () => {
      if (currentChatNodeIdRef.current) {
        disconnectChat();
        currentChatNodeIdRef.current = null;
        currentChatSessionRef.current = null;
      }
    };
  }, [disconnectChat]);

  // Subscribe to chat WebSocket messages for state updates
  // This must be at the workflow-editor level (always mounted) to catch events
  // even when the chat sidebar is not open
  useEffect(() => {
    const unsubscribeMessages = chatWebsocketService.subscribeToAll(
      (message) => {
        // chat_connected event means the chat session is ready
        if (message.event_type === 'chat_connected') {
          setChatConnected(true);
          setChatAwaitingInput(true);
          return;
        }

        // chat_denied means the agent is still processing
        if (message.event_type === 'chat_denied') {
          setChatDenied(true);
          return;
        }

        // chat_disconnected means the chat session ended
        if (message.event_type === 'chat_disconnected') {
          resetChatUI();
          return;
        }

        // AWAITING_USER_INPUT is delivered on the main workflow WebSocket (see handleNodeOrStatusEvent).
      },
    );

    // Subscribe to connection state changes to reset UI if socket drops
    // while chat panel is closed
    const unsubscribeConnectionState =
      chatWebsocketService.onConnectionStateChange(
        (state: ChatConnectionState) => {
          if (!state.isConnected) {
            resetChatUI();
          }
        },
      );

    return () => {
      unsubscribeMessages();
      unsubscribeConnectionState();
    };
  }, []);

  // Load saved execution statuses when workflow is loaded or cleared
  useEffect(() => {
    if (loadedNodeStatuses) {
      setNodeStatuses(loadedNodeStatuses);
    } else {
      // Clear nodeStatuses when no saved data exists
      setNodeStatuses({});
    }
  }, [loadedNodeStatuses, workflowId]);

  // Handle opening duplicated workflow in new tab
  useEffect(() => {
    if (
      pendingDuplicateWorkflowName &&
      shouldOpenDuplicateInNewTab &&
      savedWorkflows.length > 0
    ) {
      const duplicatedWorkflow = savedWorkflows.find(
        (w) => w.name === pendingDuplicateWorkflowName,
      );

      if (duplicatedWorkflow) {
        notification.success('Workflow duplicated successfully');
        window.open(
          orgPath(`${ROUTES.WORKFLOW.ROOT}/${duplicatedWorkflow.workflow_id}`),
          '_blank',
        );
        setPendingDuplicateWorkflowName(null);
        setShouldOpenDuplicateInNewTab(false);
      }
    }
  }, [
    savedWorkflows,
    pendingDuplicateWorkflowName,
    shouldOpenDuplicateInNewTab,
    notification,
  ]);

  // Workflow panel state
  const {
    openWorkflowPanel,
    hidePreview,
    hideFilesPreview,
    mcpCode,
    apiCode,
    setPublishedCode,
    clearPublishedCode, // MCP, API
    showPreview,
    closeWorkflowPanel,
    openWorkflowCard,
    setActiveTab,
    setConsolePanelOpen,
    setFilteredNodeId,
    setHighlightedField,
    isMaximized: isConsoleSidebarMaximized,
    isConsolePanelOpen,
    setEventsDetailNodeId,
    setMaximized: setConsoleSidebarMaximized,
    setShouldScrollToError,
    openAddNodesPanel,
  } = useWorkflowPanelStore();

  useEffect(() => {
    hidePreview();
    closeWorkflowPanel();
  }, [workflowId, hidePreview, closeWorkflowPanel]);

  // Reset node explorer sidebar and chat state when switching workflows
  useLayoutEffect(() => {
    resetNodeExplorer();
    resetChatUI();
    // Drop any chat-run artifact tracking from the previous workflow so it
    // can't surface cards into the newly opened workflow's chat, and close
    // the artifact viewer panel so it doesn't linger over the new canvas.
    chatRunPendingRef.current = false;
    chatRunIdRef.current = null;
    setChatRunEndedSignal(null);
    setArtifactPreview(null);
  }, [workflowId, resetNodeExplorer, resetChatUI]);

  // Re-validate graph structure whenever nodes or edges change while a graph
  // error is active. This clears multipleWorkflows / cyclicGraph errors as soon
  // as the user fixes the canvas (deletes a node, removes/reconnects an edge).
  // Uses getState() to check for active errors so the effect only depends on
  // graph shape — not on the error state itself — avoiding a redundant re-run
  // after the errors are cleared.
  useEffect(() => {
    const { multipleWorkflows, cyclicGraph } =
      useWorkflowValidationStore.getState();
    if (multipleWorkflows !== null || cyclicGraph !== null) {
      validateGraphStructure(nodes, edges);
    }
  }, [nodes, edges]);

  // Fetch nodes when the workflow editor page mounts or org changes
  useEffect(() => {
    if (currentOrg?.orgid) {
      fetchNodes();
      useEvalCatalogStore.getState().fetch();
    }
    // Load workflows from backend
  }, [fetchNodes, currentOrg?.orgid]);

  // Load workflow by ID or template (template ID from search params)
  useEffect(() => {
    // The pane stays mounted across a workflowId switch (see WorkflowSurface),
    // so a slower load for the previous id can still resolve after this
    // effect has moved on to the next one -- guard every action taken after
    // the await against that, or a late response loads over what the user
    // switched to.
    let cancelled = false;

    const loadWorkflowById = async () => {
      if (!currentOrg?.orgid || !workflowId) {
        return;
      }

      // Load workflow by ID if provided
      if (workflowId && workflowId !== 'new') {
        try {
          const workflow = await getSavedWorkflow(workflowId, currentOrg.orgid);
          if (cancelled) return;
          if (workflow) {
            loadWorkflow(workflow);
            // Validate on load so banners (e.g. empty collection scope) show immediately.
            // Empty File Inputs stay neutral until Run: an unfilled slot is valid saved state.
            setTimeout(() => {
              const nodes = useNodeStore.getState().nodes;
              useWorkflowValidationStore
                .getState()
                .validateFields(nodes, { skipFileInputs: true });
            }, 500);
          }
        } catch (error) {
          if (cancelled) return;
          console.error('Error loading workflow:', error);
          if (error instanceof WorkflowNotFoundError) {
            notification.error(
              "This workflow no longer exists or you don't have access",
            );
          } else {
            Sentry.captureException(error, {
              tags: {
                workflow_action: 'load_workflow',
                workflow_id: workflowId,
              },
            });
            notification.error(
              `Failed to load workflow: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
          }
          navigate(ROUTES.WORKFLOW.ROOT);
        }

        // Load template by ID if provided
      } else if (workflowId === 'new') {
        // Read template from query parameter (persists across page refresh)
        const templateId = searchParams.get('template');
        if (templateId === 'blank') {
          createBlankWorkflow();
        } else if (templateId) {
          loadTemplateWorkflow(templateId, currentOrg.orgid);
        } else {
          // No template specified - create blank workflow
          createBlankWorkflow();
        }
      }
    };

    loadWorkflowById();
    return () => {
      cancelled = true;
    };
  }, [
    workflowId,
    searchParams,
    createBlankWorkflow,
    loadTemplateWorkflow,
    loadWorkflow,
    notification,
    getSavedWorkflow,
    currentOrg?.orgid,
    navigate,
  ]);

  // Detect disabled/unsupported nodes when the set of nodes or catalog changes.
  // Depends on nodeIdsKey (not the nodes array) so drags/selections don't re-trigger.
  // Reads nodes from the store inside the effect to get the current snapshot.
  useEffect(() => {
    if (Object.keys(groupedNodes).length > 0) {
      useWorkflowValidationStore.getState().clearImportErrors();

      const currentNodes = useNodeStore.getState().nodes;
      if (currentNodes.length === 0) {
        return;
      }

      validateImportErrors(currentNodes, groupedNodes);
      useNodeStore.getState().detectNodeUpdates();
    }
  }, [workflowId, nodeIdsKey, groupedNodes]);

  // A blank canvas opens the node catalog once. Re-opening it on every pass
  // of the effect below (it re-runs as the save modal opens and closes) would
  // overrule a user who closed it while naming the workflow.
  const openedNewWorkflowCatalogRef = useRef(false);

  // Auto-open save modal and the node catalog for new workflows when they finish loading
  useEffect(() => {
    const isNew = workflowId === 'new' && currentWorkflow?.id === 'new';
    const isFullyLoaded = !isLoadingWorkflow && !isLoadingTemplate;

    if (isNew && isFullyLoaded) {
      // Only open save modal when we have a valid workflow name (not empty)
      // This ensures loadTemplateWorkflow has completed and set the name
      const hasValidName = Boolean(
        currentWorkflow?.name &&
          currentWorkflow.name !== 'new' &&
          currentWorkflow.name !== '',
      );

      // Open save modal for new workflows
      if (!isSaveWorkflowModalOpen && hasValidName) {
        setSaveWorkflowModalMode('save');
        setIsSaveWorkflowModalOpen(true);
        setIsForceSave(true); // Force save for new workflows - user must save before proceeding
      }
      // Put the node catalog in front of the empty canvas. Which catalog that
      // is depends on the design in play: the right-hand Add Nodes panel now
      // holds the library the left explorer used to, and pinning the explorer
      // open here would show a panel the workflow loses the moment it is
      // saved and the fluid host takes over.
      if (isCanvasA2aChatEnabled) {
        if (!openedNewWorkflowCatalogRef.current) {
          openedNewWorkflowCatalogRef.current = true;
          openAddNodesPanel();
        }
      } else {
        setNodeExplorerMaximized(true);
      }
    }

    if (!isNew && !isFullyLoaded && isSaveWorkflowModalOpen) {
      setIsSaveWorkflowModalOpen(false);
      setIsForceSave(false);
    }
  }, [
    workflowId,
    currentWorkflow?.id,
    currentWorkflow?.name,
    isLoadingWorkflow,
    isLoadingTemplate,
    isSaveWorkflowModalOpen,
    setNodeExplorerMaximized,
    isCanvasA2aChatEnabled,
    openAddNodesPanel,
  ]);

  // Handle navigation after saving a new workflow
  useEffect(() => {
    const handleNavigationAfterSave = async () => {
      if (shouldNavigateAfterSave && currentWorkflow?.id) {
        try {
          const workflowExists = await getSavedWorkflow(
            currentWorkflow.id,
            currentOrg?.orgid || '',
          );
          if (workflowExists) {
            setShouldNavigateAfterSave(false);
            navigate(`${ROUTES.WORKFLOW.ROOT}/${currentWorkflow.id}`);
          }
        } catch (error) {
          console.error('Error checking workflow existence:', error);
          Sentry.captureException(error, {
            tags: {
              workflow_action: 'check_workflow_existence',
              workflow_id: currentWorkflow?.id,
            },
          });
          setShouldNavigateAfterSave(false);
        }
      }
    };

    handleNavigationAfterSave();
  }, [
    shouldNavigateAfterSave,
    currentWorkflow?.id,
    getSavedWorkflow,
    currentOrg?.orgid,
    navigate,
  ]);

  // if "?openConsoleTab=<mcp|api|console>" is present in URL param, open the corresponding tab
  useEffect(() => {
    const openTabParam = searchParams.get(CONSOLE_TAB_PARAM);
    if (
      openTabParam &&
      ['mcp', 'api', 'events', 'configure'].includes(openTabParam) &&
      (mcpCode || apiCode)
    ) {
      openWorkflowPanel(openTabParam as WorkflowPanelTab);

      // Clean up the URL parameter
      searchParams.delete(CONSOLE_TAB_PARAM);
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [
    searchParams,
    mcpCode,
    apiCode,
    navigate,
    location.pathname,
    openWorkflowPanel,
  ]);

  // --- WebSocket event handlers (extracted for readability) ---

  // Shared by handleTerminationEvent / handleExecutionResultEvent: resolves
  // how a terminal event should be finalized when a pause/stop was in
  // flight when it arrived.
  //   - isConfirmedStop: the backend genuinely confirmed the stop (real
  //     "Workflow stopped successfully"), as opposed to a stop that was
  //     requested but raced by the run finishing/failing on its own first
  //     (misleading to call that a successful stop).
  //   - isError: the terminal event is a genuine failure, unrelated to
  //     the stop attempt. Failures must finalize normally (no
  //     markUserStoppedRun) so the real "Run Failed" toast surfaces
  //     instead of being silently swallowed as a user-initiated stop.
  const finalizeRacedTerminalEvent = useCallback(
    ({
      isConfirmedStop,
      isError,
    }: {
      isConfirmedStop: boolean;
      isError: boolean;
    }) => {
      // stopRequestedRef (not isStoppingRef) is the source of truth here:
      // it's set synchronously the instant Stop is clicked, whereas
      // isStoppingRef only updates on the next render after
      // setIsStopping(true) -- a terminal WS event can land mid-await,
      // before that render, and would otherwise read a stale `false`.
      const wasStopping = stopRequestedRef.current;
      const wasPausing = isPausingRef.current;
      const { currentSession: sessionId } = useWorkflowStore.getState();

      if (wasStopping) {
        clearStopWatchdog();
        if (isConfirmedStop) {
          notification.success('Workflow stopped successfully');
          if (sessionId) {
            applyStopSuccessSideEffects(sessionId);
          } else {
            finalizeWorkflowExecution();
          }
          return;
        }
        if (isError) {
          // The user asked to stop; an abrupt cancel commonly surfaces as a
          // terminal error from the in-flight nodes being torn down. Honor the
          // stop intent rather than the incidental error: record a STOPPED
          // termination so the canvas pill reads "Run stopped", and route
          // through the stop side-effects so no "Run Failed" toast fires. The
          // backend persists the run as stopped, so a later reload agrees.
          if (sessionId) {
            queueNodeStatusEvent('workflow_events', {
              event_type: WorkflowEventTypes.TERMINATION,
              event_id: `synth:user-stop:${sessionId}`,
              timestamp: new Date().toISOString(),
              status_code: TerminationStatusCode.STOPPED,
            } as TerminationEvent);
            applyStopSuccessSideEffects(sessionId);
          } else {
            finalizeWorkflowExecution();
          }
          return;
        }
        // Raced by a natural completion before the stop landed.
        if (sessionId) {
          markRunCompletionOverride(
            sessionId,
            'Run completed before it could be stopped',
          );
          applyStopSuccessSideEffects(sessionId);
        } else {
          finalizeWorkflowExecution();
        }
        return;
      }

      if (wasPausing && !isError && sessionId) {
        markRunCompletionOverride(
          sessionId,
          'Run completed before the pause could take effect',
        );
      }
      finalizeWorkflowExecution();
    },
    [
      applyStopSuccessSideEffects,
      clearStopWatchdog,
      finalizeWorkflowExecution,
      notification,
      queueNodeStatusEvent,
    ],
  );

  const handleTerminationEvent = useCallback(
    (event: NodeStatusEvent) => {
      const terminationEvent = event as TerminationEvent;
      finalizeRacedTerminalEvent({
        isConfirmedStop:
          terminationEvent.status_code === TerminationStatusCode.STOPPED,
        isError: terminationEvent.status_code === TerminationStatusCode.ERROR,
      });
      queueNodeStatusEvent('workflow_events', event);
    },
    [finalizeRacedTerminalEvent, queueNodeStatusEvent],
  );

  const handleExecutionResultEvent = useCallback(
    (event: NodeStatusEvent) => {
      const executionResultEvent = event as ExecutionResultEvent;

      if (executionResultEvent.data?.workflow?.nodes) {
        type NodeStructWithResult = NodeStruct & {
          node_result?: Record<string, unknown>;
        };

        // Apply node results first — read from store to avoid stale closure
        const { nodes: storeNodes } = useNodeStore.getState();
        executionResultEvent.data.workflow.nodes.forEach(
          (responseNode: NodeStructWithResult) => {
            const matchingNode = storeNodes.find(
              (storeNode) =>
                storeNode.id === responseNode.react_flow_generated_id,
            );
            if (matchingNode && responseNode.node_result) {
              updateNodeResult(matchingNode.id, responseNode.node_result);
            }
          },
        );

        // Read fresh nodes after result updates, then spawn artifacts
        const { nodes: freshNodes, edges: freshEdges } =
          useNodeStore.getState();
        const { filteredNodes: baseNodes, filteredEdges: baseEdges } =
          removeAllArtifactNodes(freshNodes, freshEdges);

        let artifactNodes: CustomNode[] = [];
        let artifactEdges: CustomEdge[] = [];
        const terminalIds = getTerminalNodeIds(baseNodes, baseEdges);

        executionResultEvent.data.workflow.nodes.forEach(
          (responseNode: NodeStructWithResult) => {
            const matchingNode = baseNodes.find(
              (storeNode) =>
                storeNode.id === responseNode.react_flow_generated_id,
            );
            if (!matchingNode || !responseNode.node_result) return;

            if (terminalIds.has(matchingNode.id)) {
              const { newNodes, newEdges } = spawnArtifactNodesForResult(
                matchingNode,
                responseNode.node_result as NodeResult | undefined,
                baseEdges,
                [...baseNodes, ...artifactNodes],
              );
              artifactNodes = [...artifactNodes, ...newNodes];
              artifactEdges = [...artifactEdges, ...newEdges];
            }
          },
        );

        if (artifactNodes.length > 0) {
          setNodes([...baseNodes, ...artifactNodes]);
          setEdges([...baseEdges, ...artifactEdges]);
        }
      }

      // Finalize AFTER artifact setNodes so isExecutingWorkflowRef is still
      // true when onSelectionChange fires, preventing the console panel
      // from closing.
      const isConfirmedStop = isUserCancellation({
        execution_result: [event],
      });
      finalizeRacedTerminalEvent({
        isConfirmedStop,
        isError:
          executionResultEvent.data?.success === false && !isConfirmedStop,
      });
      queueNodeStatusEvent('execution_result', event);
    },
    [
      finalizeRacedTerminalEvent,
      updateNodeResult,
      queueNodeStatusEvent,
      setNodes,
      setEdges,
    ],
  );

  const handleNodeResultEvent = useCallback(
    (event: NodeStatusEvent) => {
      const nodeResultEvent = event as NodeResultDataEvent;
      const nodeId = nodeResultEvent.node_id;

      const { nodes: storeNodes } = useNodeStore.getState();
      const matchingNode = storeNodes.find((n) => n.id === nodeId);

      if (matchingNode) {
        // Surface a truncation warning only when the *displayed* value itself
        // was clipped (string at the 50 k cap).  `content_truncated` alone is
        // too broad — it fires whenever any field (data, metadata, …) was
        // clipped, which produces false-positive warnings on nodes whose
        // visible content is short.  When value is null the node uploaded the
        // full content to S3 and the preview panel fetches it from there, so
        // no warning is needed.
        const eventData = nodeResultEvent.data;
        const WS_VALUE_CAP = 50_000; // mirrors settings.WS_CONTENT_MAX_CHARS
        const displayedValue = eventData.value;
        const valueAppearsClipped =
          typeof displayedValue === 'string' &&
          displayedValue.length >= WS_VALUE_CAP;
        const resultToStore =
          nodeResultEvent.content_truncated && valueAppearsClipped
            ? { ...eventData, _content_truncated: true }
            : eventData;
        updateNodeResult(matchingNode.id, resultToStore);

        const { nodes: freshNodes, edges: freshEdges } =
          useNodeStore.getState();
        const { filteredNodes: baseNodes, filteredEdges: baseEdges } =
          removeAllArtifactNodes(freshNodes, freshEdges);
        const terminalIds = getTerminalNodeIds(baseNodes, baseEdges);

        if (terminalIds.has(matchingNode.id)) {
          const freshMatchingNode = baseNodes.find(
            (n) => n.id === matchingNode.id,
          );
          if (freshMatchingNode) {
            const existingArtifacts = freshNodes.filter(
              (n) =>
                isArtifactNode(n) &&
                (n.data as unknown as ArtifactNodeData)?.parentNodeId !==
                  matchingNode.id,
            );
            const existingArtifactEdges = freshEdges.filter(
              (e) => isArtifactEdge(e) && e.source !== matchingNode.id,
            );

            const { newNodes, newEdges } = spawnArtifactNodesForResult(
              freshMatchingNode,
              nodeResultEvent.data as NodeResult | undefined,
              baseEdges,
              [...baseNodes, ...existingArtifacts],
            );

            if (newNodes.length > 0) {
              setNodes([...baseNodes, ...existingArtifacts, ...newNodes]);
              setEdges([...baseEdges, ...existingArtifactEdges, ...newEdges]);
            }
          }
        }
      }
      queueNodeStatusEvent(nodeResultEvent.node_id || 'node_result', event);
    },
    [updateNodeResult, queueNodeStatusEvent],
  );

  const handleChatRequestEvent = useCallback(
    (event: NodeEvent, nodeId: string) => {
      const chatRequestData = event.data as {
        request_id: string;
        timeout?: number;
        initial_message?: string;
      };

      if (!chatRequestData.request_id || !currentWorkflow?.id) return;

      setChatAwaitingInput(true);

      const { currentSession } = useWorkflowStore.getState();
      const sessionId = currentSession || '';

      const onTimeout = async () => {
        try {
          await postChatResumeWorkflow(
            currentOrg?.orgid || '',
            nodeId,
            sessionId,
            currentWorkflow.id,
            chatRequestData.request_id,
          );
          console.log(
            'Workflow auto-resumed after timeout for request:',
            chatRequestData.request_id,
          );
        } catch (error) {
          console.error('Failed to auto-resume workflow after timeout:', error);
          Sentry.captureException(error, {
            tags: {
              workflow_action: 'auto_resume_after_timeout',
              workflow_id: currentWorkflow.id,
              node_id: nodeId,
            },
          });
        }
      };

      chatTimerManager.initializeTimer(
        chatRequestData.request_id,
        chatRequestData.timeout || 300,
        onTimeout,
        10,
        true,
      );

      const matchingNode = nodes.find((storeNode) => storeNode.id === nodeId);
      if (matchingNode && matchingNode.data?.struct) {
        const nodeStruct = matchingNode.data.struct as NodeStruct;
        const nodeResult = matchingNode.data.result as
          | Record<string, InputValue>
          | null
          | undefined;

        showPreview({
          result: nodeResult,
          nodeId: nodeStruct.node_id,
          reactFlowGeneratedId: nodeId,
          statusEvents: nodeStatusesRef.current[nodeId] || [],
          isLoading: false,
          icon: nodeStruct.icon,
          displayName: nodeStruct.display_name,
          nodeType: nodeStruct.node_type,
        });

        // Scroll to chat input after preview opens
        setTimeout(() => {
          const previewPanel = document.querySelector(
            '[data-preview-panel="true"]',
          ) as HTMLElement;

          if (previewPanel) {
            const scrollableContainer = previewPanel.querySelector(
              '[data-chat-scroll-container="true"]',
            ) as HTMLElement;

            if (scrollableContainer) {
              const scrollToBottom = () => {
                scrollableContainer.scrollTo({
                  top: scrollableContainer.scrollHeight,
                  behavior: 'smooth',
                });
              };

              scrollToBottom();
              setTimeout(scrollToBottom, 200);
              setTimeout(scrollToBottom, 500);

              setTimeout(() => {
                const chatInput = previewPanel.querySelector(
                  '[data-chat-input="true"] input',
                ) as HTMLInputElement;
                if (chatInput) {
                  chatInput.focus();
                }
              }, 700);
            }
          }
        }, 300);
      }
    },
    [currentWorkflow, currentOrg?.orgid, nodes, showPreview],
  );

  const runButtonTooltip = useMemo(() => {
    if (isWorkflowFullyLoading) return 'Preparing necessary tools. Please wait';
    if (!canRunWorkflowGraph) return 'Add nodes to the workflow to run it';
    if (hasDisabledNodes) return 'Remove disabled nodes to run this workflow';
    if (isPaused) return 'Workflow is paused';
    if (isExecutingWorkflow) return 'Workflow is running';
    return 'Run Workflow';
  }, [
    isWorkflowFullyLoading,
    canRunWorkflowGraph,
    hasDisabledNodes,
    isPaused,
    isExecutingWorkflow,
  ]);

  const handleRun = useCallback(async (): Promise<boolean> => {
    if (!currentOrg?.orgid) {
      notification.error("You don't belong to an organization");
      return false;
    }

    // Validate that the workflow has executable nodes (not just canvas decorations).
    if (!hasExecutableWorkflowNodes(nodes)) {
      notification.error(
        'Cannot run an empty workflow. Please add at least one node.',
      );
      return false;
    }

    // Run all validations against the current graph state before attempting execution.
    useWorkflowValidationStore.getState().validateFields(nodes);
    useWorkflowValidationStore.getState().clearImportErrors();
    validateImportErrors(
      useNodeStore.getState().nodes,
      useNodeStore.getState().groupedNodes,
    );
    validateGraphStructure(nodes, edges);

    const validationStore = useWorkflowValidationStore.getState();
    if (!validationStore.canRunWorkflow()) {
      setActiveTab('errors');
      // Pinning suits only the legacy sidebar; it would dock the floating
      // panel over the canvas buttons.
      if (isCanvasA2aChatEnabled) {
        setConsolePanelOpen(true);
      } else {
        useWorkflowPanelStore.getState().setMaximized(true);
      }

      const {
        unsupportedNodes,
        missingFiles,
        fieldErrors,
        nodeListUnavailable,
        multipleWorkflows,
        cyclicGraph,
      } = validationStore;

      if (
        unsupportedNodes.length > 0 ||
        missingFiles.length > 0 ||
        nodeListUnavailable ||
        multipleWorkflows !== null ||
        cyclicGraph !== null
      ) {
        openWorkflowPanel('errors');
        notification.error(
          'Workflow has validation errors. Please resolve them in the Errors tab before running.',
        );
      } else if (fieldErrors.length > 0) {
        const firstError = fieldErrors[0];
        setNodes(
          nodes.map((n) => ({
            ...n,
            selected: n.id === firstError.nodeId,
            data: {
              ...n.data,
              isSelected: n.id === firstError.nodeId,
            },
          })),
        );
        setLastSelectedNodeId(firstError.nodeId);
        openWorkflowPanel('configure', { nodeId: firstError.nodeId });
        setHighlightedField(firstError.nodeId, firstError.fieldId);
        notification.error(
          'Workflow has validation errors. Please check the highlighted fields.',
        );
      }
      return false;
    }

    if (isExecutingWorkflowRef.current) {
      finalizeWorkflowExecution();
      // This restarts an in-flight run rather than waiting for its natural
      // false transition (which React may batch away entirely if it's
      // immediately followed by the setIsExecutingWorkflow(true) below) —
      // so any previous chat-run tracking must be dropped here. Otherwise
      // the run-ended effect fires on this new run's completion using the
      // interrupted run's stale id.
      chatRunPendingRef.current = false;
      chatRunIdRef.current = null;
    }

    setIsExecutingWorkflow(true);
    stopRequestedRef.current = false;
    droppedResumedDuringStopRef.current = false;
    bootstrapSessionIdRef.current = null;
    const bootstrapGeneration = ++runBootstrapGenerationRef.current;
    isExecutingWorkflowRef.current = true;
    setWorkflowStartedAt(new Date().toISOString());
    setPersistStatusHighlight(false);
    setIsRunNotificationDismissed(false);

    // Switch to Events tab and open console so events stream is visible
    setActiveTab('events');
    setConsolePanelOpen(true);
    // Clear the node filter so all events are visible during execution
    setFilteredNodeId(null);

    // Clear previous node statuses when starting a new run
    setNodeStatuses({});

    // Clear historical data flag and switch to live execution view.
    // Drop the stale ?runId= before we know the new run's id, in case the
    // dispatch below fails or is aborted.
    clearLoadedNodeStatuses();
    setSelectedRunId(null);
    clearUrlState();
    setSelectedRunCreatedAt(null);
    setIsPastRunsPanelOpen(false);

    // reset states for new run
    hidePreview();
    setLoadingNodes(new Set());
    setCancellableNodes(new Set());
    // Fresh run from scratch -- nothing is cached, so wipe any
    // teal "cached" badges that were left over from a prior
    // rerun-from-node.  finalizeWorkflowExecution intentionally
    // preserves cachedNodeIds across run-end so the user can
    // still see which nodes were re-used; the next FRESH run is
    // where that information becomes stale.
    setCachedNodeIds(new Set());
    // No parent run for a fresh start — Evals tab should not
    // fall back to anything.
    setRerunLineage(null);
    const clearedNodes = clearNodeResults(nodes).map((n) => ({
      ...n,
      selected: false,
    }));
    setNodes(clearedNodes);
    setEdges(edges.filter((e) => !isArtifactEdge(e)));
    setLastSelectedNodeId(null);

    // Disconnect any active chat session explicitly before the new run's session starts.
    if (currentChatNodeIdRef.current) {
      disconnectChat();
      currentChatNodeIdRef.current = null;
      currentChatSessionRef.current = null;
    }

    try {
      console.log('[WorkflowEditor] Starting workflow execution:', {
        workflowId: currentWorkflow?.id,
        timestamp: new Date().toISOString(),
      });

      // Get a new session before running the workflow
      const sessionStartTime = performance.now();
      const session = await getRunSession();
      const sessionDuration = performance.now() - sessionStartTime;

      if (await abortRunBootstrapIfRequested(bootstrapGeneration, null)) {
        return false;
      }

      console.log('[WorkflowEditor] Session created:', {
        sessionId: session?.session_id,
        duration: `${sessionDuration.toFixed(2)}ms`,
        hasSocketUrl: !!session?.session_socket_url,
        timestamp: new Date().toISOString(),
      });

      if (!session) {
        throw new Error('Failed to get session');
      }

      bootstrapSessionIdRef.current = session.session_id;
      setCurrentSession(session.session_id);

      if (
        await abortRunBootstrapIfRequested(
          bootstrapGeneration,
          session.session_id,
        )
      ) {
        return false;
      }

      // Update WebSocket URL and connect
      if (session.session_socket_url) {
        console.log('[WorkflowEditor] Connecting WebSocket:', {
          sessionId: session.session_id,
          socketUrl: session.session_socket_url,
          wsCurrentlyConnected: websocketService.isConnected(),
          timestamp: new Date().toISOString(),
        });

        const wsStartTime = performance.now();
        try {
          // Update URL and wait for reconnection if needed
          await websocketService.updateUrl(session.session_socket_url);

          // Connect to WebSocket (this will be a no-op if already connected)
          await connect();

          const wsDuration = performance.now() - wsStartTime;
          console.log('[WorkflowEditor] WebSocket connected successfully:', {
            duration: `${wsDuration.toFixed(2)}ms`,
            isConnected: websocketService.isConnected(),
            timestamp: new Date().toISOString(),
          });

          // Mark workflow as started to prevent premature disconnection
          websocketService.markWorkflowStarted();
        } catch (wsError) {
          const wsDuration = performance.now() - wsStartTime;
          console.error('[WorkflowEditor] WebSocket connection failed:', {
            error: wsError,
            duration: `${wsDuration.toFixed(2)}ms`,
            sessionId: session.session_id,
            timestamp: new Date().toISOString(),
          });
          throw wsError;
        }
      } else {
        console.warn('[WorkflowEditor] No session socket URL provided');
      }

      if (
        await abortRunBootstrapIfRequested(
          bootstrapGeneration,
          session.session_id,
        )
      ) {
        return false;
      }

      // Read at dispatch time, not from a closure: the user may have toggled
      // them since mount. A per-workflow override wins over the personal pref.
      const clarPrefs = effectiveClarificationPrefs(
        useClarificationPrefsStore.getState(),
      );
      const result = await runWorkflow(
        currentWorkflow?.id || '',
        session.session_id,
        currentWorkflow?.name || '',
        nodes,
        edges,
        currentOrg.orgid,
        {
          allowClarifications: modeAllowsClarifications(clarPrefs.mode),
          clarificationTimeoutSecs: clarificationTimeoutForMode(
            clarPrefs.mode,
            clarPrefs.timeoutSecs,
          ),
          // With a workflow default in play, send explicit values (even
          // "off") so the backend honors the pill instead of falling back
          // to the workflow metadata default.
          forceExplicit: clarPrefs.isWorkflowScoped,
        },
        embedded ? linkRunToOperatorChat() : null,
      );

      if (
        await abortRunBootstrapIfRequested(
          bootstrapGeneration,
          session.session_id,
          { runDispatched: true },
        )
      ) {
        // Post-dispatch bail (run finished already, superseded, or user
        // stop): the run did start unless the POST itself was rejected.
        const dispatchRejected = !!(
          result &&
          typeof result === 'object' &&
          'detail' in result
        );
        // Capture (or clear) the pending chat-run flag here too — this path
        // returns before the block below that normally handles it, and a
        // leftover `true` pending flag would misattribute a later
        // Play-button run's id to this chat request.
        if (chatRunPendingRef.current) {
          chatRunPendingRef.current = false;
          if (!dispatchRejected) {
            const runId = result.runId ?? '__no_run_id__';
            const generationMismatch =
              runBootstrapGenerationRef.current !== bootstrapGeneration;
            if (generationMismatch) {
              // A newer run superseded this dispatch; let that run's own
              // lifecycle own the signal instead of misattributing this
              // stale id to it. The chat is told nothing for the same
              // reason: it would follow a run this request did not start.
            } else if (isExecutingWorkflowRef.current) {
              chatDispatchedRunIdRef.current = result.runId ?? null;
              // Same run, still live (e.g. a Stop attempt failed and the
              // backend run continues) — isExecutingWorkflow hasn't
              // transitioned to false yet, so stash the id and let the
              // normal run-ended effect pick it up when it eventually does.
              chatRunIdRef.current = runId;
            } else {
              // Already finalized (e.g. a WS execution_result event beat
              // this continuation, or Stop resolved synchronously above) —
              // the isExecutingWorkflow=false transition already happened
              // and won't happen again, so emit directly instead of
              // stashing into chatRunIdRef for an effect that already ran.
              chatDispatchedRunIdRef.current = result.runId ?? null;
              emitChatRunEndedSignal(runId);
            }
          }
        }
        return !dispatchRejected;
      }

      // Check if result is an error response (e.g. validation failures)
      if (result && typeof result === 'object' && 'detail' in result) {
        console.error('Workflow run failed:', result.detail);

        // Handle disabled nodes error from backend (race condition safety net).
        // Populate the validation store so the banner appears and Run stays disabled.
        const detail = result.detail as
          | string
          | { error?: string; disabled_node_ids?: string[] };
        if (typeof detail === 'object' && detail?.error === 'disabled_nodes') {
          const disabledIds = new Set(detail.disabled_node_ids ?? []);
          if (disabledIds.size > 0) {
            const vs = useWorkflowValidationStore.getState();
            vs.clearImportErrors();
            for (const node of nodes) {
              if (disabledIds.has(node.id) && node.data?.struct) {
                const struct = node.data.struct;
                vs.addUnsupportedNode({
                  id: uuidv4(),
                  nodeId: node.id,
                  nodeName: struct.user_config_name || struct.display_name,
                  nodeType: struct.node_type,
                  nodeSubType: struct.node_sub_type,
                  position: node.position,
                });
              }
            }
          }
          openWorkflowPanel('errors');
          notification.error(
            'Workflow contains disabled nodes. Remove or replace them to continue.',
          );
          finalizeWorkflowExecution();
          return false;
        }

        const validationStore = useWorkflowValidationStore.getState();
        const { fieldErrors, unsupportedNodes, missingFiles } = validationStore;

        // Focus on the first error (prioritize: unsupported > missing files > field errors)
        if (unsupportedNodes.length > 0 || missingFiles.length > 0) {
          // Open errors tab for import-related errors
          openWorkflowPanel('errors');
          notification.error(
            'Workflow has validation errors. Please resolve them in the Errors tab.',
          );
        } else if (fieldErrors.length > 0) {
          const firstError = fieldErrors[0];

          // Select the error node and auto-pan to it
          setNodes(
            nodes.map((n) => ({
              ...n,
              selected: n.id === firstError.nodeId,
              data: {
                ...n.data,
                isSelected: n.id === firstError.nodeId,
              },
            })),
          );

          // Triggers useKeepSelectedNodeVisible to auto-pan the viewport
          setLastSelectedNodeId(firstError.nodeId);

          // Open Configure tab and highlight the error field
          openWorkflowPanel('configure', { nodeId: firstError.nodeId });
          setHighlightedField(firstError.nodeId, firstError.fieldId);

          notification.error(
            'Workflow has validation errors. Please check the highlighted fields.',
          );
        } else if (result.detail && isAccessDeniedError(result.detail)) {
          notification.error(ACCESS_REVOKED_MESSAGE);
          finalizeWorkflowExecution();
          // Embedded, this editor is a pane beside the chat -- see the
          // load-failure case above for why the route navigation is skipped.
          if (!embedded) {
            setTimeout(() => navigate(ROUTES.WORKFLOW.ROOT), 1500);
          }
          return false;
        } else {
          notification.error(
            result.detail || 'Failed to run workflow. Please try again.',
          );
        }

        finalizeWorkflowExecution();
        return false;
      }

      // Point the URL at the new run so a reload/share reconnects to the live
      // run. Not setting selectedRunId here: that would enable the sidebar's
      // "Current workflow" exit, which detaches the UI without stopping the run.
      if (result.runId) {
        setUrlState({ runId: result.runId });
      }

      // Capture runId for artifact polling when chat-initiated
      if (chatRunPendingRef.current) {
        chatRunPendingRef.current = false;
        chatRunIdRef.current = result.runId ?? '__no_run_id__';
        chatDispatchedRunIdRef.current = result.runId ?? null;
      }

      // Note: Workflow results will now be received via WebSocket execution_result events
      // The loading state will be controlled by termination or execution_result events
      console.log(
        'Workflow execution started, waiting for results via WebSocket...',
      );
      return true;
    } catch (error) {
      console.error('Failed to run workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'run_workflow',
          workflow_id: currentWorkflow?.id,
        },
      });
      finalizeWorkflowExecution();
      notification.error('Failed to run workflow. Please try again.');
      return false;
    }
  }, [
    currentOrg?.orgid,
    currentWorkflow?.id,
    nodes,
    edges,
    setCurrentSession,
    connect,
    clearLoadedNodeStatuses,
    notification,
    currentWorkflow?.name,
    setActiveTab,
    setConsolePanelOpen,
    hidePreview,
    setNodes,
    setLoadingNodes,
    setCancellableNodes,
    setLastSelectedNodeId,
    setHighlightedField,
    openWorkflowPanel,
    finalizeWorkflowExecution,
    abortRunBootstrapIfRequested,
    setFilteredNodeId,
    disconnectChat,
    clearUrlState,
    setUrlState,
    emitChatRunEndedSignal,
    isCanvasA2aChatEnabled,
  ]);

  // Chat-requested run: same as the Play button but refuses while a run
  // is in flight (the Play path restarts instead). Resolves to the id of the
  // run it started so the chat can follow it, or null when nothing started.
  const handleRunFromChat = useCallback(async (): Promise<string | null> => {
    if (isExecutingWorkflowRef.current) {
      notification.warning('A run is already in progress.');
      return null;
    }
    // Flag so the run-ended effect knows to trigger artifact polling
    chatRunPendingRef.current = true;
    chatDispatchedRunIdRef.current = null;
    const started = await handleRun();
    if (!started) {
      // Run did not start (validation error etc.) — clear the flag
      chatRunPendingRef.current = false;
      return null;
    }
    return chatDispatchedRunIdRef.current;
  }, [handleRun, notification]);

  // Embedded, the chat rail belongs to the host and sits beside this editor
  // rather than inside it, so it cannot be handed the dispatcher as a prop.
  // Publish it instead, and withdraw it on unmount so a chat outliving the
  // canvas can't run a workflow that is no longer on screen.
  const setCanvasRunBridge = useCanvasRunBridgeStore(
    (state) => state.setBridge,
  );
  // Same identity scheme as the apply-mutations bridge (see
  // canvas-apply-mutations-bridge-store) so a dispatch aimed at one workflow
  // can't start a run on a canvas that has since switched to another.
  const runBridgeWorkflowId =
    currentWorkflow?.id && currentWorkflow.id !== 'new'
      ? currentWorkflow.id
      : `new:${currentWorkflow?.createdAt ?? ''}`;
  useEffect(() => {
    if (!embedded) return;
    setCanvasRunBridge({
      run: handleRunFromChat,
      workflowId: runBridgeWorkflowId,
    });
    return () => setCanvasRunBridge(null);
  }, [embedded, handleRunFromChat, runBridgeWorkflowId, setCanvasRunBridge]);

  /**
   * Start a new workflow run that inherits cached results from the
   * past run currently loaded in the editor.  Triggered by the
   * "Re-run from this node" item on the canvas context menu while
   * viewing a past run.
   *
   * The chosen node and every node downstream of it execute fresh;
   * everything else is seeded from the past run's NodeResult rows
   * and shows up with the teal "Cached" badge.  The new run gets
   * its own session_id, its own WebSocket subscription, and its
   * own row in the runs list, with parent_run_id pointing at the
   * source run for lineage.
   *
   * Mirrors handleRun's session-bootstrap so the WS connection,
   * node-statuses reset, and isExecutingWorkflow flag end up in
   * exactly the same shape as a normal run.
   */
  const handleRerunFromNode = useCallback(
    async (
      /** Starting node(s) for the new run. */
      startingNodeIdOrIds: string | readonly string[],
    ) => {
      if (!currentOrg?.orgid) {
        notification.error("You don't belong to an organization");
        return;
      }
      if (!currentWorkflow?.id) {
        notification.error('Workflow is not loaded');
        return;
      }
      if (!currentSession) {
        notification.error(
          'No source run available — open a past run first to use Re-run from here.',
        );
        return;
      }
      const sourceSessionId = currentSession;
      // Normalise single-id and array forms to a consistent array view.
      // De-dupe defensively in case a caller passes duplicates (cheap
      // and avoids surprising BE behaviour when starting_node_ids has
      // repeats).
      const startingNodeIds = Array.from(
        new Set(
          typeof startingNodeIdOrIds === 'string'
            ? [startingNodeIdOrIds]
            : startingNodeIdOrIds,
        ),
      );
      if (startingNodeIds.length === 0) {
        notification.error('No starting nodes provided for re-run');
        return;
      }
      const startingNodes = startingNodeIds
        .map((id) => nodes.find((n) => n.id === id))
        .filter((n): n is (typeof nodes)[number] => n !== undefined);
      if (startingNodes.length !== startingNodeIds.length) {
        notification.error(
          'Could not find one or more chosen nodes in this workflow',
        );
        return;
      }
      // Single-node-callsite alias preserved purely for the notification
      // text below — `Re-running from <name>` reads better than `Re-running
      // 1 node` for the button path.
      const primaryStartingNode = startingNodes[0];

      // The rerun runs under a new session_id, so Temporal won't reject it and
      // an uncancelled live run would keep burning tokens orphaned. Awaited so
      // the BE acks before the new session is minted -- otherwise the live run
      // can persist one more node-result row that the rerun then seeds from.
      const sourceStillActive =
        !isHistoricalData &&
        (isExecutingWorkflowRef.current || isPausedRef.current);
      if (sourceStillActive) {
        try {
          const stopResp = await stopWorkflow(
            currentWorkflow.id,
            sourceSessionId,
            currentOrg.orgid,
          );
          // apiRequest returns { detail, success: false } on error.
          // Treat ``was_already_completed`` as success — the source
          // workflow had already reached terminal state on Temporal
          // (worker died, retention dropped, etc.); there's nothing
          // to cancel and the rerun can proceed normally.
          if ('detail' in stopResp && typeof stopResp.detail === 'string') {
            notification.error(
              `Could not cancel the in-flight run before re-run: ${stopResp.detail}`,
            );
            return;
          }
          if (stopResp.success === false && !stopResp.was_already_completed) {
            notification.error(
              'Could not cancel the in-flight run before re-run. ' +
                'Wait for the live run to finish, then try again.',
            );
            return;
          }
        } catch (cancelErr) {
          console.error(
            'Failed to cancel live source before rerun:',
            cancelErr,
          );
          Sentry.captureException(cancelErr, {
            tags: {
              workflow_action: 'rerun_cancel_live_source',
              workflow_id: currentWorkflow?.id,
            },
          });
          notification.error(
            'Could not cancel the in-flight run before re-run. ' +
              'Wait for the live run to finish, then try again.',
          );
          return;
        }
      }

      setIsExecutingWorkflow(true);
      stopRequestedRef.current = false;
      droppedResumedDuringStopRef.current = false;
      bootstrapSessionIdRef.current = null;
      const bootstrapGeneration = ++runBootstrapGenerationRef.current;
      isExecutingWorkflowRef.current = true;
      setPersistStatusHighlight(false);
      setIsRunNotificationDismissed(false);
      setActiveTab('events');
      setConsolePanelOpen(true);
      setFilteredNodeId(null);
      hidePreview();
      setLoadingNodes(new Set());
      setCancellableNodes(new Set());

      // Wipe any cached-node badges left over from a prior rerun on
      // this canvas.  Without this, the teal badge from a previous
      // rerun lingers until the new ``lifecycle/rerun_started`` (or
      // ``rerun_seeded``) event arrives -- worst case forever if
      // the BE never gets that far (worker overloaded, etc.) -- and
      // gives the user a misleading "this is cached" signal on
      // nodes that are actually about to recompute.  ``handleRun``
      // does the same thing for fresh runs; this is the matching
      // primitive for reruns.
      setCachedNodeIds(new Set());
      // Lineage is stamped below once we know the new session_id
      // (after getRunSession()).

      // Compute the FRESH set client-side using the same algorithm
      // the BE uses (helper/downstream.ts mirrors
      // temporal/utils/downstream.py).  Fresh = the chosen node +
      // everything downstream of it.  Everything else is "cached"
      // (the BE will load each cached node's prior result via
      // load_cached_results_activity and seed it into _node_results,
      // skipping execution).
      //
      // We use this set to do PARTIAL state resets instead of the
      // wholesale ``setNodeStatuses({}) + clearNodeResults(nodes)``
      // we used to do.  Wiping everything was the cause of two UX
      // bugs the user just hit:
      //
      //   1. Cached nodes' OUTPUTS became inaccessible -- we wiped
      //      ``node.data.result`` for every node, then the BE
      //      never re-emitted node_result events for cached nodes
      //      (it only emits for fresh ones), so the cached panels
      //      stayed empty for the rest of the run.
      //
      //   2. Cached nodes lost their synthesised
      //      EXECUTION_SUCCESSFUL events too, so after the rerun
      //      ended their ``isCompleted`` derivation went false.
      //      Combined with finalizeWorkflowExecution wiping
      //      cachedNodeIds, that left them with NO badge at all.
      //
      // Mirroring the BE semantics here -- "only invalidate what
      // will actually re-run" -- keeps the cached nodes' loaded
      // data and synthesised events intact, so they continue to
      // render with the teal cached badge AND their outputs stay
      // accessible all the way through and after the rerun.
      const freshSet = buildDownstreamSet(startingNodeIds, nodes, edges, true);

      // Clear nodeStatuses ONLY for the fresh set.  Cached nodes
      // retain their synthesised EXECUTION_SUCCESSFUL events
      // (loaded from past-runs-sidebar.loadRun); those drive both
      // ``isCompleted`` and the events panel.
      //
      // ``workflow_events`` and ``execution_result`` are run-scoped (they
      // carry the end-of-run TERMINATION / EXECUTION_RESULT signals), not
      // node-scoped, so they must be cleared too: a stale signal left by a
      // prior stop or completed run would make the events summary treat this
      // rerun as already ended and relabel its in-flight nodes as "Stopped".
      // A fresh ``handleRun`` wipes everything via setNodeStatuses({}); this
      // is the matching clear.
      setNodeStatuses((prev) => {
        const next = { ...prev };
        delete next['workflow_events'];
        delete next['execution_result'];
        for (const id of freshSet) delete next[id];
        return next;
      });

      // Deliberately NOT calling clearLoadedNodeStatuses() here.
      // It would set loadedNodeStatuses=null in the store, which
      // triggers the editor's mount-time effect (search for
      // ``loadedNodeStatuses`` ~line 2069) to re-run and call
      // setNodeStatuses({}), wiping the partial reset we just
      // did.  We just need to exit historical-view mode so the
      // canvas drops the past-run banner / lock; loadedNodeStatuses
      // can stay populated until the user explicitly exits
      // historical view (handleViewCurrent) or loads a different
      // past run (past-runs-sidebar overwrites it).
      setIsHistoricalData(false);

      // Strip artifact nodes/edges only for the fresh set (nodes
      // being re-run). Cached nodes keep their artifact cards.
      const canvasNodes = nodes.filter(
        (n) =>
          !isArtifactNode(n) ||
          !freshSet.has((n.data as unknown as ArtifactNodeData)?.parentNodeId),
      );
      const freshArtifactIds = new Set(
        nodes
          .filter(
            (n) =>
              isArtifactNode(n) &&
              freshSet.has(
                (n.data as unknown as ArtifactNodeData)?.parentNodeId,
              ),
          )
          .map((n) => n.id),
      );
      const canvasEdges = edges.filter(
        (e) =>
          !freshArtifactIds.has(e.target) && !freshArtifactIds.has(e.source),
      );

      // Clear node.data.result ONLY for the fresh set.  Cached
      // nodes keep the result data updateNodeResult populated for
      // them when the past run was loaded -- same data the BE is
      // about to seed into _node_results, so they're not stale,
      // they're just being shown without a re-execution round-trip.
      const clearedNodes = canvasNodes.map((n) => ({
        ...n,
        selected: false,
        data: freshSet.has(n.id)
          ? { ...n.data, result: undefined, nodeStatusEvents: [] }
          : n.data,
      }));
      setNodes(clearedNodes);
      setEdges(canvasEdges);
      setLastSelectedNodeId(null);

      if (currentChatNodeIdRef.current) {
        disconnectChat();
        currentChatNodeIdRef.current = null;
        currentChatSessionRef.current = null;
      }

      const { filteredNodes: payloadNodes, filteredEdges: payloadEdges } =
        removeAllArtifactNodes(nodes, edges);

      try {
        const session = await getRunSession();
        if (!session) {
          throw new Error('Failed to get a new session');
        }
        if (await abortRunBootstrapIfRequested(bootstrapGeneration, null)) {
          return;
        }
        bootstrapSessionIdRef.current = session.session_id;
        setCurrentSession(session.session_id);
        if (
          await abortRunBootstrapIfRequested(
            bootstrapGeneration,
            session.session_id,
          )
        ) {
          return;
        }
        // Stamp the lineage NOW so the Evals tab can fall back to
        // the source run for any eval node cached/skipped in this
        // rerun, without waiting for the WS lifecycle event.
        setRerunLineage({
          session: session.session_id,
          parent: sourceSessionId,
        });

        if (session.session_socket_url) {
          await websocketService.updateUrl(session.session_socket_url);
          await connect();
          websocketService.markWorkflowStarted();
        }

        if (
          await abortRunBootstrapIfRequested(
            bootstrapGeneration,
            session.session_id,
          )
        ) {
          return;
        }

        const rerunClarPrefs = effectiveClarificationPrefs(
          useClarificationPrefsStore.getState(),
        );
        const rerunAllowsClarifications = modeAllowsClarifications(
          rerunClarPrefs.mode,
        );
        const response = await rerunFromNode(
          currentWorkflow.id,
          currentOrg.orgid,
          {
            sourceSessionId,
            startingNodeIds,
            payload: {
              id: currentWorkflow.id,
              session_id: session.session_id,
              name: currentWorkflow.name,
              nodes: payloadNodes,
              edges: payloadEdges,
              metadata: currentWorkflow.metadata,
              ...(rerunAllowsClarifications || rerunClarPrefs.isWorkflowScoped
                ? {
                    allow_clarifications: rerunAllowsClarifications,
                    ...(rerunAllowsClarifications
                      ? {
                          // ``null`` => "ask and wait" (indefinite).
                          clarification_timeout_secs:
                            clarificationTimeoutForMode(
                              rerunClarPrefs.mode,
                              rerunClarPrefs.timeoutSecs,
                            ),
                        }
                      : {}),
                  }
                : {}),
            },
          },
        );

        if (
          await abortRunBootstrapIfRequested(
            bootstrapGeneration,
            session.session_id,
            { runDispatched: true },
          )
        ) {
          return;
        }

        if ('detail' in response && typeof response.detail === 'string') {
          throw new Error(response.detail);
        }

        const startName =
          (primaryStartingNode.data?.struct?.display_name as
            | string
            | undefined) ?? primaryStartingNode.id;
        // Multi-node start shows the count instead of the lead
        // node's name — the lead is somewhat arbitrary and
        // "Re-running from X" misrepresents what's actually
        // happening across multiple branches.
        notification.success(
          startingNodes.length === 1
            ? `Re-running from ${startName}`
            : `Re-running ${startingNodes.length} downstream nodes`,
        );
      } catch (error) {
        console.error('Failed to start rerun-from-node:', error);
        Sentry.captureException(error, {
          tags: {
            workflow_action: 'rerun_from_node',
            workflow_id: currentWorkflow.id,
          },
        });
        finalizeWorkflowExecution();
        notification.error(
          error instanceof Error
            ? error.message
            : 'Failed to start re-run. Please try again.',
        );
      }
    },
    [
      currentOrg?.orgid,
      currentWorkflow?.id,
      currentWorkflow?.name,
      currentWorkflow?.metadata,
      currentSession,
      isHistoricalData,
      nodes,
      edges,
      connect,
      setCurrentSession,
      setIsHistoricalData,
      hidePreview,
      setLoadingNodes,
      setCancellableNodes,
      setNodes,
      setLastSelectedNodeId,
      setActiveTab,
      setConsolePanelOpen,
      setFilteredNodeId,
      setPersistStatusHighlight,
      notification,
      finalizeWorkflowExecution,
      abortRunBootstrapIfRequested,
      disconnectChat,
    ],
  );

  // ── Re-run from here (per-node play button) ──────────────────
  //
  // Single dispatcher for the play button that GenericNode renders
  // on each rerunnable node card.  Resolves to one of two existing
  // primitives based on context:
  //
  //   * isPaused (live workflow paused) -> sendResumeSignal with
  //     editedNodeIds=[clickedNode UNION any editedIds from the
  //     pause snapshot].  The BE's apply_edit_activity invalidates
  //     downstream of that union and the workflow resumes,
  //     re-executing the invalidated set.  Cheap (no new session,
  //     no workflow-start latency, preserves run lineage).
  //
  //   * isHistoricalData (viewing a past run) -> handleRerunFromNode,
  //     which spins up a fresh Temporal workflow seeded from the
  //     past run's cached results.
  //
  // A small confirmation modal interposes the click to surface
  // "Re-run will reset N downstream nodes — continue?" so the user
  // doesn't accidentally re-run an expensive subgraph.  N is
  // computed via buildDownstreamSet against the live canvas; the
  // count INCLUDES the clicked node itself ("reset" set, not
  // "strictly downstream", to match the BE invalidation semantics).
  //
  // Defensive guards:
  //   * Bail if neither paused nor historical -- the play button
  //     shouldn't be visible in any other state, but this is the
  //     server-side check too.
  //   * Bail if a resume / new run is already in flight to avoid
  //     spamming signals.
  const [rerunFromHereTarget, setRerunFromHereTarget] = useState<{
    nodeId: string;
    displayName: string;
    invalidatedCount: number;
  } | null>(null);
  const [isRerunFromHereLoading, setIsRerunFromHereLoading] = useState(false);

  const requestRerunFromHere = useCallback(
    (nodeId: string) => {
      const isPausedNow = isPausedRef.current;
      const isHistoricalNow = isHistoricalData;

      if (!isPausedNow && !isHistoricalNow) {
        // Defensive: shouldn't be reachable from a hidden button,
        // but if a stale click event raced past the prop update we
        // simply no-op rather than misfire a signal.
        return;
      }

      const targetNode = nodes.find((n) => n.id === nodeId);
      if (!targetNode) {
        notification.error('Could not find that node in the current canvas');
        return;
      }

      // Compute the downstream invalidation set against the LIVE
      // canvas.  This is also what the BE will compute (same
      // algorithm, same edge-handle exclusion -- see
      // helper/downstream.ts and temporal/utils/downstream.py).
      // Include the clicked node itself in the count because that
      // node is also part of the invalidation set on the BE.
      const invalidatedSet = buildDownstreamSet([nodeId], nodes, edges, true);
      const invalidatedCount = invalidatedSet.size;

      const displayName =
        (targetNode.data?.struct?.display_name as string | undefined) ?? nodeId;

      setRerunFromHereTarget({
        nodeId,
        displayName,
        invalidatedCount,
      });
    },
    [nodes, edges, isHistoricalData, notification],
  );

  const cancelRerunFromHere = useCallback(() => {
    if (isRerunFromHereLoading) return;
    setRerunFromHereTarget(null);
  }, [isRerunFromHereLoading]);

  // A stale entry from a previous run would route the next run's first message
  // to deliverClarification for a node that no longer exists. Runs before any
  // WS handler for the new session; reload-recovery below re-seeds real ones.
  useEffect(() => {
    setPendingClarifications([]);
    setResolvedClarificationsQueue([]);
    clarificationAutoOpenedRef.current = false;
  }, [currentSession]);

  // On refresh while a workflow is parked the announcing WS event won't fire
  // again, leaving the chat panel empty with no way to answer. Fetches once per
  // session; on failure it proceeds with an empty queue.
  useEffect(() => {
    const orgId = currentOrg?.orgid;
    const workflowId = currentWorkflow?.id;
    if (!orgId || !workflowId || !currentSession) return;
    if (recoveredClarificationsForSessionRef.current === currentSession) {
      return;
    }
    recoveredClarificationsForSessionRef.current = currentSession;
    let cancelled = false;
    void (async () => {
      try {
        const response = await getPendingClarifications(
          workflowId,
          currentSession,
          orgId,
        );
        if (cancelled) return;
        if (
          !response.pending_clarifications ||
          response.pending_clarifications.length === 0
        ) {
          return;
        }
        const nowMs = Date.now();
        // The BE returns ``is_paused`` / ``paused_at_epoch_ms`` as
        // workflow-global scalars; apply to every recovered entry so the
        // frozen-countdown visual renders on first paint instead of
        // flashing the running countdown before the next WS event.
        const isPausedRecovered = response.is_paused === true;
        const pausedAtRecovered =
          typeof response.paused_at_epoch_ms === 'number'
            ? response.paused_at_epoch_ms
            : null;
        setPendingClarifications((prev) => {
          const existingIds = new Set(prev.map((p) => p.nodeId));
          const additions = response.pending_clarifications
            .filter((entry) => !existingIds.has(entry.node_id))
            .map((entry) => ({
              nodeId: entry.node_id,
              displayName: entry.display_name || entry.node_id,
              question: entry.question,
              context: entry.context ?? null,
              // ``null`` => "ask and wait" (indefinite); ``undefined``
              // (older BE) defaults to 60.
              timeoutSecs:
                entry.timeout_secs === null ? null : (entry.timeout_secs ?? 60),
              // FE clock for a unique transcript-card key; the countdown
              // still anchors to the BE's ``deadlineEpochMs``.
              requestedAt: nowMs,
              deadlineEpochMs: entry.deadline_epoch_ms ?? null,
              isPaused: isPausedRecovered,
              pausedAtEpochMs: isPausedRecovered ? pausedAtRecovered : null,
            }));
          if (additions.length === 0) return prev;
          return [...prev, ...additions];
        });
        // Auto-open the chat sidebar so the user immediately sees
        // the rehydrated question(s). Mirrors the WS-driven path.
        if (!clarificationAutoOpenedRef.current) {
          useNodeExplorerStore.getState().openChatTab();
          clarificationAutoOpenedRef.current = true;
        }
      } catch {
        // Sentry capture happens inside the service helper; here we
        // just want to keep the editor functional without the recovery.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.orgid, currentWorkflow?.id, currentSession]);

  // Deliberately does not prune pendingClarifications: the BE only emits
  // CLARIFICATION_RESOLVED once the whole batch resolves, so pruning would
  // orphan that event and strand the card as a permanent pending sticky.
  const handleDeliverClarification = useCallback(
    async (
      nodeId: string,
      answer: string,
      declined = false,
    ): Promise<boolean> => {
      const orgId = currentOrg?.orgid;
      if (!currentWorkflow?.id || !currentSession || !orgId) {
        return false;
      }
      const snapshot = pendingClarificationsRef.current.find(
        (p) => p.nodeId === nodeId,
      );
      if (!snapshot) {
        // No pending entry for this node — likely a stale click after
        // the queue moved on. No-op rather than send to a stale node.
        return false;
      }
      try {
        await deliverClarification(
          currentWorkflow.id,
          currentSession,
          orgId,
          nodeId,
          answer,
          declined,
        );
        return true;
      } catch (error) {
        notification.error(
          `Failed to deliver clarification: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        return false;
      }
    },
    [currentWorkflow?.id, currentSession, currentOrg?.orgid, notification],
  );

  // Stable identity (empty deps) so it doesn't re-fire the sidebar's resolution
  // effect every render.
  const handleConsumeResolvedClarification = useCallback(
    (key: { nodeId: string; requestedAt: number }) => {
      setResolvedClarificationsQueue((prev) =>
        prev.filter(
          (r) =>
            !(r.nodeId === key.nodeId && r.requestedAt === key.requestedAt),
        ),
      );
    },
    [],
  );

  const handlePauseClarificationCountdown =
    useCallback(async (): Promise<void> => {
      const orgId = currentOrg?.orgid;
      if (!currentWorkflow?.id || !currentSession || !orgId) return;
      try {
        await pauseClarificationCountdown(
          currentWorkflow.id,
          currentSession,
          orgId,
        );
      } catch (error) {
        // Soft-error: the BE timer keeps counting; Sentry capture is in
        // the service helper. Low-noise warn so a flaky route doesn't
        // spam notifications on every keystroke.
        console.warn('pause-clarification-countdown failed', error);
      }
    }, [currentWorkflow?.id, currentSession, currentOrg?.orgid]);

  /** Companion to handlePauseClarificationCountdown. */
  const handleResumeClarificationCountdown =
    useCallback(async (): Promise<void> => {
      const orgId = currentOrg?.orgid;
      if (!currentWorkflow?.id || !currentSession || !orgId) return;
      try {
        await resumeClarificationCountdown(
          currentWorkflow.id,
          currentSession,
          orgId,
        );
      } catch (error) {
        console.warn('resume-clarification-countdown failed', error);
      }
    }, [currentWorkflow?.id, currentSession, currentOrg?.orgid]);

  const confirmRerunFromHere = useCallback(async () => {
    if (!rerunFromHereTarget) return;
    if (isRerunFromHereLoading) return;
    setIsRerunFromHereLoading(true);

    const { nodeId } = rerunFromHereTarget;
    try {
      // ORDER MATTERS HERE.  ``isHistoricalData`` is checked FIRST,
      // before ``isPausedRef.current``, on purpose:
      //
      // The two flags can be true SIMULTANEOUSLY -- if a user is
      // mid-pause on a live workflow and then opens a past run via
      // the past-runs sidebar, the editor ends up with
      // ``isPaused=true`` (lingering from the live workflow) AND
      // ``isHistoricalData=true`` (just opened a past run).  In
      // that state, ``currentSession`` has been replaced with the
      // PAST RUN's session_id (see past-runs-sidebar's loadRun);
      // a resume signal would target the past run's already-
      // completed Temporal workflow and crash with
      // "workflow execution already completed".
      //
      // Viewing a past run is the user's explicit "switch context
      // to historical" action -- rerun-from-node (a fresh new
      // Temporal workflow seeded from cached results) is the
      // ONLY correct semantic here, regardless of any pause flag
      // left over from the previous live view.  The paused-resume
      // path is reachable only when we're NOT in historical view.
      if (isHistoricalData) {
        await handleRerunFromNode(nodeId);
      } else if (isPausedRef.current) {
        // Paused context: union the clicked node with any edits the
        // user already made during the pause so the BE invalidates
        // both sets in one apply_edit_activity round-trip.
        const snap = pauseSnapshotRef.current;
        const editedFromSnap = snap
          ? computeEditedNodeIds(snap, nodes, edges)
          : [];
        const editedSet = new Set<string>(editedFromSnap);
        editedSet.add(nodeId);
        await sendResumeSignal({
          editedNodeIds: Array.from(editedSet),
          successToast: () =>
            `Re-running from ${rerunFromHereTarget.displayName}...`,
        });
      }
    } finally {
      setIsRerunFromHereLoading(false);
      setRerunFromHereTarget(null);
    }
  }, [
    rerunFromHereTarget,
    isRerunFromHereLoading,
    isHistoricalData,
    nodes,
    edges,
    sendResumeSignal,
    handleRerunFromNode,
  ]);

  /**
   * Captures a screenshot and saves the workflow.
   * Falls back to saving without screenshot if capture fails.
   */
  const saveWorkflowWithScreenshot = useCallback(
    async (
      workflowId: string,
      workflowName: string,
      description?: string,
      isTemplate: boolean = false,
      templateMetadata?: {
        icon?: string;
        color?: string;
        bgColor?: string;
      },
    ) => {
      if (!currentOrg?.orgid) {
        throw new Error("You don't belong to an organization");
      }

      // TODO: Re-enable when backend supports screenshot
      // let screenshot: string | undefined;
      // try {
      //   const reactFlowElement = document.querySelector(
      //     '.react-flow',
      //   ) as HTMLElement;
      //   if (reactFlowElement && getNodesBoundsRef.current) {
      //     screenshot = await captureWorkflowScreenshot(
      //       reactFlowElement,
      //       nodes,
      //       getNodesBoundsRef.current,
      //     );
      //   }
      // } catch (error) {
      //   console.error('Failed to capture screenshot:', error);
      //   // Continue without screenshot
      // }

      // The store, not this render's nodes/edges: a save can run straight
      // after a canvas change, before the re-render that would refresh them.
      const { nodes: currentNodes, edges: currentEdges } =
        useNodeStore.getState();
      const { filteredNodes: saveNodes, filteredEdges: saveEdges } =
        removeAllArtifactNodes(currentNodes, currentEdges);

      await saveWorkflow({
        orgId: currentOrg.orgid,
        workflowId,
        name: workflowName,
        nodes: saveNodes,
        edges: saveEdges,
        description,
        // screenshot, // TODO: Re-enable when backend supports screenshot
        isTemplate,
        templateMetadata,
      });
    },
    [currentOrg, saveWorkflow],
  );

  // A workflow is "new" only until it has been persisted with a real id.
  // currentWorkflow.id (set to the real uuid by saveWorkflow after a create)
  // is the source of truth, not the URL param `workflowId`, which can remain
  // 'new' until the post-create navigation completes.
  const isNewWorkflow = useMemo(
    () => !currentWorkflow?.id || currentWorkflow.id === 'new',
    [currentWorkflow?.id],
  );

  // Per-workflow clarification default (Edit Workflow Attributes →
  // "Clarifying questions"). When the loaded workflow declares one, seed a
  // SESSION-scoped override so the footer pill and run dispatch start from
  // the workflow's default; edits to the pill then stay scoped to this
  // workflow and never touch the persisted personal preference. Workflows
  // without a default keep the existing localStorage behaviour exactly.
  const workflowClarificationDefault = useMemo(() => {
    const raw = currentWorkflow?.metadata?.default_clarification_mode;
    return isClarificationMode(raw) ? raw : null;
  }, [currentWorkflow?.metadata?.default_clarification_mode]);
  const workflowClarificationTimeout =
    currentWorkflow?.metadata?.default_clarification_timeout_secs;

  useEffect(() => {
    const prefsStore = useClarificationPrefsStore.getState();
    if (
      currentWorkflow?.id &&
      currentWorkflow.id !== 'new' &&
      workflowClarificationDefault
    ) {
      prefsStore.initWorkflowSession(
        currentWorkflow.id,
        workflowClarificationDefault,
        typeof workflowClarificationTimeout === 'number'
          ? workflowClarificationTimeout
          : null,
      );
    } else {
      prefsStore.clearWorkflowSession();
    }
  }, [
    currentWorkflow?.id,
    workflowClarificationDefault,
    workflowClarificationTimeout,
  ]);

  // Leaving the editor drops the session override so another workflow's
  // canvas (or the global surfaces) can't inherit it.
  useEffect(
    () => () => {
      useClarificationPrefsStore.getState().clearWorkflowSession();
    },
    [],
  );

  // TEMPORARILY DISABLED: Auto-save handler (for existing workflows only)
  // TODO: Re-enable auto-save feature later
  // const handleAutoSave = useCallback(async () => {
  //   // Don't auto-save new workflows
  //   if (isNewWorkflow) {
  //     return;
  //   }

  //   // Use the existing save function but without notifications for auto-save
  //   const isEditingTemplate =
  //     workflowId !== 'new' && location.state?.isEditingTemplate;

  //   try {
  //     if (isEditingTemplate) {
  //       await saveWorkflowWithScreenshot(
  //         currentWorkflow?.id as string,
  //         currentWorkflow?.name as string,
  //         currentWorkflow?.description as string,
  //         true,
  //         {
  //           icon: currentWorkflow?.icon || '',
  //           color: currentWorkflow?.color || '',
  //           bgColor: currentWorkflow?.bg_color || '',
  //         },
  //       );
  //     } else {
  //       await saveWorkflowWithScreenshot(
  //         currentWorkflow?.id as string,
  //         currentWorkflow?.name as string,
  //         currentWorkflow?.description as string,
  //       );
  //     }
  //   } catch (error) {
  //     console.error('Auto-save failed:', error);
  //     throw error;
  //   }
  // }, [
  //   isNewWorkflow,
  //   workflowId,
  //   currentWorkflow,
  //   location.state,
  //   saveWorkflowWithScreenshot,
  // ]);

  /**
   * Handles saving the current workflow.
   * Opens modal for new workflows or workflows without a name.
   * Directly saves existing workflows with names.
   */
  // Adopt the just-saved canvas as the current version. No reload: it
  // already holds exactly what we saved. Mirrors handleViewCurrent's exit
  // cleanup (incl. finalizeWorkflowExecution, which clears any stale
  // pause/execution flags left by a prior live run) minus the refetch.
  const adoptSavedRunCanvas = useCallback(() => {
    restoreCurrentVersion();
    setSelectedRunId(null);
    setSelectedRunSessionId(null);
    setSelectedRunCreatedAt(null);
    clearUrlState();
    setNodeStatuses({});
    finalizeWorkflowExecution();
  }, [restoreCurrentVersion, clearUrlState, finalizeWorkflowExecution]);

  const saveCurrentWorkflow = useCallback(async () => {
    if (!currentOrg) {
      notification.error("You don't belong to an organization");
      return;
    }

    const isEditingTemplate =
      workflowId !== 'new' && location.state?.isEditingTemplate;

    // Open modal for new workflows or workflows without a name
    if (isNewWorkflow) {
      setSaveWorkflowModalMode('save');
      setIsSaveWorkflowModalOpen(true);
      return;
    }

    // Save existing workflow or template directly
    setIsSaveLoading(true);
    try {
      if (isEditingTemplate) {
        // Save existing template
        await saveWorkflowWithScreenshot(
          currentWorkflow?.id as string,
          currentWorkflow?.name as string,
          currentWorkflow?.description as string,
          true, // isTemplate
          {
            icon: currentWorkflow?.icon || '',
            color: currentWorkflow?.color || '',
            bgColor: currentWorkflow?.bg_color || '',
          },
        );
        notification.success('Template was updated successfully');
      } else {
        // Save existing workflow
        await saveWorkflowWithScreenshot(
          currentWorkflow?.id as string,
          currentWorkflow?.name as string,
          currentWorkflow?.description as string,
        );
        notification.success('Workflow was updated successfully');
      }
      // Reset baseline so hasUnsavedChanges becomes false after successful save
      resetBaselineRef.current?.();

      if (isHistoricalData) adoptSavedRunCanvas();
    } catch (error) {
      console.error('Failed to save:', error);
      const errorMessage = error instanceof Error ? error.message : undefined;

      if (errorMessage && isAccessDeniedError(errorMessage)) {
        notification.error(ACCESS_REVOKED_MESSAGE);
        // Embedded, this editor is a pane beside the chat -- see the
        // load-failure case above for why the route navigation is skipped.
        if (!embedded) {
          setTimeout(() => navigate(ROUTES.WORKFLOW.ROOT), 1500);
        }
        return;
      }

      Sentry.captureException(error, {
        tags: {
          workflow_action: 'save_current_workflow',
          workflow_id: currentWorkflow?.id,
          is_template: isEditingTemplate,
        },
      });
      notification.error(
        errorMessage ||
          (isEditingTemplate
            ? 'Failed to save template. Please try again.'
            : 'Failed to save workflow. Please try again.'),
      );
    } finally {
      setIsSaveLoading(false);
    }
  }, [
    currentOrg,
    notification,
    workflowId,
    currentWorkflow,
    isNewWorkflow,
    isHistoricalData,
    location.state?.isEditingTemplate,
    saveWorkflowWithScreenshot,
    setIsSaveLoading,
    setSaveWorkflowModalMode,
    setIsSaveWorkflowModalOpen,
    adoptSavedRunCanvas,
  ]);

  // Chat applies (and their undo/redo) persist on their own so "Apply" means
  // the workflow is updated.
  const saveChatAppliedChanges = useCallback(
    async ({ summary, tone }: ChatApplySaveReport) => {
      const notify = (message: string) =>
        tone === 'success'
          ? notification.success(message)
          : notification.warning(message);

      if (!currentOrg?.orgid) {
        notify(summary);
        return;
      }
      if (isNewWorkflow) {
        notify(`${summary}. Name the workflow to save it.`);
        setSaveWorkflowModalMode('save');
        setIsSaveWorkflowModalOpen(true);
        return;
      }
      const isEditingTemplate =
        workflowId !== 'new' && location.state?.isEditingTemplate;
      const savedContentKey = workflowContentKey(useNodeStore.getState());
      try {
        if (isEditingTemplate) {
          await saveWorkflowWithScreenshot(
            currentWorkflow?.id as string,
            currentWorkflow?.name as string,
            currentWorkflow?.description as string,
            true,
            {
              icon: currentWorkflow?.icon || '',
              color: currentWorkflow?.color || '',
              bgColor: currentWorkflow?.bg_color || '',
            },
          );
        } else {
          await saveWorkflowWithScreenshot(
            currentWorkflow?.id as string,
            currentWorkflow?.name as string,
            currentWorkflow?.description as string,
          );
        }
        // An edit made while the save was in flight isn't in what was sent;
        // clearing the dirty flag then would lose it on refresh unwarned.
        if (workflowContentKey(useNodeStore.getState()) === savedContentKey) {
          resetBaselineRef.current?.();
        }
        // Same as Save while viewing a run: what's on screen (the run's
        // graph plus the chat changes) becomes the current version.
        if (isHistoricalData) adoptSavedRunCanvas();
        notify(`${summary} and saved`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : undefined;
        if (errorMessage && isAccessDeniedError(errorMessage)) {
          notification.error(ACCESS_REVOKED_MESSAGE);
          return;
        }
        Sentry.captureException(error, {
          tags: {
            workflow_action: 'save_chat_applied_changes',
            workflow_id: currentWorkflow?.id,
            is_template: !!isEditingTemplate,
          },
        });
        notification.error(
          `${summary}, but the workflow couldn't be saved. Save to try again.`,
        );
      }
    },
    [
      currentOrg,
      currentWorkflow,
      isNewWorkflow,
      isHistoricalData,
      workflowId,
      location.state?.isEditingTemplate,
      notification,
      saveWorkflowWithScreenshot,
      setSaveWorkflowModalMode,
      setIsSaveWorkflowModalOpen,
      adoptSavedRunCanvas,
    ],
  );

  const setChatApplySaveHandler = useChatApplyStore(
    (state) => state.setSaveHandler,
  );
  const clearChatApplySaveHandler = useChatApplyStore(
    (state) => state.clearSaveHandler,
  );
  const chatApplyWorkflowKey = isNewWorkflow
    ? `new:${currentWorkflow?.createdAt ?? ''}`
    : (currentWorkflow?.id as string);
  useEffect(() => {
    setChatApplySaveHandler(saveChatAppliedChanges, chatApplyWorkflowKey);
    return () => clearChatApplySaveHandler(saveChatAppliedChanges);
  }, [
    saveChatAppliedChanges,
    chatApplyWorkflowKey,
    setChatApplySaveHandler,
    clearChatApplySaveHandler,
  ]);

  // Handle forced save modal for new workflows
  const handleForceSaveModal = useCallback(() => {
    setSaveWorkflowModalMode('save');
    setIsForceSave(true); // Force save - no cancel button
    setIsSaveWorkflowModalOpen(true);
  }, []);

  // Calculate if current user is the owner of the workflow
  const isWorkflowOwner = useMemo(() => {
    // If workflow has no creator, user is considered owner (new workflow)
    if (!currentWorkflow?.created_by) {
      return true;
    }

    // Check if user's ID or email matches the workflow creator
    if (!user?.id) {
      return false;
    }

    return (
      currentWorkflow.created_by === user.id ||
      currentWorkflow.created_by_email?.toLowerCase() ===
        user.email?.toLowerCase()
    );
  }, [
    currentWorkflow?.created_by,
    currentWorkflow?.created_by_email,
    user?.id,
    user?.email,
  ]);

  const isWorkflowLocked = isWorkflowLockingEnabled
    ? (currentWorkflow?.is_locked ?? false)
    : false;

  const handleToggleWorkflowLock = useCallback(async () => {
    if (!currentOrg?.orgid || !workflowId || workflowId === 'new') return;

    const newLockedState = !isWorkflowLocked;

    if (newLockedState && nodes.length === 0) {
      notification.warning('Cannot lock an empty workflow');
      return;
    }

    if (!newLockedState && !isWorkflowAdmin) {
      notification.warning('Only workflow admins can unlock workflows');
      return;
    }

    const prevWorkflow = currentWorkflow;
    if (currentWorkflow) {
      setCurrentWorkflow({
        ...currentWorkflow,
        is_locked: newLockedState,
      });
    }

    try {
      const response = await lockWorkflow(
        currentOrg.orgid,
        workflowId,
        newLockedState,
      );
      const latestWorkflow = useWorkflowStore.getState().currentWorkflow;
      if (latestWorkflow) {
        setCurrentWorkflow({
          ...latestWorkflow,
          is_locked: response.is_locked,
          locked_by_id: response.locked_by?.id,
          locked_by_name: response.locked_by?.name,
          locked_at: response.locked_at,
        });
      }
      notification.success(
        newLockedState ? 'Workflow locked' : 'Workflow unlocked',
      );
    } catch (error) {
      if (prevWorkflow) {
        setCurrentWorkflow(prevWorkflow);
      }
      notification.error(
        newLockedState
          ? 'Failed to lock workflow'
          : 'Failed to unlock workflow',
      );
      Sentry.captureException(error, {
        extra: { context: 'toggle-workflow-lock', workflowId },
      });
    }
  }, [
    currentOrg?.orgid,
    workflowId,
    isWorkflowLocked,
    isWorkflowAdmin,
    isHistoricalData,
    currentWorkflow,
    setCurrentWorkflow,
    notification,
    nodes.length,
  ]);

  // TEMPORARILY DISABLED: Auto-save hook
  // TODO: Re-enable auto-save feature later
  // Dummy function for useAutoSave hook (not used while auto-save is disabled)
  const dummyAutoSave = useCallback(async () => {
    // No-op while auto-save is disabled
  }, []);

  // TEMPORARILY DISABLED: Auto-save hook (kept for future re-enablement)
  // TODO: Re-enable auto-save feature later
  const { hasUnsavedChanges, resetBaseline } = useAutoSave({
    nodes,
    edges,
    workflowId,
    workflowName: currentWorkflow?.name,
    workflowDescription: currentWorkflow?.description,
    isNewWorkflow,
    isLoadingWorkflow,
    isLoadingTemplate,
    isOwner: true, // TEMPORARILY DISABLED: isWorkflowOwner - allow all users to save
    onSave: dummyAutoSave, // TEMPORARILY DISABLED: handleAutoSave - using dummy function
    onForceSaveModal: handleForceSaveModal,
    debounceDelay: 3000,
    enabled: false, // TEMPORARILY DISABLED: auto-save
  });

  // Store resetBaseline in ref so saveCurrentWorkflow can access it
  resetBaselineRef.current = resetBaseline;

  // Unsaved changes warning (navigation blocking and browser close warning)
  const { isBlocked, proceed, reset } = useUnsavedChangesWarning({
    hasUnsavedChanges,
    // Embedded, "leaving the page" means navigating away from the host (e.g.
    // switching conversations) — blocking that would trap the user in chat
    // over edits made in a side pane. The host opts back in via
    // unsavedChangesWarningEnabled when this workflow is its own anchor
    // surface rather than a layered peek at another workflow.
    enabled: !embedded || unsavedChangesWarningEnabled,
    message: 'Please save your workflow before leaving this page.',
  });

  // Handle save and leave when user confirms in the unsaved changes modal
  const handleSaveAndLeave = useCallback(async () => {
    try {
      await saveCurrentWorkflow();
      // After successful save, proceed with navigation
      proceed();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'save_and_leave',
          workflow_id: currentWorkflow?.id,
        },
      });
      // Don't proceed if save failed
    }
  }, [saveCurrentWorkflow, proceed]);

  // Handle keyboard shortcut for save (Cmd/Ctrl+S)
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 's',
        handler: async () => {
          // Manual save shortcut
          await saveCurrentWorkflow();
        },
      },
    ],
    shouldIgnore: shouldIgnoreKeyboardEvent,
  });

  const handlePublishWorkflow = async (
    options: PublishOptions,
    workflowName: string,
  ) => {
    const workflowNameToPublish =
      workflowName || currentWorkflow?.name || 'Untitled Workflow';
    // Show loader modal and reset progress
    setIsPublishingLoaderOpen(true);
    setPublishingProgress(0);
    // Reset published state while publishing
    clearPublishedCode();

    // Update workflow name if provided (for new workflows)
    if (workflowNameToPublish && isNewWorkflow) {
      setCurrentWorkflow({
        ...currentWorkflow!,
        name: workflowName,
      });
    }

    try {
      // Start progress animation
      setPublishingProgress(10);

      // Simulate progress during API call
      const progressInterval = setInterval(() => {
        setPublishingProgress((prev) => {
          if (prev < 80) {
            return prev + Math.random() * 15 + 5; // Increment by 5-20%
          }
          return prev; // Stop at 80% and wait for API completion
        });
      }, 200);

      if (options.mcpTool || options.api) {
        // Use the updated workflow name if provided, otherwise fall back to current
        const workflowNameToPublish =
          workflowName || currentWorkflow?.name || 'Untitled Workflow';

        // Call the publish API
        if (!currentWorkflow || !currentOrg?.orgid) {
          throw new Error('Missing workflow data or organization ID');
        }

        // Get current session from store or generate a unique one
        const { currentSession } = useWorkflowStore.getState();
        const sessionId = currentSession || uuidv4();

        // Construct complete workflow data for publishing
        const workflowDataToPublish = {
          id: currentWorkflow.id,
          session_id: sessionId,
          name: workflowNameToPublish,
          publish_to_template: currentWorkflow.publish_to_template || false,
          publish_to_mcp: options.mcpTool,
          publish_to_api: options.api,
          software_repo_url: currentWorkflow.software_repo_url ?? null,
          software_repo_branch: currentWorkflow.software_repo_branch ?? null,
          software_repo_mode: currentWorkflow.software_repo_mode ?? null,
          nodes: nodes,
          edges: edges,
          description: currentWorkflow.description || '',
          createdAt: currentWorkflow.createdAt,
          // Use existing styling fields from current workflow
          icon: currentWorkflow.icon,
          color: currentWorkflow.color,
          bg_color: currentWorkflow.bg_color,
        };

        const response = await publishWorkflow(
          currentOrg.orgid,
          workflowDataToPublish,
        );

        // Clear the progress interval
        clearInterval(progressInterval);

        if (response.status === 'success') {
          // Complete the progress
          setPublishingProgress(100);
          // A real MCP gateway endpoint (vs the legacy placeholder) means
          // mcp_code is the actual connection snippet — the MCP tab then
          // renders the endpoint + snippet instead of the sample code.
          const realMcpEndpoint =
            response.base_url && response.base_url !== 'https://api.example.com'
              ? response.base_url
              : undefined;
          setPublishedCode(
            response.mcp_code,
            response.api_code,
            realMcpEndpoint,
          );
          // Show success message as toast
          notification.success('Workflow published successfully!');
          if (options.mcpTool && response.mcp_error) {
            notification.info(response.mcp_error);
          }
        } else {
          // Show error message as toast
          notification.error(response.message || 'Publishing failed');
          setIsPublishingLoaderOpen(false);
          setPublishingProgress(0);
          return; // Don't throw error to avoid duplicate notifications
        }
      }
    } catch (error) {
      console.error('Failed to publish workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'publish_workflow',
          workflow_id: currentWorkflow?.id,
        },
      });
      // Only show generic error if it's not from our response handling
      notification.error('Failed to publish workflow. Please try again.');
      setIsPublishingLoaderOpen(false);
      setPublishingProgress(0);
    }
  };

  const handleCancelPublishing = () => {
    setIsPublishingLoaderOpen(false);
    setPublishingProgress(0);
    notification.info('Publishing cancelled');
  };

  const handlePublishingComplete = () => {
    setPublishingProgress(0); // Reset progress after completion
  };

  /**
   * Handles renaming an existing workflow.
   */
  const handleEditWorkflowInfo = async (name: string, description?: string) => {
    if (!currentOrg || !currentWorkflow?.id) {
      throw new Error(
        "You don't belong to an organization or no workflow selected",
      );
    }

    await saveWorkflowWithScreenshot(
      currentWorkflow.id,
      name,
      description || currentWorkflow.description,
    );
  };

  /**
   * Handles creating a copy of the current workflow and saving it as a template.
   */
  const handleSaveAsTemplate = async (
    name: string,
    description?: string,
    icon?: string,
    color?: string,
    bgColor?: string,
  ) => {
    if (!currentOrg) {
      throw new Error("You don't belong to an organization");
    }

    await saveWorkflowWithScreenshot(
      uuidv4(), // New UUID for template
      name,
      description,
      true, // isTemplate
      {
        icon: icon || '',
        color: color || '',
        bgColor: bgColor || '',
      },
    );
  };

  /**
   * Unified handler for save workflow modal that routes to appropriate save handler
   * based on the current mode (editInfo, template, duplicate, save).
   */
  const handleSaveWorkflowModalSave = async (
    name: string,
    description?: string,
    icon?: string,
    color?: string,
    bgColor?: string,
  ) => {
    try {
      switch (saveWorkflowModalMode) {
        case 'editInfo':
          await handleEditWorkflowInfo(name, description);
          // Reset baseline so hasUnsavedChanges reflects the new saved state
          resetBaselineRef.current?.();
          notification.success('Workflow updated successfully');
          break;
        case 'template':
          await handleSaveAsTemplate(name, description, icon, color, bgColor);
          notification.success('Template saved successfully');
          break;
        case 'duplicate':
        case 'clone':
          await handleDuplicateWorkflow(name, description);
          break;
        case 'save':
        default:
          await saveWorkflowWithScreenshot(uuidv4(), name, description);
          setShouldNavigateAfterSave(true);
          notification.success('Workflow created successfully');
          break;
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'save_workflow_modal',
          workflow_id: currentWorkflow?.id,
          save_action: saveWorkflowModalMode,
        },
      });
      // Individual handlers throw errors, show generic error here
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to save workflow. Please try again.';
      notification.error(errorMessage);
      throw error; // Re-throw to prevent success notification in modal
    }
  };

  /**
   * Handles duplicating a workflow with custom name and description.
   */
  const handleDuplicateWorkflow = async (
    duplicatedName: string,
    duplicatedDescription?: string,
  ) => {
    if (!currentWorkflow?.id || !currentOrg?.orgid) {
      notification.error('Cannot duplicate workflow');
      throw new Error('Cannot duplicate workflow');
    }

    try {
      // Use the duplicate workflow service - no need to save first
      const newWorkflowId = await duplicateWorkflow(
        currentWorkflow.id,
        currentOrg.orgid,
        duplicatedName,
        duplicatedDescription || '',
      );

      if (newWorkflowId) {
        // Open the new workflow in a new tab immediately
        notification.success('Workflow duplicated successfully');
        window.open(
          orgPath(`${ROUTES.WORKFLOW.ROOT}/${newWorkflowId}`),
          '_blank',
        );
      } else {
        // Fallback to the old method if no workflow ID returned
        setPendingDuplicateWorkflowName(duplicatedName);
        setShouldOpenDuplicateInNewTab(true);
      }
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'duplicate_workflow',
          workflow_id: currentWorkflow?.id,
        },
      });
      notification.error('Failed to duplicate workflow. Please try again.');
      throw error; // Re-throw to prevent success notification in modal
    }
  };

  // Handle delete workflow
  const handleDeleteWorkflow = async () => {
    if (!currentWorkflow?.id || !currentOrg?.orgid) {
      notification.error('Cannot delete workflow');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteWorkflow(currentWorkflow.id, currentOrg.orgid);
      notification.success('Workflow deleted successfully');
      // Embedded, the host retires the pane instead of the route -- see the
      // load-failure case above. Without a handler there is nothing to retire
      // it, so leave rather than sit on a workflow that no longer exists.
      if (embedded && onDeleted) {
        onDeleted();
      } else {
        navigate(ROUTES.WORKFLOW.ROOT);
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'delete_workflow',
          workflow_id: currentWorkflow?.id,
        },
      });
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to delete workflow. Please try again.';
      notification.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Handle export workflow (nodes and edges as JSON file)
  const handleExportWorkflow = useCallback(() => {
    try {
      const { nodes: latestNodes, edges: latestEdges } =
        useNodeStore.getState();
      const exportData = {
        ...currentWorkflow,
        publish_to_template: false,
        nodes: scrubNodesForExport(latestNodes),
        edges: latestEdges.filter((e) => !isArtifactEdge(e)),
        exportedAt: new Date().toISOString(),
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], {
        type: 'application/json;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = sanitizeFilename(currentWorkflow?.name || 'untitled');
      link.download = `workflow-export-${safeName}-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notification.success('Workflow exported successfully');
    } catch (error) {
      console.error('Error exporting workflow:', error);
      Sentry.captureException(error, {
        tags: {
          workflow_action: 'export_workflow',
          workflow_id: currentWorkflow?.id,
        },
      });
      notification.error('Failed to export workflow. Please try again.');
    }
  }, [currentWorkflow, notification]);

  const handleExportPrompts = useCallback(() => {
    try {
      const { nodes: latestNodes } = useNodeStore.getState();
      const promptFieldIds = new Set([
        'system_prompt',
        'user_prompt',
        'task',
        'request',
        'deep_research_query',
        'output_spec_description',
        'markdown_content',
      ]);

      const rows: string[][] = [
        [
          'Node Name',
          'Node Type',
          'System Prompt',
          'Task / User Prompt',
          'Deep Research Query',
          'Output Spec / Description',
          'Markdown Content',
          'Request',
        ],
      ];

      for (const node of latestNodes) {
        const struct = node.data?.struct;
        if (!struct) continue;

        const params = struct.params || [];
        const hasPrompts = params.some(
          (p: { field_id?: string; value?: unknown }) =>
            promptFieldIds.has(p.field_id || '') &&
            typeof p.value === 'string' &&
            p.value.trim().length > 0,
        );
        if (!hasPrompts) continue;

        const nodeName =
          struct.user_config_name ||
          struct.display_name ||
          struct.node_id ||
          '';
        const nodeType = struct.display_name || struct.node_id || '';

        const values: Record<string, string> = {};
        for (const p of params) {
          const fid = p.field_id || '';
          if (promptFieldIds.has(fid) && typeof p.value === 'string') {
            values[fid] = p.value;
          }
        }

        rows.push([
          nodeName,
          nodeType,
          values['system_prompt'] || '',
          values['user_prompt'] || values['task'] || '',
          values['deep_research_query'] || '',
          values['output_spec_description'] || '',
          values['markdown_content'] || '',
          values['request'] || '',
        ]);
      }

      if (rows.length <= 1) {
        notification.warning(
          'No agent or output nodes with prompts found in this workflow.',
        );
        return;
      }

      const escapeCsv = (val: string) => {
        let safe = val;
        if (/^[=+\-@\t\r]/.test(safe)) {
          safe = "'" + safe;
        }
        if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
          return '"' + safe.replace(/"/g, '""') + '"';
        }
        return safe;
      };

      const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
      const bom = '\uFEFF';
      const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = sanitizeFilename(currentWorkflow?.name || 'workflow');
      link.download = `${safeName}-prompts.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notification.success(`Exported prompts for ${rows.length - 1} nodes`);
    } catch (error) {
      console.error('Error exporting prompts:', error);
      Sentry.captureException(error, {
        extra: { context: 'export-prompts', workflowId: currentWorkflow?.id },
      });
      notification.error('Failed to export prompts.');
    }
  }, [currentWorkflow, notification]);

  // Handle import workflow (nodes and edges from JSON file)
  const handleImportWorkflow = useCallback(() => {
    // Create a hidden file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.style.display = 'none';

    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        // Clean up if no file selected
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
        return;
      }

      try {
        const fileContent = await file.text();
        const importData = JSON.parse(fileContent);

        // Validate the imported data structure
        if (
          !importData ||
          !Array.isArray(importData.nodes) ||
          !Array.isArray(importData.edges)
        ) {
          notification.error(
            'Invalid file format. Expected JSON with nodes and edges arrays.',
          );
          return;
        }

        const importedNodes: CustomNode[] = importData.nodes;
        const importedEdges: CustomEdge[] = importData.edges;

        if (importedNodes.length === 0 && importedEdges.length === 0) {
          notification.warning('The imported file contains no nodes or edges.');
          return;
        }

        // Assign fresh IDs, clean stale connections, and re-build from edges
        const { nodes: newNodes, edges: newEdges } = remapNodesAndEdges(
          importedNodes,
          importedEdges,
        );

        // Add imported nodes and edges to existing ones (not overwrite)
        setNodes([...nodes, ...newNodes]);
        setEdges([...edges, ...newEdges]);

        const { groupedNodes } = useNodeStore.getState();
        const { clearAllErrors } = useWorkflowValidationStore.getState();
        const { setActiveTab, setMaximized, setConsolePanelOpen } =
          useWorkflowPanelStore.getState();

        // Clear all previous errors then re-detect from the imported nodes
        clearAllErrors();
        validateImportErrors(newNodes, groupedNodes);

        // Run required-field validation so Missing Field Value errors show immediately after import
        useWorkflowValidationStore.getState().validateFields(newNodes);

        // If there are validation errors, open the errors tab
        const hasErrors = useWorkflowValidationStore.getState().hasErrors();
        if (hasErrors) {
          setActiveTab('errors');
          if (isCanvasA2aChatEnabled) {
            setConsolePanelOpen(true);
          } else {
            setMaximized(true);
          }
        }

        // Save snapshot after importing
        saveSnapshot();

        // Pan and zoom to fit imported nodes in viewport
        if (fitViewRef.current && newNodes.length > 0) {
          setTimeout(() => {
            fitViewRef.current?.({ padding: 0.1, duration: 500 });
          }, 100);
        }

        notification.success(
          `Imported ${newNodes.length} node(s) and ${newEdges.length} edge(s) successfully`,
        );
      } catch (error) {
        console.error('Error importing workflow:', error);
        Sentry.captureException(error, {
          tags: {
            workflow_action: 'import_workflow',
            workflow_id: currentWorkflow?.id,
          },
        });
        notification.error(
          'Failed to import workflow. Please check the file format and try again.',
        );
      } finally {
        // Clean up the input element
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      }
    };

    // Add input to DOM and trigger click
    document.body.appendChild(input);
    input.click();
  }, [
    nodes,
    edges,
    setNodes,
    setEdges,
    saveSnapshot,
    notification,
    isCanvasA2aChatEnabled,
  ]);

  // Connect to chat for a specific node
  const connectChatForNode = useCallback(
    (nodeId: string) => {
      const effectiveWorkflowId =
        workflowId !== 'new' ? workflowId : currentWorkflow?.id;

      if (!effectiveWorkflowId || effectiveWorkflowId === 'new') {
        return;
      }

      // The operator chat answers a paused node itself -- as the host's rail
      // when embedded, as the explorer's chat tab when it is the one rendered
      // there. Connecting here would only take the shared socket from it.
      if (embedded || isCanvasA2aChatEnabled) {
        return;
      }

      // Non–node-click callers (e.g. WS events) should not connect after the run ends.
      if (!isExecutingWorkflowRef.current) {
        return;
      }

      // Get the current session from the workflow store
      const { currentSession } = useWorkflowStore.getState();

      // Reuse only when already connected to this node in the current run. Switching nodes
      // must call createChatSession — outbound chat messages carry no node_id, so the
      // WebSocket session stays bound to whichever node created it.
      if (
        currentSession &&
        chatWebsocketService.isConnected() &&
        currentChatSessionRef.current === currentSession &&
        currentChatNodeIdRef.current === nodeId
      ) {
        return;
      }

      // Disconnect from previous chat session if any
      if (currentChatNodeIdRef.current) {
        disconnectChat();
      }

      // Update the current node and session refs
      currentChatNodeIdRef.current = nodeId;

      if (!currentSession) {
        console.warn('No current session available - run the workflow first');
        setChatUnavailable('workflow_not_running');
        return;
      }

      currentChatSessionRef.current = currentSession;

      // Set connecting state (this also clears unavailable/denied state)
      setChatConnecting(true);

      // Create new chat session for the selected node
      createChatSession({
        sessionId: currentSession,
        workflowId: effectiveWorkflowId,
        nodeId: nodeId,
      })
        .then((chatSession) => {
          // Check if another node was selected while waiting for the response
          if (currentChatNodeIdRef.current !== nodeId) {
            console.log(
              'Node selection changed during chat session creation, ignoring response',
            );
            return;
          }

          if (chatSession?.chat_socket_url) {
            connectChat(chatSession.chat_socket_url)
              .then(() => {
                // Check again after WebSocket connection completes
                if (currentChatNodeIdRef.current !== nodeId) {
                  console.log(
                    'Node selection changed during WebSocket connection, disconnecting',
                  );
                  disconnectChat();
                  return;
                }
                console.log('Chat WebSocket connected successfully');
              })
              .catch((error) => {
                console.error('Failed to connect to chat WebSocket:', error);
                // Only clear connecting state if this node is still selected
                if (currentChatNodeIdRef.current === nodeId) {
                  setChatConnecting(false);
                }
              });
          } else {
            setChatConnecting(false);
          }
        })
        .catch((error) => {
          console.error('Failed to create chat session:', error);
          // Only clear connecting state if this node is still selected
          if (currentChatNodeIdRef.current === nodeId) {
            setChatConnecting(false);
          }
        });
    },
    [
      workflowId,
      currentWorkflow?.id,
      embedded,
      isCanvasA2aChatEnabled,
      connectChat,
      disconnectChat,
    ],
  );

  // The pause as the chat rail sees it: who is waiting, on which session,
  // and a way to reopen what the node produced in the preview panel.
  const buildCanvasPausedNode = useCallback(
    (pause: {
      workflowId: string;
      sessionId: string;
      nodeId: string;
      reviewContent?: string;
      announcedAt?: number;
    }) => {
      const { nodes: currentNodes } = useNodeStore.getState();
      const struct = currentNodes.find((n) => n.id === pause.nodeId)?.data
        ?.struct as NodeStruct | undefined;
      return {
        ...pause,
        displayName: struct?.user_config_name || struct?.display_name,
        openPreview: struct
          ? () =>
              showPreview({
                result: (useNodeStore
                  .getState()
                  .nodes.find((n) => n.id === pause.nodeId)?.data?.result ??
                  null) as Record<string, InputValue> | null,
                nodeId: struct.node_id,
                reactFlowGeneratedId: pause.nodeId,
                statusEvents: nodeStatusesRef.current[pause.nodeId] || [],
                isLoading: false,
                icon: struct.icon,
                displayName: struct.display_name,
                nodeType: struct.node_type,
              })
          : undefined,
      };
    },
    [showPreview],
  );

  // Triggered from main ws not chat socket events - sets the chat awaiting input state and opens the chat tab
  const handleUserInputUpdateEvent = useCallback(
    (nodeId: string, event?: NodeEvent) => {
      const nodeExplorerState = useNodeExplorerStore.getState();
      const { currentSession } = useWorkflowStore.getState();
      const { nodes: currentNodes } = useNodeStore.getState();

      setChatAwaitingInput(true);

      const pausedWorkflowId =
        workflowId !== 'new' ? workflowId : currentWorkflow?.id;
      if (currentSession && pausedWorkflowId && pausedWorkflowId !== 'new') {
        const reviewContent = pauseReviewContent(event?.data);
        setCanvasPausedNode(
          buildCanvasPausedNode({
            workflowId: pausedWorkflowId,
            sessionId: currentSession,
            nodeId,
            reviewContent,
            announcedAt: Date.now(),
          }),
        );
      }

      // Avoid redundant openChatTab state writes on duplicate events.
      if (
        !embedded &&
        !(
          nodeExplorerState.isOpen &&
          nodeExplorerState.activeTab === 'chat' &&
          nodeExplorerState.isMaximized
        )
      ) {
        nodeExplorerState.openChatTab();
      }

      const isSameChatTarget =
        currentChatNodeIdRef.current === nodeId &&
        currentChatSessionRef.current === currentSession;
      if (!(isSameChatTarget && chatWebsocketService.isConnected())) {
        connectChatForNode(nodeId);
      }

      const isAlreadySelected = currentNodes.some(
        (node) => node.id === nodeId && node.selected,
      );
      if (!isAlreadySelected) {
        const updatedNodes = currentNodes.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }));
        useNodeStore.getState().setNodes(updatedNodes);
      }

      // The output under review opens on its own, embedded or not: seeing
      // what the node produced is the review. The chat's composer can reopen
      // it if the user closes it.
      const matchingNode = currentNodes.find((n) => n.id === nodeId);
      if (matchingNode && matchingNode.data?.struct) {
        const nodeStruct = matchingNode.data.struct as NodeStruct;
        const nodeResult = matchingNode.data.result as
          | Record<string, InputValue>
          | null
          | undefined;

        showPreview({
          result: nodeResult,
          nodeId: nodeStruct.node_id,
          reactFlowGeneratedId: nodeId,
          statusEvents: nodeStatusesRef.current[nodeId] || [],
          isLoading: false,
          icon: nodeStruct.icon,
          displayName: nodeStruct.display_name,
          nodeType: nodeStruct.node_type,
        });
      }
    },
    [
      embedded,
      workflowId,
      currentWorkflow?.id,
      connectChatForNode,
      showPreview,
      setCanvasPausedNode,
      buildCanvasPausedNode,
    ],
  );

  const handleNodeOrStatusEvent = useCallback(
    (event: NodeStatusEvent) => {
      const nodeId =
        event.event_type === WorkflowEventTypes.STATUS_UPDATE
          ? (event as StatusUpdateEvent).node_id
          : (event as NodeEvent).node_id;

      // ZZZ: remove after demo
      if (
        event.event_type === WorkflowEventTypes.STATUS_UPDATE &&
        (event as StatusUpdateEvent).message === 'Workflow stopped by user'
      ) {
        finalizeWorkflowExecution();
      }

      if (
        isNodeEvent(event) &&
        event.event_type === NodeEventType.AWAITING_USER_INPUT &&
        event.display_type === NodeDataType.CHAT_REQUEST &&
        event.data &&
        typeof event.data === 'object' &&
        'request_id' in event.data &&
        nodeId
      ) {
        handleChatRequestEvent(event, nodeId);
      }

      if (
        isNodeEvent(event) &&
        nodeId &&
        event.event_type === NodeEventType.AWAITING_USER_INPUT &&
        event.display_type === NodeDataType.UPDATE
      ) {
        handleUserInputUpdateEvent(nodeId, event);
      }

      // The node finished or failed, so it is no longer waiting on anyone.
      // Receiving input is not the end of the review: the node regenerates
      // and asks again, and the card must stay up through that.
      if (
        isNodeEvent(event) &&
        nodeId &&
        (event.event_type === NodeEventType.EXECUTION_SUCCESSFUL ||
          event.event_type === NodeEventType.EXECUTION_FAILED ||
          event.event_type === NodeEventType.END)
      ) {
        clearCanvasPausedNode(nodeId);
      }

      if (!nodeId) {
        queueNodeStatusEvent('workflow_events', event);
      } else {
        queueNodeStatusEvent(nodeId, event);
      }
    },
    [
      finalizeWorkflowExecution,
      handleChatRequestEvent,
      handleUserInputUpdateEvent,
      queueNodeStatusEvent,
      clearCanvasPausedNode,
    ],
  );

  // Lifecycle event handler.  These are non-terminal -- the WS
  // channel must stay open afterwards (handled in redis_manager.py
  // on the backend) -- so we just update local state and post a
  // toast when relevant.
  //
  // Diagnostic logging is on by default: LIFECYCLE events are rare
  // (a handful per run) so the noise is acceptable, and being able
  // to grep them out of a user's console during a bug report is
  // worth a lot.  Look for ``[WorkflowEditor:Lifecycle]`` in
  // dev-tools console.
  const handleLifecycleEvent = useCallback(
    (event: LifecycleEvent) => {
      console.log('[WorkflowEditor:Lifecycle]', event.phase, {
        message: event.message,
        data: event.data,
        timestamp: event.timestamp,
      });
      switch (event.phase) {
        case LifecyclePhase.PAUSED: {
          setIsPaused(true);
          setIsPausing(false);
          // Cancel the pause watchdog -- the WS confirmed it; no
          // need for the safety timeout to fire.
          clearPauseWatchdog();
          const snap = (event.data?.pause_state ??
            null) as PauseStateSnapshot | null;
          setPauseState(snap);
          notification.info('Workflow paused');
          break;
        }
        case LifecyclePhase.RESUMED: {
          if (stopRequestedRef.current) {
            // The backend actually resumed while our stop was in
            // flight. If the stop fails, replay this transition --
            // see handleStopWorkflow's failure paths.
            droppedResumedDuringStopRef.current = true;
            break;
          }
          setIsPaused(false);
          setIsResuming(false);
          clearResumeWatchdog();
          setPauseState(null);
          // Drop the snapshot now that the resume succeeded.  The
          // user's edits (if any) have been applied; further edits
          // are part of the ongoing run.
          setPauseSnapshot(null);
          break;
        }
        case LifecyclePhase.EDIT_APPLIED: {
          const invalidatedNodeIds = Array.isArray(
            event.data?.invalidated_node_ids,
          )
            ? (event.data.invalidated_node_ids as string[])
            : [];

          if (invalidatedNodeIds.length > 0) {
            const invalidatedSet = new Set(invalidatedNodeIds);
            setNodeStatuses((prev) => {
              const next = { ...prev };
              for (const id of invalidatedSet) delete next[id];
              return next;
            });
            const currentNodes = useNodeStore.getState().nodes;
            const clearedNodes = currentNodes.map((n) =>
              invalidatedSet.has(n.id)
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      result: undefined,
                      nodeStatusEvents: [],
                    },
                  }
                : n,
            );
            useNodeStore.getState().setNodes(clearedNodes);
          }

          const deletedCount =
            typeof event.data?.deleted_result_count === 'number'
              ? event.data.deleted_result_count
              : 0;
          if (deletedCount > 0) {
            notification.success(
              `Edit applied — ${deletedCount} node result${deletedCount === 1 ? '' : 's'} will recompute`,
            );
          }
          break;
        }
        case LifecyclePhase.RERUN_STARTED: {
          // Early companion to RERUN_SEEDED.  Fired the instant the
          // worker picks up the rerun and computes the cached-vs-
          // fresh split, before any per-node DB writes happen.
          // Lets the canvas paint the teal cached badges within
          // ~1s of the user clicking "Re-run from here", instead
          // of waiting for the full seed loop.
          //
          // We deliberately don't toast here -- the corresponding
          // RERUN_SEEDED event a moment later will toast with the
          // final count.  Two toasts for the same rerun would just
          // be noise.
          const startedCachedIdsRaw = event.data?.cached_node_ids;
          const startedCachedIds = Array.isArray(startedCachedIdsRaw)
            ? (startedCachedIdsRaw as string[])
            : [];
          if (startedCachedIds.length > 0) {
            setCachedNodeIds(new Set(startedCachedIds));
          }
          const startedSourceSession = event.data?.source_session_id;
          if (
            typeof startedSourceSession === 'string' &&
            startedSourceSession &&
            currentSession
          ) {
            setRerunLineage({
              session: currentSession,
              parent: startedSourceSession,
            });
          }
          break;
        }
        case LifecyclePhase.RERUN_SEEDED: {
          const cachedIdsRaw = event.data?.cached_node_ids;
          const cachedIds = Array.isArray(cachedIdsRaw)
            ? (cachedIdsRaw as string[])
            : [];
          if (cachedIds.length > 0) {
            setCachedNodeIds(new Set(cachedIds));
            notification.success(
              `Reusing ${cachedIds.length} cached node result${cachedIds.length === 1 ? '' : 's'}`,
            );
          }
          const seededSourceSession = event.data?.source_session_id;
          if (
            typeof seededSourceSession === 'string' &&
            seededSourceSession &&
            currentSession
          ) {
            setRerunLineage({
              session: currentSession,
              parent: seededSourceSession,
            });
          }
          break;
        }
        case LifecyclePhase.CLARIFICATION_REQUESTED: {
          // Append, not replace: parallel asks at the same level would
          // otherwise clobber each other.
          const askerNodeId =
            typeof event.data?.asker_node_id === 'string'
              ? (event.data.asker_node_id as string)
              : '';
          const displayName =
            typeof event.data?.display_name === 'string'
              ? (event.data.display_name as string)
              : askerNodeId;
          const question =
            typeof event.data?.question === 'string'
              ? (event.data.question as string)
              : '';
          const ctx =
            typeof event.data?.context === 'string'
              ? (event.data.context as string)
              : null;
          // ``null`` is the "ask and wait" sentinel (indefinite — no
          // countdown). ``undefined`` (missing) only from an older BE, which
          // never sent indefinite, so default to 60. A number is a bounded
          // timeout.
          const timeoutSecs =
            event.data?.timeout_secs === null
              ? null
              : typeof event.data?.timeout_secs === 'number'
                ? (event.data.timeout_secs as number)
                : 60;
          // BE-authoritative deadline (epoch ms); older BE without the
          // field falls back to ``requestedAt + timeoutSecs`` at render.
          const deadlineEpochMs =
            typeof event.data?.deadline_epoch_ms === 'number'
              ? (event.data.deadline_epoch_ms as number)
              : null;

          if (!askerNodeId || !question) {
            // Malformed payload: without a node id the answer can't be
            // routed back, so skip rather than open an unanswerable card.
            console.warn('CLARIFICATION_REQUESTED missing fields', event.data);
            break;
          }

          setPendingClarifications((prev) => {
            // Idempotency: don't double-append on duplicate WS delivery
            // (reconnect). Keyed on nodeId — the BE tracks one pending
            // per node too.
            if (prev.some((p) => p.nodeId === askerNodeId)) return prev;
            return [
              ...prev,
              {
                nodeId: askerNodeId,
                displayName,
                question,
                context: ctx,
                timeoutSecs,
                requestedAt: Date.now(),
                deadlineEpochMs,
                // New clarifications arrive un-paused; the sidebar's
                // effect flips these on typing or hover.
                isPaused: false,
                pausedAtEpochMs: null,
              },
            ];
          });

          // Auto-open the chat sidebar so the user sees the question +
          // inline answer input. Only once per pending-queue session
          // (reset when the queue empties on RESOLVED).
          if (!clarificationAutoOpenedRef.current) {
            useNodeExplorerStore.getState().openChatTab();
            clarificationAutoOpenedRef.current = true;
          }
          // Union the new asker with all currently-pending askers so a
          // second parallel clarification doesn't deselect the rest.
          const allPendingAskerIds = new Set<string>([
            ...pendingClarificationsRef.current.map((p) => p.nodeId),
            askerNodeId,
          ]);
          const { nodes: currentNodes } = useNodeStore.getState();
          const updatedNodes = currentNodes.map((n) => ({
            ...n,
            selected: allPendingAskerIds.has(n.id),
          }));
          useNodeStore.getState().setNodes(updatedNodes);

          notification.info(
            `${displayName} is asking: ${question.length > 80 ? `${question.slice(0, 80)}…` : question}`,
          );
          break;
        }
        case LifecyclePhase.CLARIFICATION_RESOLVED: {
          // The asker was answered or timed out. Remove ONLY that
          // node's entry; other parallel pendings stay answerable.
          const resolvedNodeId =
            typeof event.data?.asker_node_id === 'string'
              ? (event.data.asker_node_id as string)
              : '';
          const timedOut =
            typeof event.data?.timed_out === 'boolean'
              ? (event.data.timed_out as boolean)
              : false;
          // Shares timed_out's re-dispatch semantics but renders distinctly.
          const skipped =
            typeof event.data?.skipped === 'boolean'
              ? (event.data.skipped as boolean)
              : false;
          // The run hit its abandonment ceiling unanswered and was
          // auto-stopped: terminal, with no best-guess re-dispatch.
          const abandoned =
            typeof event.data?.abandoned === 'boolean'
              ? (event.data.abandoned as boolean)
              : false;
          // Snapshot the resolution (keyed on the entry's ``requestedAt``
          // so a node asking twice resolves each card independently) for
          // the sidebar's footer flip. Read the queue from the ref so
          // rapid successive resolutions see live state, not a stale
          // closure.
          const currentQueue = pendingClarificationsRef.current;
          const resolved = resolvedNodeId
            ? currentQueue.find((p) => p.nodeId === resolvedNodeId)
            : undefined;
          if (resolved) {
            // Deduped by (nodeId, requestedAt) to tolerate a duplicate WS
            // frame on reconnect.
            setResolvedClarificationsQueue((prev) => {
              const isDup = prev.some(
                (r) =>
                  r.nodeId === resolved.nodeId &&
                  r.requestedAt === resolved.requestedAt,
              );
              if (isDup) return prev;
              return [
                ...prev,
                {
                  requestedAt: resolved.requestedAt,
                  nodeId: resolved.nodeId,
                  timedOut,
                  skipped,
                  abandoned,
                  resolvedAt: Date.now(),
                },
              ];
            });
          }
          setPendingClarifications((prev) =>
            resolvedNodeId
              ? prev.filter((p) => p.nodeId !== resolvedNodeId)
              : prev,
          );
          // Reset the auto-open guard only when the queue empties after
          // removal; with pendings left the user is still mid-conversation.
          const remainingAfterRemoval = resolvedNodeId
            ? currentQueue.filter((p) => p.nodeId !== resolvedNodeId)
            : currentQueue;
          if (remainingAfterRemoval.length === 0) {
            clarificationAutoOpenedRef.current = false;
          }
          // Only toast on a genuine timeout — a skip is user-initiated.
          if (timedOut && !skipped) {
            notification.warning(
              'Clarification timed out — workflow is proceeding with best guess',
            );
          }
          break;
        }
        case LifecyclePhase.CLARIFICATION_COUNTDOWN_PAUSED: {
          // Pause is workflow-global, so every pending card freezes together
          // and asker_node_id is diagnostic, not a routing key. Visual sync
          // only -- the sidebar's typing detection is what triggers a pause.
          const pausedAtEpochMs =
            typeof event.data?.paused_at_epoch_ms === 'number'
              ? (event.data.paused_at_epoch_ms as number)
              : Date.now();
          setPendingClarifications((prev) =>
            prev.map((p) => ({ ...p, isPaused: true, pausedAtEpochMs })),
          );
          break;
        }
        case LifecyclePhase.CLARIFICATION_COUNTDOWN_RESUMED: {
          // Every card re-anchors to the deadline shifted by the pause
          // duration. forced=true means the BE's 60s safety cap fired before
          // the FE resumed, usually a closed tab.
          const newDeadlineEpochMs =
            typeof event.data?.deadline_epoch_ms === 'number'
              ? (event.data.deadline_epoch_ms as number)
              : null;
          const forced =
            typeof event.data?.forced === 'boolean'
              ? (event.data.forced as boolean)
              : false;
          setPendingClarifications((prev) =>
            prev.map((p) => ({
              ...p,
              isPaused: false,
              pausedAtEpochMs: null,
              // Only overwrite when the BE shipped a new deadline; older
              // builds keep the existing value.
              deadlineEpochMs: newDeadlineEpochMs ?? p.deadlineEpochMs,
            })),
          );
          if (forced) {
            notification.info(
              'Clarification countdown auto-resumed (paused too long); answer soon or it will time out',
            );
          }
          break;
        }
        default:
          // Unknown phase -- still queue so it shows up in the
          // events panel / dev tools, but don't surface a toast.
          break;
      }
      // Skip rerun_started / rerun_seeded entries from the events
      // panel.  They're operational events, NOT user-meaningful
      // audit log entries: the cached-node teal badges and the
      // success toast already tell the user what happened.  Also
      // suppresses the "System: Event" noise the user saw while
      // load_cached_results_activity was retry-failing on the
      // file-UUID bug -- when that activity retries, each attempt
      // re-emits RERUN_STARTED, which read as random ping-like
      // entries in the events panel.  Pause / resume / edit_applied
      // remain queued because those are explicit user actions that
      // belong in the audit timeline.
      const isRerunPhase =
        event.phase === LifecyclePhase.RERUN_STARTED ||
        event.phase === LifecyclePhase.RERUN_SEEDED;
      if (!isRerunPhase) {
        queueNodeStatusEvent('workflow_events', event);
      }
    },
    [
      notification,
      queueNodeStatusEvent,
      clearPauseWatchdog,
      clearResumeWatchdog,
      currentSession,
    ],
  );

  // Subscribe to all WebSocket events for node status updates
  useEffect(() => {
    if (isConnected) {
      const unsubscribe = subscribeToAll((event: NodeStatusEvent) => {
        switch (event.event_type) {
          case WorkflowEventTypes.TERMINATION:
            return handleTerminationEvent(event);
          case WorkflowEventTypes.EXECUTION_RESULT:
            return handleExecutionResultEvent(event);
          case WorkflowEventTypes.NODE_RESULT:
            return handleNodeResultEvent(event);
          case WorkflowEventTypes.LIVE_TRANSCRIPT:
            return queueNodeStatusEvent(
              (event as LiveTranscriptEvent).node_id,
              event,
            );
          case WorkflowEventTypes.LIFECYCLE:
            return handleLifecycleEvent(event as LifecycleEvent);
          default:
            if (
              event.event_type === WorkflowEventTypes.STATUS_UPDATE ||
              isNodeEvent(event)
            ) {
              return handleNodeOrStatusEvent(event);
            }
            queueNodeStatusEvent('workflow_events', event);
        }
      });

      return unsubscribe;
    }
  }, [
    isConnected,
    subscribeToAll,
    handleTerminationEvent,
    handleExecutionResultEvent,
    handleNodeResultEvent,
    handleLifecycleEvent,
    handleNodeOrStatusEvent,
    queueNodeStatusEvent,
  ]);

  useWorkflowStatusToasts({
    isExecutingWorkflow: isExecutingWorkflow && !isHistoricalData,
    isConnected,
    connectionError,
    nodeStatuses,
    reconcile: {
      isExecutingWorkflowRef,
      isPausingRef,
      isResumingRef,
      handleExecutionResultEvent,
      handleTerminationEvent,
      setNodeStatuses,
      setIsPaused,
      setPauseState,
    },
  });

  // Handle node click - connects to chat even when clicking the same node
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Group containers are a canvas-only annotation, not a configurable or
      // chat-capable node — clicking one shouldn't open the console panel or
      // attempt to wire up chat.
      if (node.type === NodeType.GroupNode) return;

      // Clicking a node that's already part of a multi-selection narrows the
      // selection down to it. React Flow's own handleNodeClick deliberately
      // no-ops here (it only reacts when the node isn't selected yet, or when
      // a multi-selection modifier is held to toggle it off), which otherwise
      // leaves the click with no visible effect at all — the per-node floating
      // bar is gated on the selection being exactly one node.
      //
      // Safe to do on click rather than pointer down: React Flow leaves
      // d3-drag's clickDistance at 0, so any pointer movement suppresses the
      // trailing click event and dragging a multi-selection never lands here.
      const isMultiSelectModifier =
        event.metaKey || event.ctrlKey || event.shiftKey;
      const selectedIds = selectSelectedNodeIds(useNodeStore.getState());
      if (
        !isMultiSelectModifier &&
        selectedIds.length > 1 &&
        selectedIds.includes(node.id)
      ) {
        reduceSelectionToNode(node.id);
      }

      // Only skip chat when the node cannot host it: has_chat explicitly
      // false AND no interactive-feedback toggle enabled (agent nodes with
      // "Ask for feedback on outputs" pause via the same chat protocol).
      // Older/catalog payloads omit has_chat on chat-capable nodes and
      // should still connect.
      const nodeStruct = node?.data?.struct as NodeStruct | undefined;
      if (!nodeSupportsChat(nodeStruct)) {
        setChatUnavailable('not_supported');
      } else if (isExecutingWorkflow) {
        connectChatForNode(node.id);
      } else {
        setChatUnavailable('workflow_not_running');
      }

      // Sticky notes and artifact cards are canvas decorations — no configure
      // panel, chat, or execution events.
      if (node.type === NodeType.StickyNoteNode) {
        setLastSelectedNodeId(node.id);
        setConsolePanelOpen(false);
        return;
      } else if (node.type === NodeType.ArtifactNode) {
        const parentId = (node.data as Record<string, unknown>)
          ?.parentNodeId as string | undefined;
        if (parentId) {
          connectChatForNode(parentId);
          setLastSelectedNodeId(parentId);
        }
        setActiveTab('files');
        setConsolePanelOpen(true);
        return;
      }
      // Only pick a default tab when opening the panel from closed. If the panel
      // is already visible, keep the user on their current tab and let the
      // content filter to the clicked node instead of bouncing to Configure.
      const panelAlreadyOpen = isConsoleSidebarMaximized || isConsolePanelOpen;
      if (!isExecutingWorkflow && !panelAlreadyOpen) {
        setActiveTab(isHistoricalData ? 'events' : 'configure');
      }
      // Track last clicked node for auto-pan functionality
      setLastSelectedNodeId(node.id);
      // on node click registers after mouseup, so we open console panel here
      setConsolePanelOpen(true);
    },
    [
      connectChatForNode,
      isExecutingWorkflow,
      isHistoricalData,
      isConsoleSidebarMaximized,
      isConsolePanelOpen,
      setActiveTab,
      setChatUnavailable,
      setConsolePanelOpen,
      setLastSelectedNodeId,
      reduceSelectionToNode,
    ],
  );

  // Handle selecting a past run - just update local state, sidebar handles data loading
  const handleSelectPastRun = useCallback(
    (
      runId: string | null,
      sessionId?: string | null,
      createdAt?: string | null,
    ) => {
      // If we're currently attached to a live run (via handleReconnectToLiveRun)
      // and the user selects a different run, disconnect from the live session.
      // The sidebar will either reconnect to the new run (if live) via
      // onReconnectToLiveRun, or load it as historical data.
      if (isExecutingWorkflow && runId !== selectedRunId) {
        finalizeWorkflowExecution();
        websocketService.disconnect('Switching to different past run');
      }
      if (runId !== selectedRunId) {
        hidePreview();
        hideFilesPreview();
      }
      setIsRunNotificationDismissed(false);
      setSelectedRunId(runId);
      setSelectedRunSessionId(sessionId ?? null);
      setSelectedRunCreatedAt(createdAt ?? null);
      setUrlState({ runId });
    },
    [
      isExecutingWorkflow,
      selectedRunId,
      finalizeWorkflowExecution,
      hidePreview,
      hideFilesPreview,
      setUrlState,
    ],
  );

  // Handle viewing current version - clear historical state and reload current workflow
  const handleViewCurrent = useCallback(async () => {
    // Clear historical state first
    restoreCurrentVersion();
    setSelectedRunId(null);
    setSelectedRunSessionId(null);
    setSelectedRunCreatedAt(null);
    setIsRunNotificationDismissed(false);
    clearUrlState();

    // Live-run statuses are local state (WS-driven, not loadedNodeStatuses),
    // so restoreCurrentVersion can't reach them — clear directly.
    setNodeStatuses({});

    // Also clear live-mode state.  Necessary because of the new
    // reconnect-to-live-run path: if the user opened a paused
    // (or running) past run via the sidebar, the editor's
    // ``isExecutingWorkflow`` / ``isPaused`` / ``pauseState`` were
    // set by ``handleReconnectToLiveRun``.  Without this cleanup,
    // exiting back to the saved-workflow view would leave a
    // stale "Paused" status bar with a Resume button that
    // targets a session the user is no longer viewing.
    //
    // Calling ``finalizeWorkflowExecution`` is the right primitive
    // -- same end-state we reach when a workflow finishes
    // naturally (clears all live + pause flags, marks WS service
    // as no-longer-executing, cancels any pause/resume
    // watchdogs).  For terminal past runs (the original use case
    // of handleViewCurrent) this is a no-op since those flags
    // were already false.
    finalizeWorkflowExecution();

    // Reload the saved workflow to restore the current state
    if (currentWorkflow?.id && currentOrg?.orgid) {
      const savedWorkflow = await getSavedWorkflow(
        currentWorkflow.id,
        currentOrg.orgid,
      );
      if (savedWorkflow) {
        loadWorkflow(savedWorkflow);
      }
    }

    setIsPastRunsPanelOpen(false);
  }, [
    restoreCurrentVersion,
    currentWorkflow?.id,
    currentOrg?.orgid,
    getSavedWorkflow,
    loadWorkflow,
    finalizeWorkflowExecution,
    clearUrlState,
    setNodeStatuses,
  ]);

  const handleClosePastRunsSidebar = useCallback(() => {
    setIsPastRunsPanelOpen(false);
  }, []);

  const handleViewRunDetails = useCallback(() => {
    setShouldScrollToError(true);
    openWorkflowPanel('events');
    if (isCanvasA2aChatEnabled) {
      setConsolePanelOpen(true);
    } else {
      setConsoleSidebarMaximized(true);
    }
  }, [
    setShouldScrollToError,
    openWorkflowPanel,
    isCanvasA2aChatEnabled,
    setConsolePanelOpen,
    setConsoleSidebarMaximized,
  ]);

  // Shown whenever a finished run is on the canvas — a past run opened from
  // history/deep-link, or the run that just completed. Derived from node
  // statuses (not the transient toast status, which a pane click resets), so
  // it persists until dismissed or cleared.
  const isLoadingRun = isLoadingPastRun || isLoadingDeepLinkedRun;
  const runStatusView = useMemo(() => {
    if (isExecutingWorkflow || isLoadingRun) return null;
    const hasResults = Object.keys(nodeStatuses).length > 0;
    if (!isHistoricalData && !hasResults) return null;
    return {
      status: classifyTerminalRun(nodeStatuses),
      timestamp:
        selectedRunCreatedAt ?? lastExecutionTimestamp ?? workflowStartedAt,
    };
  }, [
    isExecutingWorkflow,
    isLoadingRun,
    isHistoricalData,
    nodeStatuses,
    selectedRunCreatedAt,
    lastExecutionTimestamp,
    workflowStartedAt,
  ]);

  // ── Reconnect to a still-live (running or paused) run ────────
  //
  // Called by PastRunsSidebar when the user clicks a past-run
  // entry whose ``status`` is ``'running'`` or ``'paused'``.  This
  // is the recovery path for the "I refreshed mid-run / I paused
  // and walked away" UX -- the BE workflow is still alive, we
  // just need to re-attach the FE.
  //
  // By the time we get here the sidebar has already populated the
  // canvas (nodes, edges, ``loadedNodeStatuses`` with synthesised
  // EXECUTION_SUCCESSFUL events for completed nodes,
  // ``currentSession`` updated to the live session_id).  This
  // handler does the editor-local pieces only IT can do:
  //
  //   1. Hydrate live-mode flags so the status bar renders
  //      Pause / Resume buttons exactly like a freshly-started
  //      run that happened to be paused.
  //   2. For paused runs, take a fresh ``pauseSnapshot`` of the
  //      hydrated canvas so any subsequent edit-while-paused
  //      followed by Resume is detected by ``computeEditedNodeIds``
  //      (the diff baseline is the canvas as the user just saw it).
  //   3. Re-attach the WebSocket to the run's session via the
  //      pre-built URL the BE provided in
  //      ``run.session_socket_url``.  Both paused AND running
  //      cases benefit: paused runs have no events flowing right
  //      now, but the user is about to Resume which will start
  //      streaming again; running runs need it immediately.
  //
  // Race note: for paused runs there's effectively no race
  // (workflow is parked, no events flowing during the gap
  // between getWorkflowRun returning and connect() succeeding).
  // For running runs there's a small window of missed
  // intermediate ``processing`` events, bounded by the call
  // round-trip; structural events (begin/end/execution_*) will
  // still arrive on the next activity transition.
  const handleReconnectToLiveRun = useCallback(
    async (run: WorkflowRunWithResults) => {
      if (!run.session_socket_url) {
        // Defensive: BE always populates this for running/paused
        // runs after our schema change, but if for any reason it
        // didn't, surface a clear error rather than silently
        // ending up with no WS subscription.
        throw new Error('Backend did not return a WebSocket URL for this run');
      }

      // Drop any transient pause/resume flags left over from a
      // previous live session we might have been viewing.  These
      // are sticky UI states (e.g. "Pausing..." or "Resuming...")
      // that were tied to a different signal we sent earlier;
      // they don't apply to the freshly-reconnected session.
      setIsPausing(false);
      setIsResuming(false);

      // Hydrate the live-run flags from the BE's snapshot.  After
      // these set, the editor's existing UI renders exactly the
      // same way it would for a freshly-started workflow that
      // happened to be in this state.
      const isPausedRun = run.status === 'paused';
      setIsPaused(isPausedRun);
      setPauseState(isPausedRun ? (run.pause_state ?? null) : null);
      setIsExecutingWorkflow(true);
      stopRequestedRef.current = false;
      droppedResumedDuringStopRef.current = false;
      isExecutingWorkflowRef.current = true;
      setWorkflowStartedAt(run.started_at || null);
      setPersistStatusHighlight(false);
      // Defensively ensure historical-view is OFF -- the sidebar
      // already skips setting it on the live-run path, but this
      // guards against any earlier user action having flipped it.
      setIsHistoricalData(false);

      // For paused runs, take a fresh pause snapshot from the
      // CURRENT canvas state (the BE just hydrated it from the
      // workflow_version's payload, which is the same payload the
      // BE workflow has in memory).  Any subsequent edit the user
      // makes BEFORE clicking Resume will be diff'd against this
      // baseline by ``computeEditedNodeIds`` -- so edit-then-resume
      // works seamlessly across a refresh, just like it does in a
      // live pause.  For running runs there's no pause snapshot
      // (irrelevant -- you can't edit a running workflow).
      if (isPausedRun) {
        setPauseSnapshot(takePauseSnapshot(nodes, edges));
      } else {
        setPauseSnapshot(null);
      }

      // Mirror what handleRun does for a fresh run: switch to
      // events tab + open console so the live event stream is
      // immediately visible.
      setActiveTab('events');
      setConsolePanelOpen(true);
      setFilteredNodeId(null);

      // Re-attach the live WebSocket.  ``updateUrl`` handles the
      // disconnect-and-reconnect dance if a previous WS was open
      // for a different session.  ``markWorkflowStarted`` flips
      // the WS service into live-run mode so subsequent
      // unexpected disconnects trigger automatic reconnects.
      try {
        await websocketService.updateUrl(run.session_socket_url);
        await connect();
        websocketService.markWorkflowStarted();
        // The channel does not replay history: a node already waiting when
        // we reattach never announces itself again. The run says which, and
        // links the output under review -- streamed before the pause, not
        // yet persisted, so this is the only way the preview can show it.
        if (run.awaiting_chat_node_id) {
          const reviewResult = run.awaiting_chat_review_url
            ? officeDocumentResult(run.awaiting_chat_review_url)
            : null;
          const waitingNode = useNodeStore
            .getState()
            .nodes.find((n) => n.id === run.awaiting_chat_node_id);
          if (reviewResult && waitingNode && !waitingNode.data?.result) {
            updateNodeResult(run.awaiting_chat_node_id, reviewResult);
          }
          const paused = buildCanvasPausedNode({
            workflowId: run.workflow_id,
            sessionId: run.session_id,
            nodeId: run.awaiting_chat_node_id,
          });
          setCanvasPausedNode(paused);
          // Where the operator chat is the chat that answers a pause, a
          // reattached pause has to reach it the way a live one does: the
          // chat tab opens on it and the output under review opens with it.
          // The legacy chat recovers a pause on a node click, as it always has.
          if (embedded || isCanvasA2aChatEnabled) {
            if (!embedded) useNodeExplorerStore.getState().openChatTab();
            paused.openPreview?.();
          }
        } else {
          clearCanvasPausedNode();
        }
        console.log('[WorkflowEditor] Reconnected to live run', {
          sessionId: run.session_id,
          status: run.status,
          isPaused: isPausedRun,
          completedCount: run.node_results?.length ?? 0,
          timestamp: new Date().toISOString(),
        });
      } catch (wsError) {
        console.error(
          '[WorkflowEditor] Failed to re-attach WS for live run',
          wsError,
        );
        // Roll back live-mode flags so the user isn't left with
        // a misleading "executing" status bar AND a dead WS.
        // The sidebar's catch will surface the user-facing
        // toast and fall back to historical view.
        setIsExecutingWorkflow(false);
        isExecutingWorkflowRef.current = false;
        setIsPaused(false);
        setPauseState(null);
        setPauseSnapshot(null);
        throw wsError;
      }
    },
    [
      connect,
      nodes,
      edges,
      setActiveTab,
      setConsolePanelOpen,
      setFilteredNodeId,
      setIsHistoricalData,
      setPersistStatusHighlight,
      setCanvasPausedNode,
      clearCanvasPausedNode,
      buildCanvasPausedNode,
      updateNodeResult,
      embedded,
      isCanvasA2aChatEnabled,
    ],
  );

  // Deep-link: open a specific run when the URL carries ``?runId=`` (e.g. from
  // a notification) or the host pane names one (a run card in the chat beside
  // it). Runs once the workflow for this route is loaded. Live
  // (running/paused) runs re-attach to their session so the user can interact
  // (respond to clarifications / human intervention); terminal runs load onto
  // the canvas for review.
  const deepLinkLoadTokenRef = useRef(0);
  const deepLinkWorkflowIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Embedded, the editor stays mounted across a workflow switch (a foreign
    // workflow layered over the host's anchor), so a run applied on the
    // previous workflow must not block that same run once the user is back
    // on this one.
    if (deepLinkWorkflowIdRef.current !== workflowId) {
      deepLinkWorkflowIdRef.current = workflowId;
      appliedDeepLinkRunIdRef.current = null;
    }
    if (!initialParams.runId) {
      // The host can clear runIdOverride directly (a run card, Clear Run
      // from elsewhere) without going through this editor's own
      // setUrlState/clearUrlState -- reset here too, or a later request for
      // the same run id would be mistaken for one already applied.
      appliedDeepLinkRunIdRef.current = null;
      return;
    }
    if (appliedDeepLinkRunIdRef.current === initialParams.runId) return;
    if (isLoadingWorkflow || isLoadingTemplate) return;
    if (!currentWorkflow?.id || currentWorkflow.id !== workflowId) return;
    if (!currentOrg?.orgid) return;
    if (isExecutingWorkflow) {
      // Attached to a live run the URL has moved away from. Detach the way
      // Past Runs does on a different selection; the re-run this triggers
      // opens the requested one.
      finalizeWorkflowExecution();
      websocketService.disconnect('Switching to different past run');
      return;
    }

    const requestedRunId = initialParams.runId;
    appliedDeepLinkRunIdRef.current = requestedRunId;
    const loadToken = ++deepLinkLoadTokenRef.current;
    setIsLoadingDeepLinkedRun(true);

    let cancelled = false;
    let appliedToCanvas = false;

    const loadDeepLinkedRun = async () => {
      try {
        const result = await loadRunData(
          currentWorkflow.id,
          requestedRunId,
          currentOrg.orgid,
        );
        if (cancelled) return;
        applyRunToCanvas(result, {
          setNodes,
          setEdges,
          updateNodeResult,
          setLoadedNodeStatuses,
          setCurrentSession,
          setLastExecutionTimestamp,
          setIsHistoricalData,
        });
        appliedToCanvas = true;
        setSelectedRunId(result.runData.id);
        setSelectedRunSessionId(result.runData.session_id);
        setSelectedRunCreatedAt(result.runData.created_at ?? null);
        setUrlState({ runId: result.runData.id });

        // Fetch CoT events in parallel with the reconnect, but merge them
        // only after it settles (see below).
        const runEventsPromise = getRunEvents(
          currentOrg.orgid,
          currentWorkflow.id,
          result.runData.id,
          'all',
        );

        const isLiveRun =
          result.runData.status === 'running' ||
          result.runData.status === 'paused';
        if (isLiveRun) {
          try {
            await handleReconnectToLiveRun(result.runData);
          } catch (reconnectError) {
            if (cancelled) return;
            notification.error('Could not reconnect to the live run');
            Sentry.captureException(reconnectError, {
              extra: { context: 'deep-link-reconnect', workflowId },
            });
          }
        }

        const runEvents = await runEventsPromise;
        // Guard on the ref, not ``cancelled``: setNodes churn re-runs this
        // effect and sets ``cancelled`` for the same run, which would drop
        // the merge. The ref only changes when a different run is opened.
        if (appliedDeepLinkRunIdRef.current !== requestedRunId) return;
        const eventsByNode = mapThinkingEvents(runEvents, {
          includeAwaitingIntervention: result.isLiveRun,
        });
        const merged = { ...result.synthesizedStatuses };
        for (const [nodeId, nodeEvents] of Object.entries(eventsByNode)) {
          const enriched = nodeEvents.map((evt) => ({
            ...evt,
            node_display_name: result.displayNameById[nodeId] ?? '',
          }));
          merged[nodeId] = [
            ...enriched,
            ...(result.synthesizedStatuses[nodeId] ?? []),
          ];
        }
        setLoadedNodeStatuses(merged);
      } catch (error) {
        if (cancelled) return;
        // Allow the same run to be retried after a failed load.
        appliedDeepLinkRunIdRef.current = null;
        notification.error('Could not open the requested run');
        Sentry.captureException(error, {
          extra: { context: 'deep-link-run', workflowId },
        });
        clearUrlState();
      } finally {
        if (deepLinkLoadTokenRef.current === loadToken) {
          setIsLoadingDeepLinkedRun(false);
        }
      }
    };

    loadDeepLinkedRun();

    return () => {
      cancelled = true;
      // Release the ref if the load was torn down before reaching the canvas
      // so it can retry; after a successful apply it must stay set.
      if (
        !appliedToCanvas &&
        appliedDeepLinkRunIdRef.current === requestedRunId
      ) {
        appliedDeepLinkRunIdRef.current = null;
      }
    };
  }, [
    initialParams.runId,
    isLoadingWorkflow,
    isLoadingTemplate,
    currentWorkflow?.id,
    workflowId,
    currentOrg?.orgid,
    isExecutingWorkflow,
    finalizeWorkflowExecution,
    setNodes,
    setEdges,
    updateNodeResult,
    setLoadedNodeStatuses,
    setCurrentSession,
    setLastExecutionTimestamp,
    setIsHistoricalData,
    setUrlState,
    clearUrlState,
    handleReconnectToLiveRun,
    notification,
  ]);

  // Handle Resume Workflow for intervention
  const handleResumeIntervention = useCallback(async () => {
    if (
      !currentWorkflow?.id ||
      !interventionState?.interventionRequestId ||
      !interventionState?.nodeId
    ) {
      notification.error('Missing workflow or intervention information');
      return;
    }

    const { currentSession } = useWorkflowStore.getState();
    if (!currentSession) {
      notification.error('No active session');
      return;
    }

    try {
      const response = await nodeResumeWorkflow(
        currentWorkflow.id,
        currentSession,
        interventionState.nodeId,
        interventionState.interventionRequestId,
      );

      if (response.data?.success) {
        notification.success('Workflow resumed successfully');
        clearInterventionState();
      } else {
        throw new Error(response.detail || 'Failed to resume workflow');
      }
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to resume workflow',
      );
    }
  }, [
    currentWorkflow,
    interventionState,
    notification,
    clearInterventionState,
  ]);

  // Handle Stop Workflow for intervention
  const handleStopIntervention = useCallback(async () => {
    if (
      !currentWorkflow?.id ||
      !interventionState?.interventionRequestId ||
      !interventionState?.nodeId
    ) {
      notification.error('Missing workflow or intervention information');
      return;
    }

    const { currentSession } = useWorkflowStore.getState();
    if (!currentSession) {
      notification.error('No active session');
      return;
    }

    setIsStoppingIntervention(true);

    try {
      const response = await nodeStopWorkflow(
        currentWorkflow.id,
        currentSession,
        interventionState.nodeId,
        interventionState.interventionRequestId,
      );

      // Check if websocket disconnected - this indicates workflow was stopped regardless
      if (!websocketService.isConnected()) {
        notification.success('Workflow stopped successfully');
        markUserStoppedRun(currentSession);
        clearInterventionState();
        return;
      }

      if (response.data?.success) {
        notification.success('Workflow stopped successfully');
        markUserStoppedRun(currentSession);
        clearInterventionState();
      } else {
        throw new Error(response.detail || 'Failed to stop workflow');
      }
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to stop workflow',
      );
    } finally {
      setIsStoppingIntervention(false);
    }
  }, [
    currentWorkflow,
    interventionState,
    notification,
    clearInterventionState,
  ]);

  // When isExecutingWorkflow transitions from true to false (workflow finished),
  // persist the status highlight on nodes so the user can see final results
  const prevIsExecutingRef = useRef(isExecutingWorkflow);
  useEffect(() => {
    if (prevIsExecutingRef.current && !isExecutingWorkflow) {
      setPersistStatusHighlight(true);

      // Reconcile artifact nodes from node results. Skip if the
      // execution result handler already placed them on the canvas.
      const { nodes: currentNodes, edges: currentEdges } =
        useNodeStore.getState();
      const hasArtifacts = currentNodes.some(
        (n) => n.type === NodeType.ArtifactNode,
      );
      if (!hasArtifacts) {
        const { filteredNodes: baseNodes, filteredEdges: baseEdges } =
          removeAllArtifactNodes(currentNodes, currentEdges);

        let artifactNodes: CustomNode[] = [];
        let artifactEdges: CustomEdge[] = [];
        const terminalIds = getTerminalNodeIds(baseNodes, baseEdges);

        baseNodes.forEach((node) => {
          const result = node.data?.result as NodeResult | undefined;
          if (terminalIds.has(node.id)) {
            const { newNodes, newEdges } = spawnArtifactNodesForResult(
              node,
              result,
              baseEdges,
              [...baseNodes, ...artifactNodes],
            );
            artifactNodes = [...artifactNodes, ...newNodes];
            artifactEdges = [...artifactEdges, ...newEdges];
          }
        });

        if (artifactNodes.length > 0) {
          setNodes([...baseNodes, ...artifactNodes]);
          setEdges([...baseEdges, ...artifactEdges]);
        }
      }
    }
    prevIsExecutingRef.current = isExecutingWorkflow;
  }, [isExecutingWorkflow, setPersistStatusHighlight, setNodes, setEdges]);

  // When a chat-initiated run ends, emit the signal so the node-explorer
  // sidebar can poll for artifacts. Declared after the artifact-node
  // reconcile effect above (both keyed on isExecutingWorkflow) so its
  // canvas snapshot always runs after reconcile has spawned this run's
  // artifact nodes — reversing the order would let this effect fire with
  // an empty fallback-file list on any run where handleExecutionResultEvent
  // hadn't already placed them.
  useEffect(() => {
    if (isExecutingWorkflow) return;
    const runId = chatRunIdRef.current;
    if (runId === null) return;
    // Clear the ref immediately so this fires exactly once per run
    chatRunIdRef.current = null;
    emitChatRunEndedSignal(runId);
  }, [isExecutingWorkflow, emitChatRunEndedSignal]);

  // Handle node selection
  const onSelectionChange = useCallback(
    (params: { nodes: Node[]; edges: Edge[] }) => {
      const selected = params.nodes || [];
      if (selected.length > 0) {
        setPersistStatusHighlight(false); // dismiss Run Complete bar when user focuses a node
      }

      // Prevent edge selection when locked
      const selectedEdges = params.edges || [];
      if ((isLocked || isWorkflowLocked) && selectedEdges.length > 0) {
        // Deselect all edges
        setEdges(
          edges.map((edge) => ({
            ...edge,
            selected: false,
          })),
        );
      }

      // Clear last selected node when nothing is selected. We intentionally do
      // NOT close the console panel on empty selection — deselecting (canvas
      // click or "All Events" back navigation) keeps the panel open; the user
      // closes it explicitly via the panel controls.
      setLastSelectedNodeId(null);

      // Read fresh state from the store rather than the closured `nodes`/
      // groupContainerFocusedId — onNodesChange can mark a group as dragging
      // synchronously just before this fires, and a stale closure would miss
      // it and clear focus mid-drag.
      const freshState = useNodeStore.getState();
      const draggingGroup = freshState.nodes.find(
        (node) => isGroupNode(node) && node.dragging,
      );
      if (draggingGroup) {
        setGroupContainerFocusedId(draggingGroup.id);
      } else if (selected.length > 0) {
        const focusedId = freshState.groupContainerFocusedId;
        // Stateless check: is the current selection exactly the focused
        // group's full set of eligible children? Re-derived fresh every
        // call (instead of a one-shot marker set when we select them) so it
        // can't go stale -- XYFlow can re-invoke onSelectionChange for
        // reasons unrelated to an actual selection change (this callback's
        // own `edges` dependency changing recreates it, and XYFlow replays
        // current state into a new handler identity), and a one-shot marker
        // consumed by an earlier, correct match wouldn't survive to cover
        // that redundant later call for the same selection.
        const selectedIds = selected.map((n) => n.id).sort();
        const focusedGroupChildIds = focusedId
          ? getGroupChildIds(freshState.nodes, focusedId)
          : [];
        const isFocusedGroupsFullChildSelection =
          focusedGroupChildIds.length > 0 &&
          focusedGroupChildIds.length === selectedIds.length &&
          focusedGroupChildIds.every((id, i) => id === selectedIds[i]);
        if (!isFocusedGroupsFullChildSelection) {
          // Child selection shows the dashed group chrome; clear container
          // focus so we don't keep the solid "container-focused" border.
          setGroupContainerFocusedId(null);
        }
      }
      // Empty selection does not clear container focus — e.g. after dragging
      // the group padding the container should stay focused. Canvas clicks
      // clear focus via onPaneClick instead.

      // Mirror the deselection in the Events tab: clear the drill-down so the
      // panel returns to the workflow summary, matching the Configure header
      // which also resets to the whole-workflow view on canvas deselect.
      if (selected.length === 0) {
        setEventsDetailNodeId(null);
      }
      // Note: We don't auto-open console panel here on selection because
      // onSelectionChange fires when starting a drag. The panel is opened
      // in onNodeClick which only fires on actual clicks (after mouseup).
    },
    [
      setLastSelectedNodeId,
      setGroupContainerFocusedId,
      setEventsDetailNodeId,
      isLocked,
      isWorkflowLocked,
      edges,
      setEdges,
      setPersistStatusHighlight,
    ],
  );

  // Track previous session to detect when workflow starts
  const prevSessionRef = useRef<string | null>(null);

  // Auto-connect to chat when workflow starts (if a node is selected and supports chat)
  useEffect(() => {
    // Check if session just started (was null, now has value). Only while executing so
    // loading a past run's session_id does not call connectChatForNode.
    if (currentSession && !prevSessionRef.current && isExecutingWorkflow) {
      // Find first selected node that supports chat (has_chat, or the
      // interactive-feedback toggle for agent-family nodes)
      for (const nodeId of selectedNodeIdsFromStore) {
        const node = nodes.find((n) => n.id === nodeId);
        const nodeStruct = node?.data?.struct;
        if (nodeSupportsChat(nodeStruct)) {
          connectChatForNode(nodeId);
          break;
        }
      }
    }

    prevSessionRef.current = currentSession;
  }, [
    currentSession,
    selectedNodeIdsFromStore,
    nodes,
    connectChatForNode,
    isExecutingWorkflow,
  ]);

  // Deselecting closes the console as a side effect, so the panel is reopened
  // on the workflow-level Configure view rather than whichever node was last.
  const handleOpenWorkflowSettings = useCallback(() => {
    deselectAllNodes();
    openWorkflowCard();
  }, [deselectAllNodes, openWorkflowCard]);

  const ConsoleSidebarHeaderButtons = useMemo(() => {
    // Flag ON — ENG-4987 design: overflow first (outline), then Settings,
    // Artifacts, Share, Past Runs, Save (all outline), then Run pill.
    if (isCanvasA2aChatEnabled) {
      return (
        <div className='flex items-center gap-2'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size='icon' variant='outline' aria-label='More actions'>
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {workflowId !== 'new' && (isWorkflowOwner || isWorkflowAdmin) && (
                <DropdownMenuItem
                  onClick={() => {
                    setSaveWorkflowModalMode('editInfo');
                    setIsSaveWorkflowModalOpen(true);
                  }}
                  disabled={isWorkflowLocked}
                >
                  <Edit className='size-4' />
                  Edit Workflow Attributes
                </DropdownMenuItem>
              )}
              {(isAdmin || isWorkflowOwner || isWorkflowAdmin) && (
                <DropdownMenuItem
                  onClick={() => {
                    setSaveWorkflowModalMode('template');
                    setIsSaveWorkflowModalOpen(true);
                  }}
                >
                  <FileText className='size-4' />
                  Save as Template
                </DropdownMenuItem>
              )}
              {workflowId !== 'new' && (
                <DropdownMenuItem
                  onClick={() => {
                    setSaveWorkflowModalMode('duplicate');
                    setIsSaveWorkflowModalOpen(true);
                  }}
                >
                  <CopyPlus className='size-4' />
                  Duplicate Workflow
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleImportWorkflow()}
                disabled={isBlockingNodeListLoad || isWorkflowLocked}
              >
                {isBlockingNodeListLoad ? (
                  <Loader2 className='size-4 animate-spin' />
                ) : (
                  <Import className='size-4' />
                )}
                Import Workflow
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportWorkflow()}
                disabled={nodes.length === 0}
              >
                <Share className='size-4' />
                Export Workflow
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPrompts()}
                disabled={nodes.length === 0}
              >
                <FileSpreadsheet className='size-4' />
                Export Prompts
              </DropdownMenuItem>
              {isWorkflowLockingEnabled && workflowId !== 'new' && (
                <>
                  <DropdownMenuSeparator />
                  {isWorkflowLocked ? (
                    isWorkflowAdmin ? (
                      <DropdownMenuItem onClick={handleToggleWorkflowLock}>
                        <Unlock className='size-4' />
                        Unlock Workflow
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled>
                        <Lock className='size-4' />
                        Locked
                      </DropdownMenuItem>
                    )
                  ) : (
                    <DropdownMenuItem
                      onClick={handleToggleWorkflowLock}
                      disabled={nodes.length === 0}
                    >
                      <Lock className='size-4' />
                      Lock Workflow
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {workflowId !== 'new' && (isWorkflowOwner || isWorkflowAdmin) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={isWorkflowLocked}
                  >
                    <Trash2 className='size-4' />
                    Delete Workflow
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size='icon'
                variant='outline'
                onClick={handleOpenWorkflowSettings}
                aria-label='Workflow settings'
              >
                <Settings className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom'>Workflow settings</TooltipContent>
          </Tooltip>

          {!isExecutingWorkflow && workflowId !== 'new' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='icon'
                  variant='outline'
                  aria-label='View Artifacts'
                  onClick={() => {
                    if (onViewArtifacts && workflowId) {
                      onViewArtifacts(workflowId);
                    } else {
                      navigate(`${ROUTES.ARTIFACTS}?workflow=${workflowId}`);
                    }
                  }}
                >
                  <Shapes className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom'>View Artifacts</TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Button
                  size='icon'
                  variant='outline'
                  onClick={() => setIsShareModalOpen(true)}
                  disabled={workflowId === 'new'}
                  aria-label='Share'
                >
                  <Share2 className='size-4' />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side='bottom'>Share</TooltipContent>
          </Tooltip>

          {workflowId !== 'new' && (
            <PastRunsButton
              isViewingPastRun={
                isHistoricalData && Boolean(selectedRunCreatedAt)
              }
              onClick={() => setIsPastRunsPanelOpen(!isPastRunsPanelOpen)}
              variant='outline'
            />
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size='icon'
                variant='outline'
                onClick={() => saveCurrentWorkflow()}
                disabled={isSaveLoading}
                aria-label='Save Workflow'
              >
                {isSaveLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Save className='size-4' />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom'>Save Workflow</TooltipContent>
          </Tooltip>

          {/* Play / timer pill — expands into a running timer with pause+stop when executing */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <RunTimerPill
                  isExecutingWorkflow={isExecutingWorkflow}
                  isPaused={isPaused}
                  isPausing={isPausing}
                  isResuming={isResuming}
                  isStopping={isStopping}
                  isWaitingForInput={
                    isExecutingWorkflow && pendingClarifications.length > 0
                  }
                  workflowStartedAt={workflowStartedAt}
                  onPlay={handleRun}
                  onPause={handlePauseWorkflow}
                  onResume={handleResumeWorkflow}
                  onStop={handleStopWorkflow}
                  playDisabled={
                    !canRunWorkflowGraph ||
                    hasDisabledNodes ||
                    isWorkflowFullyLoading
                  }
                  isLoading={isWorkflowFullyLoading}
                  elapsedSnapshotRef={timerElapsedMsRef}
                />
              </span>
            </TooltipTrigger>
            {!isExecutingWorkflow && (
              <TooltipContent side='bottom'>{runButtonTooltip}</TooltipContent>
            )}
          </Tooltip>

          {/* Publish — re-enabled with the MCP-gateway publish wire
              (publish_to_mcp -> auto-curated Published server). Stays
              clickable after publishing for re-publish / flag flips. */}
          {workflowId !== 'new' && (
            <Button
              size='sm'
              variant={mcpCode || apiCode ? 'outline' : 'default'}
              onClick={() => setIsPublishModalOpen(true)}
              disabled={isPublishingLoaderOpen}
              className={mcpCode || apiCode ? 'text-foreground' : undefined}
            >
              {isPublishingLoaderOpen ? (
                <LoadingSpinner />
              ) : (
                <RocketIcon className='size-4 mr-1' />
              )}
              {isPublishingLoaderOpen
                ? 'Publishing...'
                : mcpCode || apiCode
                  ? 'Published'
                  : 'Publish'}
            </Button>
          )}
        </div>
      );
    }

    // Flag OFF — original dev layout, unchanged for EY production.
    return (
      <div className='flex items-center gap-2'>
        {/* View Artifacts — hidden while a run is in progress */}
        {!isExecutingWorkflow && workflowId !== 'new' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => {
                  if (onViewArtifacts && workflowId) {
                    onViewArtifacts(workflowId);
                  } else {
                    navigate(`${ROUTES.ARTIFACTS}?workflow=${workflowId}`);
                  }
                }}
              >
                <Shapes className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom'>View Artifacts</TooltipContent>
          </Tooltip>
        )}

        {/* Share — span wrapper keeps the tooltip working while the button is
            disabled for new workflows (disabled elements don't fire hover) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex'>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => setIsShareModalOpen(true)}
                disabled={workflowId === 'new'}
                aria-label='Share'
              >
                <Share2 className='size-4' />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side='bottom'>Share</TooltipContent>
        </Tooltip>

        {/* Past Runs Panel */}
        {workflowId !== 'new' && (
          <PastRunsButton
            isViewingPastRun={isHistoricalData && Boolean(selectedRunCreatedAt)}
            onClick={() => setIsPastRunsPanelOpen(!isPastRunsPanelOpen)}
          />
        )}

        {/* Save Button - Added temporarily while auto-save is disabled */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => saveCurrentWorkflow()}
              disabled={isSaveLoading}
            >
              {isSaveLoading ? <LoadingSpinner /> : <Save className='size-4' />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom'>Save Workflow</TooltipContent>
        </Tooltip>

        {/* ... Show more dropdown button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size='icon' variant='ghost'>
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {/* API exposure is deliberately only the header Switch and the
                list-table column: two affordances for the same store action
                can disagree on disabled state during a rolling deploy. */}
            {workflowId !== 'new' && (isWorkflowOwner || isWorkflowAdmin) && (
              <DropdownMenuItem
                onClick={() => {
                  setSaveWorkflowModalMode('editInfo');
                  setIsSaveWorkflowModalOpen(true);
                }}
                disabled={isWorkflowLocked}
              >
                <Edit className='size-4' />
                Edit Workflow Attributes
              </DropdownMenuItem>
            )}
            {(isAdmin || isWorkflowOwner || isWorkflowAdmin) && (
              <DropdownMenuItem
                onClick={() => {
                  setSaveWorkflowModalMode('template');
                  setIsSaveWorkflowModalOpen(true);
                }}
              >
                <FileText className='size-4' />
                Save as Template
              </DropdownMenuItem>
            )}
            {workflowId !== 'new' && (
              <DropdownMenuItem
                onClick={() => {
                  setSaveWorkflowModalMode('duplicate');
                  setIsSaveWorkflowModalOpen(true);
                }}
              >
                <CopyPlus className='size-4' />
                Duplicate Workflow
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => handleImportWorkflow()}
              disabled={isBlockingNodeListLoad || isWorkflowLocked}
            >
              {isBlockingNodeListLoad ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Import className='size-4' />
              )}
              Import Workflow
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleExportWorkflow()}
              disabled={nodes.length === 0}
            >
              <Share className='size-4' />
              Export Workflow
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleExportPrompts()}
              disabled={nodes.length === 0}
            >
              <FileSpreadsheet className='size-4' />
              Export Prompts
            </DropdownMenuItem>
            {isWorkflowLockingEnabled && workflowId !== 'new' && (
              <>
                <DropdownMenuSeparator />
                {isWorkflowLocked ? (
                  isWorkflowAdmin ? (
                    <DropdownMenuItem onClick={handleToggleWorkflowLock}>
                      <Unlock className='size-4' />
                      Unlock Workflow
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem disabled>
                      <Lock className='size-4' />
                      Locked
                    </DropdownMenuItem>
                  )
                ) : (
                  <DropdownMenuItem
                    onClick={handleToggleWorkflowLock}
                    disabled={nodes.length === 0}
                  >
                    <Lock className='size-4' />
                    Lock Workflow
                  </DropdownMenuItem>
                )}
              </>
            )}
            {workflowId !== 'new' && (isWorkflowOwner || isWorkflowAdmin) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isWorkflowLocked}
                >
                  <Trash2 className='size-4' />
                  Delete Workflow
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Expose-via-A2A toggle previously lived here in the canvas
            header.  It\'s been moved into the "Edit Workflow Attributes"
            modal so all admin-scoped workflow settings live in one
            place (alongside Default Access).  Same store action
            (``setPublishToApi`` + ``saveWorkflowWithScreenshot``)
            wired through the modal\'s ``onPublishToApiChange`` prop.
            See ``SaveWorkflowModal`` invocation below. */}

        {/* Play / timer pill — expands into a running timer with pause+stop when executing */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex'>
              <RunTimerPill
                isExecutingWorkflow={isExecutingWorkflow}
                isPaused={isPaused}
                isPausing={isPausing}
                isResuming={isResuming}
                isStopping={isStopping}
                isWaitingForInput={
                  isExecutingWorkflow && pendingClarifications.length > 0
                }
                workflowStartedAt={workflowStartedAt}
                onPlay={handleRun}
                onPause={handlePauseWorkflow}
                onResume={handleResumeWorkflow}
                onStop={handleStopWorkflow}
                playDisabled={
                  !canRunWorkflowGraph ||
                  hasDisabledNodes ||
                  isWorkflowFullyLoading
                }
                isLoading={isWorkflowFullyLoading}
                elapsedSnapshotRef={timerElapsedMsRef}
              />
            </span>
          </TooltipTrigger>
          {!isExecutingWorkflow && (
            <TooltipContent side='bottom'>{runButtonTooltip}</TooltipContent>
          )}
        </Tooltip>

        {/* Publish button — re-enabled with the MCP-gateway publish wire
            (publish_to_mcp -> auto-curated Published server). The published
            state stays clickable so admins can re-publish / flip flags. */}
        {mcpCode || apiCode ? (
          <Button
            size='sm'
            variant='outline'
            onClick={() => setIsPublishModalOpen(true)}
            className='text-foreground'
            disabled={isPublishingLoaderOpen}
          >
            {isPublishingLoaderOpen ? (
              <LoadingSpinner />
            ) : (
              <RocketIcon className='size-4 mr-1' />
            )}
            {isPublishingLoaderOpen ? 'Publishing...' : 'Published'}
          </Button>
        ) : workflowId !== 'new' ? (
          <Button
            size='sm'
            onClick={() => setIsPublishModalOpen(true)}
            disabled={isPublishingLoaderOpen}
          >
            {isPublishingLoaderOpen ? (
              <LoadingSpinner />
            ) : (
              <RocketIcon className='size-4 mr-1' />
            )}
            {isPublishingLoaderOpen ? 'Publishing...' : 'Publish'}
          </Button>
        ) : null}
      </div>
    );
  }, [
    handleRun,
    isExecutingWorkflow,
    isPaused,
    isPausing,
    isResuming,
    isStopping,
    workflowStartedAt,
    handlePauseWorkflow,
    handleResumeWorkflow,
    handleStopWorkflow,
    isSaveLoading,
    saveCurrentWorkflow,
    workflowId,
    isWorkflowOwner,
    isAdmin,
    mcpCode,
    apiCode,
    handleImportWorkflow,
    handleExportWorkflow,
    isWorkflowFullyLoading,
    hasDisabledNodes,
    canRunWorkflowGraph,
    runButtonTooltip,
    isPublishingLoaderOpen,
    setIsPublishModalOpen,
    setSaveWorkflowModalMode,
    setIsSaveWorkflowModalOpen,
    setIsShareModalOpen,
    isPastRunsPanelOpen,
    isHistoricalData,
    selectedRunCreatedAt,
    isWorkflowLocked,
    isWorkflowLockingEnabled,
    isWorkflowSchedulingEnabled,
    isWorkflowAdmin,
    handleToggleWorkflowLock,
    setActiveTab,
    setConsolePanelOpen,
    isCanvasA2aChatEnabled,
    isArtifactsEnabled,
    onViewArtifacts,
    navigate,
    handleOpenWorkflowSettings,
  ]);

  const nodeExplorerHeader = useMemo(
    () => <NodeExplorerSidebarHeaderButtons />,
    [],
  );

  // Compute the workflow name for the save modal
  const saveModalWorkflowName = useMemo(() => {
    // Force save mode: use existing name (don't generate new one - that's done in loadTemplateWorkflow)
    if (isForceSave) {
      return currentWorkflow?.name && currentWorkflow.name !== 'new'
        ? currentWorkflow.name
        : '';
    }

    // Duplicate/clone mode: append "(Copy)" to existing name
    if (
      saveWorkflowModalMode === 'duplicate' ||
      saveWorkflowModalMode === 'clone'
    ) {
      return `${currentWorkflow?.name || 'Untitled'} (Copy)`;
    }

    // Save mode with new workflow: use existing name
    if (
      saveWorkflowModalMode === 'save' &&
      (currentWorkflow?.name === 'new' ||
        !currentWorkflow?.name ||
        currentWorkflow?.name === '')
    ) {
      return '';
    }

    // Default: use existing workflow name
    return currentWorkflow?.name || '';
  }, [isForceSave, saveWorkflowModalMode, currentWorkflow?.name]);

  return (
    <div className='flex flex-1 min-h-0 flex-col overflow-hidden'>
      {/* Loading overlay for workflow/template/catalog loading (skip for new workflows) */}
      {isWorkflowFullyLoading && workflowId !== 'new' && (
        <div
          className={cn(
            'bg-background/80 backdrop-blur-sm z-100 flex items-center justify-center',
            // Embedded, the overlay must cover the pane, not the whole app.
            embedded ? 'absolute inset-0' : 'fixed inset-0',
          )}
        >
          <div className='flex flex-col items-center gap-4'>
            <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin'></div>
            <div className='text-center'>
              <p className='text-lg font-semibold'>
                {isLoadingTemplate
                  ? 'Loading template...'
                  : isLoadingWorkflow
                    ? 'Loading workflow...'
                    : 'Loading tools...'}
              </p>
              <p className='text-sm text-muted-foreground mb-4'>
                {isLoadingTemplate
                  ? 'Setting up your template workflow'
                  : isLoadingWorkflow
                    ? 'Fetching your saved workflow'
                    : 'Fetching available tools and nodes'}
              </p>
              <p className='text-xs text-muted-foreground mb-2'>
                Taking longer than expected?
              </p>
              <button
                onClick={() => {
                  resetLoadingState();
                  setIsLoadingDeepLinkedRun(false);
                  notification.warning(
                    'Loading cancelled. Please try refreshing the page.',
                  );
                }}
                className='px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 rounded-md transition-colors'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='relative w-full flex-1 min-h-0'>
        <ReactFlowProvider>
          <DnDProvider>
            <div ref={panelGroupRef} className='relative w-full h-full flex'>
              <div className='flex-1 relative min-w-0'>
                {/* Loading overlay when switching between past runs or opening a deep-linked run */}
                {isLoadingRun && (
                  <div className='absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm'>
                    <div className='flex flex-col items-center gap-3'>
                      <LoadingSpinner />
                      <span className='text-sm text-muted-foreground'>
                        Loading run...
                      </span>
                    </div>
                  </div>
                )}

                {/* Run status pill */}
                {runStatusView && !isRunNotificationDismissed && (
                  <div className='absolute top-4 left-1/2 -translate-x-1/2 z-50'>
                    <RunStatusPill
                      status={runStatusView.status}
                      timestamp={runStatusView.timestamp}
                      onClearRun={handleViewCurrent}
                      onDismiss={() => setIsRunNotificationDismissed(true)}
                      onViewDetails={handleViewRunDetails}
                    />
                  </div>
                )}

                {/* Canvas banners - stacked above the mouse control bar.
                    One column, not one absolute box each: they are raised by
                    unrelated conditions that a single workflow can meet at the
                    same time, and separate boxes at the same offset drew them
                    on top of one another. Ordered least to most urgent, so the
                    one nearest the control bar is the one to act on first. */}
                <div className='absolute bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none'>
                  <div className='pointer-events-auto flex flex-col items-center gap-2'>
                    <CarbonArcConnectBanner />
                    <EmptyScopeBanner />
                    {hasDisabledNodes && <DisabledNodesBanner />}
                    {interventionState &&
                      interventionState.isInterventionPending &&
                      interventionState.endTime !== null &&
                      interventionState.totalSeconds !== null && (
                        <HumanInterventionBanner
                          endTime={interventionState.endTime}
                          onResume={handleResumeIntervention}
                          onStop={handleStopIntervention}
                          isStoppingWorkflow={isStoppingIntervention}
                          nodeIcon={interventionState.nodeIcon}
                          nodeIconColor={interventionState.nodeIconColor}
                          nodeType={interventionState.nodeType}
                        />
                      )}
                  </div>
                </div>

                {/* Artifact viewer sits to the RIGHT of the pinned chat
                    panel, simulating the side-by-side layout of the new design.
                    We read panelSize directly from the store (same source the
                    sidebar itself uses) so the viewer edge tracks resizing.
                    When chat is not maximised the viewer spans the full area. */}
                {artifactPreview ? (
                  <div
                    className='absolute top-0 bottom-0 right-0 z-20 bg-background border-l border-border'
                    style={{
                      left: isNodeExplorerMaximized
                        ? Math.max(
                            NODE_EXPLORER_PANEL_MIN_WIDTH,
                            Math.min(
                              NODE_EXPLORER_PANEL_MAX_WIDTH,
                              nodeExplorerPanelSize,
                            ),
                          )
                        : 0,
                    }}
                  >
                    <ArtifactViewer
                      artifactId={artifactPreview.artifactId}
                      userRole={artifactPreview.userRole}
                      rootClassName='h-full'
                      titleMaxWidthClass='max-w-[280px]'
                      onClose={() => setArtifactPreview(null)}
                      onAfterDelete={() => setArtifactPreview(null)}
                      onViewWorkflow={(workflowId, runId) => {
                        navigate(workflowRunPath(workflowId, runId));
                        setArtifactPreview(null);
                      }}
                      loadErrorAction={
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => setArtifactPreview(null)}
                        >
                          Close
                        </Button>
                      }
                    />
                  </div>
                ) : null}

                {/* Main Canvas - full size, sidebars overlay it */}
                <NodeCanvas
                  key={selectedRunId || 'current'}
                  className='w-full h-full'
                  nodeStatuses={nodeStatuses}
                  isExecutingWorkflow={isExecutingWorkflow}
                  onSelectionChange={onSelectionChange}
                  onNodeClick={onNodeClick}
                  isLoadingWorkflow={isLoadingWorkflow}
                  isLoadingTemplate={isLoadingTemplate}
                  onGetNodesBoundsReady={(getNodesBounds) => {
                    getNodesBoundsRef.current = getNodesBounds;
                  }}
                  onFitViewReady={(fitView) => {
                    fitViewRef.current = fitView;
                  }}
                  isOwner={isWorkflowOwner}
                  onCloneWorkflow={() => {
                    setSaveWorkflowModalMode('clone');
                    setIsSaveWorkflowModalOpen(true);
                  }}
                  shouldIgnoreKeyboardEvent={shouldIgnoreKeyboardEvent}
                  panelGroupWidth={panelGroupWidth}
                  isHistoricalData={isHistoricalData}
                  isLocked={isLocked}
                  onToggleLock={toggleLock}
                  onDismissStatusBarCompleted={() =>
                    setPersistStatusHighlight(false)
                  }
                  isWorkflowLocked={isWorkflowLocked}
                  isPaused={isHistoricalData ? false : isPaused}
                  cachedNodeIds={cachedNodeIds}
                  // Context menu's "Re-run from this node" item.  We
                  // route it through requestRerunFromHere (same
                  // dispatcher the play button uses) so the
                  // confirmation modal + dual-context dispatch
                  // (paused vs historical) is shared between both
                  // surfaces.  Only wired when re-run actually
                  // makes sense for the current state.
                  onRerunFromNode={
                    isPaused || isHistoricalData
                      ? requestRerunFromHere
                      : undefined
                  }
                  onRerunFromHere={requestRerunFromHere}
                  embedded={embedded}
                  isConnected={isConnected}
                  addNodesEnabled={isCanvasA2aChatEnabled}
                  isNewWorkflow={isNewWorkflow}
                />

                {/* Left Sidebar - Node Explorer (absolutely positioned).
                    Embedded, the host supplies its own chat rail in this
                    position, so the explorer is dropped entirely. Under the
                    unified chat design the same is true of the truly-new
                    canvas (`/workflow/new`): its library moved to the
                    right-hand panel and its chat to the operator rail waiting
                    on the other side of the save. Other workflows that fall
                    through to this standalone page (flag on, no primary
                    agent) are not new, so they keep the explorer. */}
                {!embedded && (!isCanvasA2aChatEnabled || !isNewWorkflow) && (
                  <NodeExplorerSidebar
                    headerComponent={nodeExplorerHeader}
                    panelGroupWidth={panelGroupWidth}
                    isLocked={isLocked}
                    allowedSubTypes={
                      isWorkflowLocked
                        ? WORKFLOW_LOCK_ALLOWED_SUBTYPES
                        : undefined
                    }
                    isWorkflowLocked={isWorkflowLocked}
                    isWorkflowAdmin={isWorkflowAdmin}
                    onUnlockWorkflow={handleToggleWorkflowLock}
                    pendingClarifications={pendingClarifications}
                    onDeliverClarification={handleDeliverClarification}
                    onPauseClarificationCountdown={
                      handlePauseClarificationCountdown
                    }
                    onResumeClarificationCountdown={
                      handleResumeClarificationCountdown
                    }
                    isWorkflowPaused={isPaused}
                    resolvedClarifications={resolvedClarificationsQueue}
                    onConsumeResolvedClarification={
                      handleConsumeResolvedClarification
                    }
                    runSessionId={currentSession}
                    onRunWorkflow={handleRunFromChat}
                    chatRunEndedSignal={chatRunEndedSignal}
                    onOpenArtifact={(artifact: ArtifactListItem) =>
                      setArtifactPreview({
                        artifactId: artifact.id,
                        userRole: artifact.user_role,
                      })
                    }
                    onChatRunSignalConsumed={(seq) =>
                      setChatRunEndedSignal((prev) =>
                        prev?.seq === seq ? null : prev,
                      )
                    }
                  />
                )}

                {/* Collapsed chat rail. Only for the truly-new canvas
                    (`/workflow/new`), the doorstep of the fluid host — whose
                    rail lands collapsed to exactly this bar, so the
                    bottom-left does not jump across the save. There is no
                    conversation to continue until the workflow has an id, so
                    the bar asks for the name that gives it one. Gated on
                    isNewWorkflow so an existing workflow that falls through
                    to this standalone page (flag on, no primary agent) does
                    not get force-routed into a save modal that would mint a
                    duplicate. */}
                {!embedded && isCanvasA2aChatEnabled && isNewWorkflow && (
                  <ChatRailShortcut
                    label='Name this workflow to start chatting'
                    ariaLabel='Name this workflow to start chatting'
                    onClick={handleForceSaveModal}
                  />
                )}

                {/* Node library — new right-hand panel, only with the unified chat design */}
                {isCanvasA2aChatEnabled && !artifactPreview && (
                  <AddNodesPanel isLocked={isLocked || isWorkflowLocked} />
                )}

                {/* Right Sidebar - Console (hidden when artifact viewer is open so
                    it doesn't overlay the side-by-side preview panel) */}
                {!artifactPreview &&
                  (isCanvasA2aChatEnabled ? (
                    <ConsoleSidebar
                      nodeStatuses={nodeStatuses}
                      isConnected={isConnected}
                      headerButtons={ConsoleSidebarHeaderButtons}
                      panelGroupWidth={panelGroupWidth}
                      onEditWorkflowInfo={() => {
                        setSaveWorkflowModalMode('editInfo');
                        setIsSaveWorkflowModalOpen(true);
                      }}
                      isLocked={isLocked}
                      isWorkflowLocked={isWorkflowLocked}
                      workflowId={currentWorkflow?.id ?? null}
                      workflowRunId={
                        selectedRunSessionId ?? currentSession ?? null
                      }
                      parentRunId={
                        // Only fall back to the parent run when the eval
                        // tab is showing the live rerun's session. If the
                        // user navigated to a different past run via the
                        // sidebar, the lineage tuple no longer applies.
                        !selectedRunSessionId &&
                        rerunLineage &&
                        rerunLineage.session === currentSession
                          ? rerunLineage.parent
                          : null
                      }
                      cachedEvalNodeIds={cachedNodeIds}
                      isWorkflowExecuting={
                        // Only treat the live canvas session as "in progress".
                        // If the user has navigated to a past run via the
                        // sidebar, that run is by definition terminal — even
                        // if a separate live run is happening on this canvas.
                        !selectedRunSessionId && isExecutingWorkflow
                      }
                      isWorkflowPaused={isPaused}
                      workflowRunRecordId={selectedRunId}
                    />
                  ) : (
                    <ConsoleSidebarLegacy
                      nodeStatuses={nodeStatuses}
                      isConnected={isConnected}
                      headerButtons={ConsoleSidebarHeaderButtons}
                      panelGroupWidth={panelGroupWidth}
                      onEditWorkflowInfo={() => {
                        setSaveWorkflowModalMode('editInfo');
                        setIsSaveWorkflowModalOpen(true);
                      }}
                      isLocked={isLocked}
                      isWorkflowLocked={isWorkflowLocked}
                      workflowId={currentWorkflow?.id ?? null}
                      workflowRunId={
                        selectedRunSessionId ?? currentSession ?? null
                      }
                      parentRunId={
                        !selectedRunSessionId &&
                        rerunLineage &&
                        rerunLineage.session === currentSession
                          ? rerunLineage.parent
                          : null
                      }
                      cachedEvalNodeIds={cachedNodeIds}
                      isWorkflowExecuting={
                        !selectedRunSessionId && isExecutingWorkflow
                      }
                      isWorkflowPaused={isPaused}
                    />
                  ))}
              </div>

              {/* Past Runs Sidebar (part of flex layout, not overlay) */}
              {workflowId !== 'new' && isPastRunsPanelOpen && (
                <div className='w-[263px] border-l bg-background shrink-0'>
                  <PastRunsSidebar
                    workflowId={currentWorkflow?.id || workflowId || ''}
                    isOpen={isPastRunsPanelOpen}
                    onClose={handleClosePastRunsSidebar}
                    isExecuting={isExecutingWorkflow}
                    selectedRunId={selectedRunId}
                    onSelectRun={handleSelectPastRun}
                    onLoadingChange={setIsLoadingPastRun}
                    onReconnectToLiveRun={handleReconnectToLiveRun}
                    onViewCurrentRun={handleViewCurrent}
                  />
                </div>
              )}
            </div>
          </DnDProvider>
        </ReactFlowProvider>
      </div>
      <SaveWorkflowModal
        isModalOpen={isSaveWorkflowModalOpen}
        setIsModalOpen={(isOpen) => {
          setIsSaveWorkflowModalOpen(isOpen);
          // Reset forceSave when modal closes
          if (!isOpen) {
            setIsForceSave(false);
          }
        }}
        isExistingWorkflow={saveWorkflowModalMode === 'editInfo'}
        currentWorkflowName={saveModalWorkflowName}
        currentWorkflowDescription={currentWorkflow?.description || ''}
        mode={saveWorkflowModalMode}
        showAssetMode={true}
        showSpecialInstructions={
          saveWorkflowModalMode !== 'duplicate' &&
          saveWorkflowModalMode !== 'clone'
        }
        forceSave={isForceSave}
        currentDefaultRole={
          (currentWorkflow?.default_role as DefaultRoleOption) ?? null
        }
        showClientDataWarning={isShareClientDataWarningEnabled}
        onDefaultRoleChange={async (role) => {
          if (!currentOrg?.orgid || !workflowId) return;
          try {
            await setWorkflowDefaultRole(currentOrg.orgid, workflowId, role);
            if (currentWorkflow) {
              setCurrentWorkflow({
                ...currentWorkflow,
                default_role: role,
              });
            }
            notification.success('Default access updated');
          } catch (err) {
            Sentry.captureException(err, {
              extra: { context: 'edit-modal-default-role', workflowId, role },
            });
            notification.error('Failed to update default access');
          }
        }}
        // Only wired in editInfo mode, so the Save / template / duplicate
        // flows can't accidentally surface the toggle.
        currentPublishToApi={
          saveWorkflowModalMode === 'editInfo' &&
          workflowId !== 'new' &&
          (isAdmin || isWorkflowOwner || isWorkflowAdmin)
            ? Boolean(currentWorkflow?.publish_to_api)
            : undefined
        }
        isWorkflowLocked={isWorkflowLocked}
        onPublishToApiChange={async (next) => {
          // Optimistic flip in the store, persist, roll back on
          // failure -- identical contract to the header switch the
          // toggle replaced.
          setPublishToApi(next);
          try {
            await saveWorkflowWithScreenshot(
              currentWorkflow?.id as string,
              currentWorkflow?.name as string,
              currentWorkflow?.description as string,
            );
          } catch (e) {
            setPublishToApi(!next);
            Sentry.captureException(e, {
              extra: { context: 'edit-modal-publish-to-api', workflowId, next },
            });
            notification.error('Failed to update API exposure');
          }
        }}
        onSave={async (name, description, icon, color, bgColor) => {
          try {
            await handleSaveWorkflowModalSave(
              name,
              description,
              icon,
              color,
              bgColor,
            );
            setIsSaveWorkflowModalOpen(false);
            setIsForceSave(false);
          } catch (error) {
            // Error is already handled in handleSaveWorkflowModalSave
            console.error('Failed to save workflow:', error);
            Sentry.captureException(error, {
              tags: {
                workflow_action: 'save_workflow_modal_callback',
                workflow_id: currentWorkflow?.id,
              },
            });
          }
        }}
      />

      <PublishWorkflowModal
        isModalOpen={isPublishModalOpen}
        setIsModalOpen={setIsPublishModalOpen}
        workflowName={currentWorkflow?.name || 'Untitled Workflow'}
        isNewWorkflow={isNewWorkflow}
        onPublish={handlePublishWorkflow}
      />

      <PublishingLoaderModal
        isModalOpen={isPublishingLoaderOpen}
        setIsModalOpen={setIsPublishingLoaderOpen}
        workflowName={currentWorkflow?.name || 'Untitled Workflow'}
        onCancel={handleCancelPublishing}
        onComplete={handlePublishingComplete}
        progress={publishingProgress}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title='Delete Workflow'
        description={`Are you sure you want to delete "${currentWorkflow?.name || 'this workflow'}"? All artifacts created from this workflow will be deleted as well. Shared users will no longer be able to view or download them.`}
        buttonLabel='Delete'
        isLoading={isDeleting}
        handleDelete={handleDeleteWorkflow}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />

      {/*
        Re-run from this node confirmation.  ``rerunFromHereTarget``
        is set by ``requestRerunFromHere`` (the click handler that
        the per-node play button fires through the
        NodeActionsContext).  We compute the invalidation set
        client-side using the same algorithm the BE uses
        (helper/downstream.ts mirrors temporal/utils/downstream.py)
        so the "N nodes will recompute" preview matches the
        outcome.  ``invalidatedCount`` includes the chosen node
        itself because the BE invalidates from the node forward,
        inclusive.
      */}
      <ConfirmationModal
        isOpen={rerunFromHereTarget !== null}
        title='Re-run from this node?'
        description={
          rerunFromHereTarget
            ? `Re-running from "${rerunFromHereTarget.displayName}" will reset ${rerunFromHereTarget.invalidatedCount} node${
                rerunFromHereTarget.invalidatedCount === 1 ? '' : 's'
              } (this node and everything downstream).  Upstream node results will be reused.`
            : ''
        }
        buttonLabel='Re-run'
        isLoading={isRerunFromHereLoading}
        handleDelete={confirmRerunFromHere}
        handleCancel={cancelRerunFromHere}
      />

      <ShareModal
        resourceType='workflow'
        resourceId={workflowId ?? ''}
        orgId={currentOrg?.orgid ?? null}
        open={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        defaultRole={
          (currentWorkflow?.default_role as DefaultRoleOption) ?? null
        }
        onDefaultRoleChange={(role) => {
          if (currentWorkflow) {
            setCurrentWorkflow({
              ...currentWorkflow,
              default_role: role,
            });
          }
        }}
        showClientDataWarning={isShareClientDataWarningEnabled}
      />

      <UnsavedChangesModal
        isOpen={isBlocked}
        onCancel={reset}
        onLeave={proceed}
        onSaveAndLeave={handleSaveAndLeave}
        isSaving={isSaveLoading}
      />
    </div>
  );
};
